import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  Save,
  Rocket,
  Code,
  AlertCircle,
  Pencil,
  Eye,
  EyeOff,
  Wrench,
  Info,
  Hash,
  Layers,
  ChevronDown,
  Search,
  Database,
  Play,
  Loader2,
  RefreshCw,
  History,
  AlertTriangle,
  Lock,
  Link2 as LinkIcon,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SqlViewer } from "@/components/ui/sql-viewer";
import { AUTO_NUMBER_UNIQUE_CONFLICT_MESSAGE } from "@/lib/myapp/auto-number";
import { evaluateFormula as evaluateFormulaFn } from "@/lib/myapp/formula-evaluator";
import { runAsyncFullRepair } from "@/lib/myapp/full-repair-poll";
import {
  deriveLinkFkUpgradeStatus,
  describeLinkFkUpgradeResult,
} from "@/lib/myapp/link-fk-upgrade-status";
import {
  canConfirmLineTableDelete,
  evaluateLineTableDelete,
} from "@/lib/myapp/line-table-delete";
import {
  canFieldHaveStateRules,
  getReadOnlyFromDisplayOverride,
  getRequiredFromDisplayOverride,
} from "@/lib/myapp/field-state-rules";
import { LINK_DETACH_TARGET_CODES } from "@/lib/myapp/link-detach";
import { unhideField } from "@/lib/myapp/unhide-field";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DateBoundInput } from "@/components/portal/tables/DateBoundInput";
import { UniqueKeyPicker } from "@/components/portal/tables/UniqueKeyPicker";
import { Combobox } from "@/components/ui/combobox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ApiErrorAlert } from "@/components/ui/api-error-alert";
import { myappApi, type BackfillInheritSourcesResponse } from "@/services/api/myapp";
import { useVirtualRefresh } from "@/hooks/useVirtualRefresh";
import { useAuth } from "@/contexts/AuthContext";
import { PermissionDenied } from "@/components/ui/permission-denied";
import { useMyAppPermissions } from "@/hooks/useMyAppPermissions";
import { useConnectorAttachmentBinding } from "@/hooks/useConnectorAttachments";
import { useParentTable } from "@/hooks/useParentTable";
import { usePermissions } from "@/hooks/usePermissions";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type {
  Form,
  FormDetail,
  FormField,
  FormFieldCreate,
  FormFieldUpdate,
  FieldDependentsResponse,
  FieldType,
  FormLinkOption,
  FormLinkFieldOption,
  AreaSet,
  TableState,
  TypeChangePreviewResponse,
} from "@/types/myapp";
import { OptionsConfigEditor } from "@/components/portal/tables/OptionsConfigEditor";
import {
  parseOptionsConfig,
  validateOptionsConfig,
} from "@/lib/myapp/options";
import { BulkFieldCreateDialog } from "@/components/portal/tables/BulkFieldCreate";
import { BulkFieldEditDialog } from "@/components/portal/tables/BulkFieldEditDialog";
import { BindingBuilder } from "@/components/portal/myapp/BindingBuilder";
import { BindingSummaryCard } from "@/components/portal/myapp/BindingSummaryCard";
import { bridgeTableToConfig, type BridgeDemoConfig } from "@/components/portal/myapp/bridgeConfig";
import { VersionMilestonePanel } from "@/components/portal/tables/VersionMilestonePanel";
import { TableImportDefaults } from "@/components/portal/tables/TableImportDefaults";
import { AnonymousSettingsPanel } from "@/components/portal/tables/AnonymousSettingsPanel";
import { TableStatesEditor } from "@/components/portal/tables/state-machine/TableStatesEditor";
import { FieldStatePillRow } from "@/components/portal/tables/state-machine/FieldStatePillRow";
import {
  lockedAtToReadOnlyFromStateId,
  readOnlyFromToLockedAtStateId,
} from "@/components/portal/tables/state-machine/fieldLockResolution";
import { SubmitGuardSection } from "@/components/portal/tables/SubmitGuardSection";
import { SaveChip } from "@/components/portal/tables/SaveChip";
import { useSaveChip } from "@/components/portal/tables/useSaveChip";
import { QRCodeDisplay } from "@/components/portal/tables/QRCodeDisplay";
import { SelectableCard } from "@/components/portal/tables/TableConfigPanel";
import { SampleDataTable } from "@/components/portal/tables/SampleDataTable";

// ---------------------------------------------------------------------------
// Field-type help content
//
// Single source of truth for the two surfaces that describe field types:
//  - The Field Type info popover (lists every type for a user browsing the
//    catalogue before they pick).
//  - The blue info box that appears below the Field Type selector once a
//    type is chosen (FieldTypeInfoBox below).
//
// When a new field-type row is added to dp_myapp.FieldType, add a matching
// entry here so both surfaces stay in step. Order in this array drives the
// popover order; the Field Type dropdown itself orders by FieldType.SortOrder
// from the API.
// ---------------------------------------------------------------------------
type FieldTypeDescription = {
  code: string;
  headline: string;
  detail: string;
};

const FIELD_TYPE_DESCRIPTIONS: readonly FieldTypeDescription[] = [
  { code: "text", headline: "Text (Single Line)", detail: "Short text input. Default 250 characters; a Maximum Characters above 4000 means no limit." },
  { code: "longtext", headline: "Text (Multi-line)", detail: "Textarea for longer content. Default 2000 characters; a Maximum Characters above 4000 means no limit." },
  { code: "int", headline: "Whole Number", detail: "Integer values without decimals (e.g. quantities, counts). Supports min/max." },
  { code: "bigint", headline: "Large Number", detail: "Large integer for values exceeding 2 billion." },
  { code: "decimal", headline: "Decimal", detail: "Numbers with up to 4 decimal places. Supports min/max." },
  { code: "money", headline: "Currency", detail: "Monetary values stored to 4 decimal places. Supports min/max." },
  { code: "percentage", headline: "Percentage", detail: "Decimal value representing a rate or margin. Supports min/max." },
  { code: "date", headline: "Date", detail: "Calendar date without a time component. Supports min/max." },
  { code: "datetime", headline: "Date & Time", detail: "Full date and time value with precision. Supports min/max." },
  { code: "time", headline: "Time", detail: "Time of day without a date component. Supports min/max." },
  { code: "bit", headline: "Yes/No", detail: "Boolean checkbox for true/false values." },
  { code: "guid", headline: "Unique ID", detail: "Auto-generated GUID for unique identification." },
  { code: "email", headline: "Email", detail: "Email address field with format validation." },
  { code: "sortorder", headline: "Sort Order", detail: "Integer field for controlling display order in Kanban columns and dropdowns." },
  { code: "link", headline: "Link to Table", detail: "Reference to a row in another table. Stores both a display snapshot and an FK column. The display value is a snapshot at write time; the FK enforces the relationship." },
  { code: "lookup", headline: "Lookup Field", detail: "Reads a column from a linked row (via a chosen Link field on this table) and stores it as a snapshot. The value is captured at write time and does not refresh if the source row later changes." },
  { code: "calculation", headline: "Calculation", detail: "Computed numeric value from a formula across other fields. Evaluated when a record is created or updated; the result is stored with the record." },
  { code: "concatenation", headline: "Concatenation", detail: "Combines other fields via a template like [Customer] - [OrderNumber]. The value is recomputed and stored on every write to the row." },
  { code: "rollup", headline: "Rollup", detail: "Aggregates a numeric column from a child table (SUM / COUNT / AVG / MIN / MAX). Recalculated when child rows change." },
  { code: "options", headline: "Options", detail: "A fixed list of choices you define on this field (like a Google Form question). Single-select stores one value; multi-select stores several. Enable “allow other” to also accept free text. Not a reusable link to another table." },
  { code: "attachment", headline: "Attachment", detail: "Stores a count of file attachments associated with the row. Files live in a separate Attachment table." },
  { code: "connector_files", headline: "Connector Files", detail: "The number of source-system files on each row, kept up to date by Refresh Data. Sorts like any column; click the count to open the files. Only on a virtual table whose rows have connector files, one per table." },
  { code: "signature", headline: "Signature", detail: "Stores a count of captured signatures associated with the row. Signatures themselves live in a separate table." },
  { code: "inherit_from_parent", headline: "Inherit from Parent", detail: "Copies a value from the parent header row when a line is inserted. Use for fields you don't want users to re-enter on each line (e.g. Product, Customer, Template Identifier). The copied value is a snapshot captured at line write time." },
];

const FIELD_TYPE_DESCRIPTION_BY_CODE: Record<string, FieldTypeDescription> =
  Object.fromEntries(FIELD_TYPE_DESCRIPTIONS.map((d) => [d.code, d]));

function FieldTypeInfoBox({ code }: { code: string | null | undefined }) {
  if (!code) return null;
  const d = FIELD_TYPE_DESCRIPTION_BY_CODE[code];
  if (!d) return null;
  return (
    <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
      <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
      <div className="text-xs text-blue-700 dark:text-blue-300">
        <p className="font-medium">{d.headline}</p>
        <p className="mt-1">{d.detail}</p>
      </div>
    </div>
  );
}

// Field-state-rules helpers (canFieldHaveStateRules + Required-from /
// Read-only-from display overrides) live in lib/myapp/field-state-rules
// so the field card and the dialog cannot drift.

// Sortable field item component
type TableStateSlice = {
  id: string;
  name: string;
  sort_order: number;
  is_terminal: boolean;
  description?: string | null;
};

interface SortableFieldItemProps {
  field: FormField;
  isPublished: boolean;
  canStructure: boolean;
  getFieldTypeName: (fieldTypeId: string) => string;
  onEdit: (field: FormField) => void;
  onDelete: (field: FormField) => void;
  /** One-click unhide for a hidden field on a published table. */
  onUnhide: (field: FormField) => void;
  /** True while this field's unhide request is in flight. */
  unhiding: boolean;
  tableStates: ReadonlyArray<TableStateSlice>;
  onJumpToMatrixRow?: (fieldId: string, stateId: string) => void;
  /** Optional inline patch handler. When provided, the card renders two
   *  compact selects (Required from / Read-only from) so the consultant can set
   *  the per-field state rules without opening the pencil dialog. */
  onFieldRuleChange?: (
    fieldId: string,
    patch: {
      locked_at_state_id?: string | null;
      required_from_state_id?: string | null;
    },
  ) => Promise<void>;
}

function SortableFieldItem({
  field,
  isPublished,
  canStructure,
  getFieldTypeName,
  onEdit,
  onDelete,
  onUnhide,
  unhiding,
  tableStates,
  onJumpToMatrixRow,
  onFieldRuleChange,
}: SortableFieldItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-muted/50 ${
        field.is_hidden ? "opacity-50" : ""
      } ${isDragging ? "shadow-lg z-10" : ""}`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-5 w-5 text-muted-foreground" />
      </button>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          {field.original_name ? (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-medium cursor-default border-b border-dashed border-muted-foreground/40">
                    {field.name}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p className="text-xs text-muted-foreground">Original heading</p>
                  <p>{field.original_name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <span className="font-medium">{field.name}</span>
          )}
          {field.is_hidden && (
            <Badge variant="secondary" className="text-xs">
              Hidden
            </Badge>
          )}
          {field.is_inherited && (
            <Badge variant="outline" className="text-xs">
              Inherit from Parent
            </Badge>
          )}
          {field.is_required && (
            <Badge variant="outline" className="text-xs">
              Required
            </Badge>
          )}
          {field.is_unique && (
            <Badge variant="outline" className="text-xs">
              Unique
            </Badge>
          )}
          {/* Theme 13a: explicit Auto badge for autonumber-target fields.
              Mirrors the Required/Unique badges so the consultant scanning
              the field list can spot autonumber fields at a glance without
              opening the dialog. The autonumber field itself is hidden in
              the data-entry form but visible here. */}
          {field.is_auto_numbered && (
            <Badge variant="outline" className="text-xs">
              Auto
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground">
          {getFieldTypeName(field.field_type_id)}
          {field.linked_table && (
            <> &bull; {field.linked_table.name}</>
          )}
          {field.linked_display_field_name && (
            <> &bull; {field.linked_display_field_name}</>
          )}
          {" "}&bull; <code>{field.column_name}</code>
        </div>
        {tableStates.length > 0 && (
          <FieldStatePillRow
            field={field}
            tableStates={tableStates}
            onPillClick={onJumpToMatrixRow}
          />
        )}
        {/* Inline rule controls. Mirror the field-editor dialog selects so
            the consultant can flip Locks-at / Required-from without
            opening the pencil. canHaveStateRules excludes system-computed
            types (rollup / calculation / lookup / concatenation) per the
            same rule the dialog uses (edit.tsx selectedFieldType branch).
            Required-from is disabled with an explanatory tooltip when the
            field is unconditionally required (is_required=true makes the
            per-state required-from moot). */}
        {tableStates.length > 0 &&
          onFieldRuleChange !== undefined &&
          canStructure &&
          (() => {
            const typeCode = field.field_type?.code ?? "";
            const typeName = field.field_type?.name ?? null;
            const isInherited = field.is_inherited === true;
            const isAutoNumbered = field.is_auto_numbered === true;
            const canHaveStateRules = canFieldHaveStateRules({
              typeCode,
              isInherited,
              isAutoNumbered,
            });
            const requiredFromOverride = getRequiredFromDisplayOverride({
              typeCode,
              typeName,
              isRequired: field.is_required,
              isInherited,
              isAutoNumbered,
            });
            const readOnlyFromOverride = getReadOnlyFromDisplayOverride({
              typeCode,
              typeName,
              isInherited,
              isAutoNumbered,
            });
            const sorted = [...tableStates].sort(
              (a, b) => a.sort_order - b.sort_order,
            );
            // Order left-to-right tracks the field lifecycle: optional
            // -> Required-from sets the state at/after which the field
            // becomes mandatory -> Locks-at sets the state at/after which
            // the field can no longer be edited. Reading left-to-right
            // matches the natural workflow progression.
            return (
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Required from:</span>
                  <Select
                    value={field.required_from_state_id ?? "__never__"}
                    disabled={!canHaveStateRules || field.is_required}
                    onValueChange={(v) => {
                      void onFieldRuleChange(field.id, {
                        required_from_state_id: v === "__never__" ? null : v,
                      });
                    }}
                  >
                    <SelectTrigger
                      className="h-7 w-56 text-xs [&>span]:truncate"
                      title={
                        isInherited
                          ? "Value is inherited from the parent record; state rules don't apply on this form."
                          : isAutoNumbered
                          ? "Autonumber target: value is assigned by the platform on create, so state rules cannot gate a user input that doesn't exist."
                          : !canHaveStateRules
                          ? "This field type cannot have state rules"
                          : field.is_required
                            ? "Field is Required at every state. Toggle Required off in the field editor to use a state-conditional Required-from instead."
                            : "Record cannot advance PAST this state unless the field is populated. Reaching the state itself is allowed (the field becomes required upon arrival, gating further forward progress)."
                      }
                    >
                      {requiredFromOverride ? (
                        <span>{requiredFromOverride}</span>
                      ) : (
                        <SelectValue />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__never__">Not required</SelectItem>
                      {sorted.map((s, idx) => (
                        <SelectItem key={s.id} value={s.id}>
                          {idx + 1}. {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Read-only from:</span>
                  <Select
                    // Stored value (`locked_at_state_id`) points at the LAST
                    // editable state. The "Read-only from" label asks for
                    // the FIRST locked state instead, so we translate
                    // through the helpers — picking option "State N"
                    // persists as locked_at_state_id = state[N-1].
                    value={
                      lockedAtToReadOnlyFromStateId(
                        field.locked_at_state_id,
                        sorted,
                      ) ?? "__never__"
                    }
                    disabled={!canHaveStateRules}
                    onValueChange={(v) => {
                      void onFieldRuleChange(field.id, {
                        locked_at_state_id:
                          v === "__never__"
                            ? null
                            : readOnlyFromToLockedAtStateId(v, sorted),
                      });
                    }}
                  >
                    <SelectTrigger
                      className="h-7 w-56 text-xs [&>span]:truncate"
                      title={
                        isInherited
                          ? "Value is inherited from the parent record; state rules don't apply on this form."
                          : isAutoNumbered
                          ? "Autonumber target: values are assigned by the platform on create and never edited by the user, so a read-only-from rule cannot apply."
                          : !canHaveStateRules
                          ? "This field type cannot have state rules"
                          : "Field becomes read-only from this state onwards. Pick the FIRST state at which the field locks (the previous state is the last one in which it remains editable)."
                      }
                    >
                      {readOnlyFromOverride ? (
                        <span>{readOnlyFromOverride}</span>
                      ) : (
                        <SelectValue />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__never__">Never</SelectItem>
                      {/* First state excluded: it has no predecessor to
                          act as the last editable, so "Read-only from
                          state 1" isn't representable. */}
                      {sorted.slice(1).map((s, i) => (
                        <SelectItem key={s.id} value={s.id}>
                          {i + 2}. {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })()}
      </div>
      {canStructure && (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(field)}
            // An open Edit dialog would snapshot is_hidden: true mid-unhide
            // and re-hide the field on save.
            disabled={unhiding}
            title="Edit field"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {field.is_hidden && isPublished && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onUnhide(field)}
              disabled={unhiding}
              title="Unhide field"
              aria-label="Unhide field"
            >
              {unhiding ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Eye className="h-4 w-4 text-orange-500" />
              )}
            </Button>
          )}
          {!field.is_hidden && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDelete(field)}
              title={isPublished ? "Hide field" : "Delete field"}
            >
              {isPublished ? (
                <EyeOff className="h-4 w-4 text-orange-500" />
              ) : (
                <Trash2 className="h-4 w-4 text-destructive" />
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

interface SortableChildTableRowProps {
  child: Form;
  canStructure: boolean;
  /** Org Owner / Org Admin — gates the drag handle (matches the backend
   * require_org_admin guard on the reorder endpoint). */
  canReorder: boolean;
  onDelete: (child: Form) => void;
}

// One draggable row in the "Line Tables" list. The order set here persists to
// TableDef.SortOrder (server-side) and drives the child-table order everywhere,
// including the capture form. Mirrors SortableFieldItem's drag affordance.
function SortableChildTableRow({
  child,
  canStructure,
  canReorder,
  onDelete,
}: SortableChildTableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: child.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 ${
        isDragging ? "shadow-lg z-10" : ""
      }`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {canReorder && (
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground"
            aria-label="Reorder line table"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-5 w-5" />
          </button>
        )}
        <Link
          to="/portal/myapp/$tableId/edit"
          params={{ tableId: String(child.id) }}
          className="flex-1 min-w-0"
        >
          <div className="font-medium">{child.name}</div>
          <div className="text-sm text-muted-foreground">
            <code>{child.table_name}</code>
            {child.is_published ? (
              <Badge variant="info" className="ml-2">
                Published
              </Badge>
            ) : (
              <Badge variant="secondary" className="ml-2">
                Draft
              </Badge>
            )}
          </div>
        </Link>
      </div>
      <div className="flex items-center gap-1 shrink-0 ml-3">
        <Link
          to="/portal/myapp/$tableId/edit"
          params={{ tableId: String(child.id) }}
          title="Edit line table"
          className="inline-flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted"
        >
          <Pencil className="h-4 w-4 text-muted-foreground" />
        </Link>
        {canStructure && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Delete line table"
            onClick={() => onDelete(child)}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/portal/myapp/$tableId/edit")({
  component: FormDesignerPage,
});

// The shared en-GB conflict copy for the invalid per-parent + no-prefix +
// unique-target auto-number combination now lives in
// "@/lib/myapp/auto-number" (AUTO_NUMBER_UNIQUE_CONFLICT_MESSAGE) so the
// bulk field edit dialog can reuse it verbatim.

function FormDesignerPage() {
  const { tableId } = Route.useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const formId = tableId;

  // Object-level permissions — API is single source of truth
  const { permissions: objPerms, loading: permsLoading, denied: permissionDenied } = useMyAppPermissions(formId);
  const canStructure = objPerms?.can_edit ?? false;
  // Reordering line tables is an Org Owner / Org Admin action (mirrors the
  // backend require_org_admin guard on POST /tables/{id}/children/reorder), so
  // the drag handle is gated on the org role, not on can_edit.
  const { isAdmin: isOrgAdmin, bypassesGroups, isPlatformAdmin, rbacResolving } = usePermissions();
  // Adding a line table creates a table: org admin and above only (MyApp
  // enforces it on POST /tables), on top of edit rights on this table.
  const canCreateLineTable = canStructure && (isOrgAdmin || isPlatformAdmin);
  // Authoring a Pairing binding is gated on `bypassesGroups` (org_owner /
  // org_admin / platform_admin) to match the backend `_require_bridge_setup_admin`
  // and the Pairing tab — NOT the narrower org-role `isAdmin`, which would wrongly
  // block a platform admin who isn't an org-role admin of the selected org.
  // `bypassesGroups` defaults false until RBAC resolves, so any DENY path must
  // hold off while `rbacResolving` (else a real admin who deep-links straight to
  // the editor sees a false "ask an admin" denial). See usePermissions.ts.
  const canAuthorBinding = bypassesGroups;

  const [form, setForm] = useState<FormDetail | null>(null);
  const [fieldTypes, setFieldTypes] = useState<FieldType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);

  // Per-section save chips. Click-driven controls in each section share a
  // chip so the user gets the same ✓ Saved confirmation we ship in the
  // States editor and Submit-Guard section.
  const autoNumberChip = useSaveChip();
  const areaSetChip = useSaveChip();
  const refreshModeChip = useSaveChip();
  // Bumped when the versions panel changes versions/milestones, so the
  // import-default selects refetch their options.
  const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);

  // Child tables (for header/line pattern)
  const [childTables, setChildTables] = useState<Form[]>([]);
  const [createChildDialogOpen, setCreateChildDialogOpen] = useState(false);
  const [newChildName, setNewChildName] = useState("");
  const [creatingChild, setCreatingChild] = useState(false);

  // Delete-line-table dialog state. A line table is hidden from the main
  // Table Maintenance list and managed here, via its header. It can be
  // deleted while it is a draft, or published-but-empty, but not while it is
  // published and still holds data (the backend enforces the same rule).
  const [deleteChildDialogOpen, setDeleteChildDialogOpen] = useState(false);
  const [childToDelete, setChildToDelete] = useState<Form | null>(null);
  const [deletingChild, setDeletingChild] = useState(false);
  const [checkingChildDeps, setCheckingChildDeps] = useState(false);
  const [childDeps, setChildDeps] = useState<{
    can_delete: boolean;
    dependencies: { table_name: string; field_name: string; table_id: string }[];
    record_count: number | null;
  } | null>(null);
  const [deleteChildError, setDeleteChildError] = useState<string | null>(null);
  const [confirmDeleteChildText, setConfirmDeleteChildText] = useState("");
  const [bulkFieldDialogOpen, setBulkFieldDialogOpen] = useState(false);
  const [bulkFieldEditDialogOpen, setBulkFieldEditDialogOpen] = useState(false);
  const [fieldFilter, setFieldFilter] = useState("");

  // Inherit-from-parent state (used inside the Add Field dialog when the
  // "Inherit from Parent" sentinel is picked in the Field Type dropdown).
  const [inheritSourceFieldId, setInheritSourceFieldId] = useState<string>("");
  const [inheritError, setInheritError] = useState<string | null>(null);
  // When editing an inherited field that already has a source, the picker
  // shows the source read-only by default (driven by the FK-resolved
  // source_field_name, race-free); the pencil flips this to true to reveal
  // the editable Select. Reset on every dialog open. A field with no source
  // yet (legacy / new) is editable from the start (see sourceIsEditable in
  // the render).
  const [editingSource, setEditingSource] = useState<boolean>(false);

  // Drag-and-drop sensors with activation constraints
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require the mouse to move 5 pixels before activating drag
      // This prevents accidental drags when clicking
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Edit table details dialog
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ name: "", description: "" });
  const [editNameError, setEditNameError] = useState<string | null>(null);
  const [editNameValidating, setEditNameValidating] = useState(false);
  const editNameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Add/Edit field dialog
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [fieldData, setFieldData] = useState<FormFieldCreate & { is_hidden?: boolean }>({
    name: "",
    field_type_id: "",
    description: null,
    is_required: false,
    is_unique: false,
    default_value: null,
    max_length: null,
    min_value: null,
    max_value: null,
    linked_table_id: null,
    linked_display_field_id: null,
    source_link_field_id: null,
    lookup_field_id: null,
    calculation_formula: null,
    calculation_result_field_type_id: null,
    concatenation_template: null,
    options_config: null,
    show_total: false,
    decimal_places: null,
    is_hidden: false,
    rollup_source_field_id: null,
    rollup_function: null,
    kanban_mode: null,
    locked_at_state_id: null,
    required_from_state_id: null,
  });

  // Workflow states for the table (loaded for the per-field "Locks at"
  // picker AND the per-card state pill row on each field). `is_terminal`
  // is carried because terminal states always render as locked,
  // independent of any per-field `locked_at_state_id`.
  const [tableStates, setTableStates] = useState<
    Array<{
      id: string;
      name: string;
      sort_order: number;
      is_terminal: boolean;
      description?: string | null;
    }>
  >([]);

  // Linked table selection
  const [linkableTables, setLinkableForms] = useState<FormLinkOption[]>([]);
  const [displayFields, setDisplayFields] = useState<FormLinkFieldOption[]>([]);
  const [loadingDisplayFields, setLoadingDisplayFields] = useState(false);

  // Rollup field: child tables and their fields
  const [rollupChildTables, setRollupChildTables] = useState<
    { id: string; name: string; fields: { id: string; name: string; code: string }[] }[]
  >([]);
  const [selectedRollupChildTableId, setSelectedRollupChildTableId] = useState<string | null>(null);

  // Lookup field: fields from linked table
  const [lookupTargetFields, setLookupTargetFields] = useState<FormLinkFieldOption[]>([]);
  const [loadingLookupFields, setLoadingLookupFields] = useState(false);

  // Formula test values
  const [testValues, setTestValues] = useState<Record<string, unknown>>({});

  // v2 (T-SQL) calc formula compile status. Debounced (400ms) against the
  // compile-only endpoint — same pattern as CelField.tsx's inline
  // validation. Nothing is executed server-side; compile-only, safe on
  // every keystroke. Reset to idle whenever the field isn't in v2 mode
  // (no declared result type) or has no formula yet.
  const [calcCompileState, setCalcCompileState] = useState<{
    status: "idle" | "checking" | "ok" | "error";
    error: string | null;
    resultSqlType: string | null;
    previewAvailable: boolean;
  }>({ status: "idle", error: null, resultSqlType: null, previewAvailable: false });

  const calcResultTypeId = fieldData.calculation_result_field_type_id ?? null;
  useEffect(() => {
    const formula = fieldData.calculation_formula?.trim();
    if (!formula || !calcResultTypeId) {
      setCalcCompileState({ status: "idle", error: null, resultSqlType: null, previewAvailable: false });
      return;
    }
    setCalcCompileState((prev) => ({ ...prev, status: "checking" }));
    let cancelled = false;
    const handle = setTimeout(() => {
      myappApi.fields
        .compileCalculation(token, formId, {
          expression: formula,
          result_field_type_id: calcResultTypeId,
          exclude_field_id: editingField?.id,
        })
        .then((result) => {
          if (cancelled) return;
          setCalcCompileState({
            status: result.is_valid ? "ok" : "error",
            error: result.error,
            resultSqlType: result.result_sql_type,
            previewAvailable: result.preview_available,
          });
        })
        .catch((err) => {
          if (cancelled) return;
          setCalcCompileState({
            status: "error",
            error: err instanceof Error ? err.message : "Failed to validate formula",
            resultSqlType: null,
            previewAvailable: false,
          });
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [fieldData.calculation_formula, calcResultTypeId, token, formId, editingField?.id]);

  // Field name validation
  const [fieldNameError, setFieldNameError] = useState<string | null>(null);
  const [fieldNameValidating, setFieldNameValidating] = useState(false);
  const [generatedColumnName, setGeneratedColumnName] = useState<string | null>(null);

  // Delete field dialog
  const [deleteFieldOpen, setDeleteFieldOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<FormField | null>(null);
  const [deletingField, setDeletingField] = useState(false);
  // Field ids whose one-click unhide is in flight (disables that row's Eye).
  const [unhidingFieldIds, setUnhidingFieldIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // Raw caught error from the hide/delete attempt, rendered structured
  // inside the dialog. Holds the PortalApiError so ApiErrorAlert can show
  // the FIELD_HAS_DEPENDENTS blocking list + hint, not just a flat string.
  const [deleteFieldError, setDeleteFieldError] = useState<unknown>(null);
  // Dependents of the field being hidden/deleted, fetched when the dialog
  // opens so we can DISABLE the action up front when the field is in use,
  // rather than letting the user click into a failure. null = not yet
  // checked; the dialog disables the action while checking too.
  const [deleteFieldDependents, setDeleteFieldDependents] =
    useState<FieldDependentsResponse | null>(null);
  const [checkingDependents, setCheckingDependents] = useState(false);
  // Tracks the field id of the in-flight dependents check so a slow
  // response from a previously-opened field can't overwrite the current
  // one (close-A then open-B with A still in flight). Only the response
  // matching the latest requested id is applied.
  const dependentsReqRef = useRef<string | null>(null);

  // Dependents of the Link field currently being edited. Fetched on open so
  // the editor can offer re-point / detach ONLY when the link is isolated
  // (nothing references it) — mirrors the backend isolation gate. null = not
  // yet loaded / not a published link (treated as "not isolated", conservative).
  const [editLinkDependents, setEditLinkDependents] =
    useState<FieldDependentsResponse | null>(null);
  const editLinkDepsReqRef = useRef<string | null>(null);

  // Monotonic token guarding the displayFields loads: handleLinkedFormChange
  // and dismissRepointDialog both write the same state, and a slow earlier
  // fetch (pick table A) must not clobber a later one (dismiss restores B) —
  // same hazard the dependents fetches guard with their reqRefs. An id-keyed
  // ref can't be used here because a restore can target the SAME table id as
  // an in-flight pick. Un-tokened writers (fresh dialog open) bump it to
  // invalidate any in-flight tokened load.
  const displayFieldsLoadRef = useRef(0);

  // Publish dialog
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState(0);
  const publishTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [ddlPreview, setDdlPreview] = useState<string | null>(null);
  const [loadingDdl, setLoadingDdl] = useState(false);
  const [showTechnicalInfo, setShowTechnicalInfo] = useState(false);

  // Pairing (bridge) binding — authored inline so the user never leaves the editor.
  // `publishAfterBinding` means the builder was opened from the Publish button and
  // should auto-continue to publish once the binding is saved.
  const [bindingBuilderOpen, setBindingBuilderOpen] = useState(false);
  const [publishAfterBinding, setPublishAfterBinding] = useState(false);

  // Repair fields
  const [repairing, setRepairing] = useState(false);
  const [repairMessage, setRepairMessage] = useState<string | null>(null);

  // Wave 0b: backfill the inherited-source FK on this table's inherited
  // fields. Result of the last apply (so we can list any that still need
  // the picker); null until the user runs it.
  const [inheritBackfilling, setInheritBackfilling] = useState(false);
  const [inheritBackfillResult, setInheritBackfillResult] =
    useState<BackfillInheritSourcesResponse | null>(null);

  // "Upgrade Link Table Fields" — move link fields onto the per-field SysFk
  // scheme. linkFkFound is the dry-run count (null while unknown); set on load
  // for published parent/standalone tables (the cascade covers children).
  const [linkFkFound, setLinkFkFound] = useState<number | null>(null);
  const [linkFkUpgrading, setLinkFkUpgrading] = useState(false);
  const [linkFkConfirmOpen, setLinkFkConfirmOpen] = useState(false);
  const [linkFkResult, setLinkFkResult] = useState<string | null>(null);
  const linkFkStatus = useMemo(
    () => deriveLinkFkUpgradeStatus(linkFkFound),
    [linkFkFound],
  );
  // Dry-run the link-FK upgrade to learn how many fields still need it. Cheap
  // (a sys.columns read per table); cascades to children so the count covers
  // the Lines/Versions tables too. Safe to call repeatedly. Declared here (not
  // beside the apply handler) so the load-probe effect below can depend on it.
  const refreshLinkFkStatus = useCallback(async () => {
    try {
      const preview = await myappApi.fields.upgradeLinkFk(token, formId, true);
      setLinkFkFound(preview.total_found);
    } catch {
      // Non-fatal: the card just won't show. Don't surface a page error for a
      // background status probe.
      setLinkFkFound(0);
    }
  }, [token, formId]);

  // Full repair / re-publish
  const [fullRepairing, setFullRepairing] = useState(false);
  const [fullRepairResult, setFullRepairResult] = useState<{
    table_created?: boolean;
    computed_columns_fixed: number;
    fk_columns_added: number;
    lookup_records_updated: number;
    int_to_nvarchar_fixed?: number;
    lengths_fixed?: number;
    temp_columns_fixed?: number;
    temp_columns_dropped?: number;
    child_tables_published?: number;
    child_tables_failed?: Array<{ id: string; name: string; error: string }>;
    warnings: string[];
    errors: string[];
  } | null>(null);
  const [republishPreviewOpen, setRepublishPreviewOpen] = useState(false);
  const [republishPreview, setRepublishPreview] = useState<{
    computed_columns_found: number;
    missing_fk_columns_found: number;
    int_to_nvarchar_found: number;
    wrong_length_found: number;
    temp_columns_found?: number;
    warnings: string[];
  } | null>(null);

  // Organisation-level area sets (for AreaSet picker)
  const [areaSets, setAreaSets] = useState<AreaSet[]>([]);
  const [loadingAreaSets, setLoadingAreaSets] = useState(false);

  // Virtual table query validation
  const [queryValidating, setQueryValidating] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [detectedColumns, setDetectedColumns] = useState<
    { name: string; sql_type: string; suggested_field_type: string }[]
  >([]);
  const [previewRows, setPreviewRows] = useState<Record<string, string | null>[]>([]);
  const { refreshing: virtualRefreshing, triggerRefresh: handleVirtualRefresh } = useVirtualRefresh({
    token,
    formId: String(formId),
    onSuccess: async () => {
      const refreshed = await myappApi.tables.get(token, formId);
      setForm(refreshed);
    },
  });
  const [sourceQueryDraft, setSourceQueryDraft] = useState<string | null>(null);

  // Calculation formula change confirmation (for published tables)
  const [formulaChangeDialogOpen, setFormulaChangeDialogOpen] = useState(false);
  const [originalFormula, setOriginalFormula] = useState<string | null>(null);
  // Flipping v1<->v2 (or changing the declared result type) is a real
  // evaluator change even when the formula TEXT is untouched — the guard
  // below must catch that too, not just a literal text diff.
  const [originalResultTypeId, setOriginalResultTypeId] = useState<string | null>(null);

  // Inherited-source repoint confirmation (for published tables). The snapshot
  // warning ("existing rows keep their current value") is what the user is
  // approving; auto-unlock REPAIRS (FK was NULL or dangling) skip this gate
  // because there is nothing for existing rows to drift from.
  const [inheritSourceChangeDialogOpen, setInheritSourceChangeDialogOpen] =
    useState(false);

  // Post-conversion backfill prompt (WS1). After an existing PUBLISHED field is
  // converted to inherited, its old row values are stale — offer to back-fill
  // them from the parent via republish(backfill_inherited=true).
  const [inheritBackfillPromptOpen, setInheritBackfillPromptOpen] =
    useState(false);
  const [inheritBackfillField, setInheritBackfillField] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [inheritBackfillRunning, setInheritBackfillRunning] = useState(false);

  // Unique constraint change confirmation (for published tables)
  const [uniqueChangeDialogOpen, setUniqueChangeDialogOpen] = useState(false);
  const [uniqueConfirmText, setUniqueConfirmText] = useState("");
  // Re-pointing an isolated published link to a different table — "type to
  // confirm" gate (the no-backfill change is irreversible for existing rows).
  const [repointDialogOpen, setRepointDialogOpen] = useState(false);
  const [repointConfirmText, setRepointConfirmText] = useState("");

  // Field type change confirmation (for published tables)
  const [typeChangeDialogOpen, setTypeChangeDialogOpen] = useState(false);
  const [typeChangePreview, setTypeChangePreview] = useState<TypeChangePreviewResponse | null>(null);
  const [typeChangeConfirmText, setTypeChangeConfirmText] = useState("");
  const [typeChangeLoading, setTypeChangeLoading] = useState(false);
  const [structureChanged, setStructureChanged] = useState(false);
  const [republishPromptOpen, setRepublishPromptOpen] = useState(false);
  const [structureChanges, setStructureChanges] = useState<
    { fieldName: string; fromType: string; toType: string }[]
  >([]);

  // Single-shot approval flags for the published-table confirmations.
  // Stored as refs (not state) so the value is immediately visible to the
  // next handleSaveField call without waiting for re-render, and so the
  // re-entrant calls between the two confirmation dialogs can't re-open
  // each other in a loop. Set true on Apply Change / Update Formula, and
  // reset to false whenever the user changes the underlying input (type
  // dropdown / formula text) or the Edit Field dialog closes.
  const pendingTypeChangeApprovedRef = useRef(false);
  const pendingFormulaChangeApprovedRef = useRef(false);
  const pendingInheritSourceChangeApprovedRef = useRef(false);
  const pendingRepointApprovedRef = useRef(false);

  // Read the token via a ref so an auth-token rotation does NOT recreate
  // fetchData. Without it, every keep-alive / 401-driven refresh flips the
  // page-level `loading` true and the `if (loading) return <spinner>` gate
  // unmounts the whole designer — including an open Edit Field dialog and its
  // unsaved draft (field name, SQL source query, type-change/inherit
  // confirmations). Navigating to a different table still refetches via formId.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const fetchData = useCallback(async () => {
    const currentToken = tokenRef.current;
    try {
      setLoading(true);
      const [formData, types, forms, children] = await Promise.all([
        myappApi.tables.get(currentToken, formId),
        myappApi.lookups.listFieldTypes(currentToken),
        myappApi.lookups.listLinkableForms(currentToken, formId),
        myappApi.tables.getChildren(currentToken, formId),
      ]);
      setForm(formData);
      setFieldTypes(types);
      setLinkableForms(forms);
      setChildTables(children);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load table");
    } finally {
      setLoading(false);
    }

    // Token read via tokenRef so rotation doesn't remount the designer; only
    // formId refetch.
  }, [formId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset the published-table confirmation approval flags whenever the Edit
  // Field dialog closes. Without this, a stale "true" from a previous edit
  // session would let the next save bypass the confirmation entirely.
  useEffect(() => {
    if (!fieldDialogOpen) {
      pendingTypeChangeApprovedRef.current = false;
      pendingFormulaChangeApprovedRef.current = false;
      pendingInheritSourceChangeApprovedRef.current = false;
      pendingRepointApprovedRef.current = false;
    }
  }, [fieldDialogOpen]);

  // Parent-table context (only loaded when this table has a parent — i.e. line
  // table). Shared between the "Inherit from Parent" flow and the per-parent
  // auto-number config panel below — avoids two duplicate fetches on the page.
  const { parentTable, inheritableParentFields, resolveSourceFieldHealth } =
    useParentTable(token, form?.parent_table_id ?? null);

  // Fetch organisation-level area sets when area_mode is enabled
  useEffect(() => {
    if (form && form.area_mode !== "none") {
      setLoadingAreaSets(true);
      myappApi.areaSets.list(token)
        .then(setAreaSets)
        .catch(() => setAreaSets([]))
        .finally(() => setLoadingAreaSets(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.area_mode, token]);

  // Fetch workflow states for the per-field "Locks at" / "Required from"
  // pickers.
  //
  // Composite CHILD tables: this is the only source. Children inherit
  // state from their parent (their fields' LockedAtStateId references
  // the parent's TableState rows), and TableStatesEditor is NOT rendered
  // on a child's designer page. So we fetch states from the parent.
  //
  // TOP-LEVEL Simple/Advanced tables: skip the fetch entirely. The
  // on-page TableStatesEditor renders and broadcasts every state
  // mutation (initial load, add, rename, delete) via
  // `handleTableStatesChanged` below. That broadcast is the canonical
  // source. A duplicate fetch here would mean two GETs per page load
  // and a transient race where the broadcast's initial empty value
  // could clobber the parent's loaded value before the editor's own
  // fetch resolves.
  useEffect(() => {
    if (!form) return;
    if (!form.parent_table_id) return; // Top-level: rely on the editor's broadcast.
    myappApi.tableStates
      .list(token, form.parent_table_id)
      .then((sts) =>
        setTableStates(
          sts.map((s) => ({
            id: s.id,
            name: s.name,
            sort_order: s.sort_order,
            is_terminal: s.is_terminal,
            description: s.description ?? null,
          })),
        ),
      )
      .catch(() => setTableStates([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.id, form?.parent_table_id, token]);

  // Probe the link-FK upgrade status once a published parent/standalone table
  // loads. Line tables are skipped — they're upgraded via the parent's cascade
  // (same routing as "Re-publish Structure"), so we don't offer the button on
  // them. The probe is a cheap dry-run; the card only renders when found > 0.
  useEffect(() => {
    if (form?.is_published && !form.parent_table_id) {
      void refreshLinkFkStatus();
    } else {
      setLinkFkFound(null);
    }
  }, [form?.id, form?.is_published, form?.parent_table_id, refreshLinkFkStatus]);

  // Stable subscription for TableStatesEditor's onStatesChanged broadcast.
  // Without useCallback the subscription effect inside TableStatesEditor
  // would tear down and re-fire on every parent render, double-emitting
  // and potentially fighting the field-editor's open dialog.
  const handleTableStatesChanged = useCallback(
    (states: TableState[]) => {
      setTableStates(
        states.map((s) => ({
          id: s.id,
          name: s.name,
          sort_order: s.sort_order,
          is_terminal: s.is_terminal,
          description: s.description ?? null,
        })),
      );
    },
    [],
  );

  // Highlighted matrix row, used so a click on a field card's pill row
  // scrolls the Lockdown preview into view AND visually flashes the
  // matching row so the user can find it.
  const [highlightedFieldId, setHighlightedFieldId] = useState<string | null>(
    null,
  );
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jumpToMatrixRow = useCallback((fieldId: string, _stateId: string) => {
    const el = document.getElementById(`field-state-row-${fieldId}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setHighlightedFieldId(fieldId);
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
    }
    highlightTimerRef.current = setTimeout(() => {
      setHighlightedFieldId(null);
      highlightTimerRef.current = null;
    }, 1500);
  }, []);
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
    };
  }, []);

  // Optimistic per-field state-rule change driven by the inline
  // Required-from / Read-only-from selects on each field card. We own
  // the form.fields array, so we apply the patch locally first (the
  // matrix + pill rows snap immediately), then PUT, then either replace
  // the row with the server response or roll back to the pre-click
  // values on error.
  //
  // Rollback semantics: ONLY this field's two state-rule values are
  // captured + restored. We deliberately do NOT snapshot
  // form.fields as a whole — a concurrent change to a sibling field
  // would otherwise be wiped if this PUT fails. Sibling changes are
  // independent and must survive each other's failures.
  //
  // BE merge semantics: `locked_at_state_id` and `required_from_state_id`
  // both use the "UNSET" sentinel on the data-plane side
  // (table_field_service.update), so sending only the changed key
  // preserves the other one. Reference: lara-data-plane
  // src/myapp/api/v1/table_fields.py update_field (line 263+).
  const handleFieldRuleChange = useCallback(
    async (
      fieldId: string,
      patch: {
        locked_at_state_id?: string | null;
        required_from_state_id?: string | null;
      },
    ) => {
      const target = form?.fields.find((f) => f.id === fieldId);
      if (!target) return;
      // Capture ONLY this field's pre-click rule values for rollback.
      const fieldSnapshot = {
        locked_at_state_id: target.locked_at_state_id,
        required_from_state_id: target.required_from_state_id,
      };
      // Optimistic local apply.
      setForm((prev) =>
        prev
          ? {
              ...prev,
              fields: prev.fields.map((f) =>
                f.id === fieldId ? { ...f, ...patch } : f,
              ),
            }
          : null,
      );
      try {
        const updated = await myappApi.fields.update(
          token,
          formId,
          fieldId,
          patch,
        );
        setForm((prev) =>
          prev
            ? {
                ...prev,
                fields: prev.fields.map((f) =>
                  f.id === updated.id ? updated : f,
                ),
              }
            : null,
        );
      } catch (err) {
        // Per-field rollback: only restore THIS field's rule values, so
        // any concurrent sibling-field optimistic patches are preserved.
        setForm((prev) =>
          prev
            ? {
                ...prev,
                fields: prev.fields.map((f) =>
                  f.id === fieldId ? { ...f, ...fieldSnapshot } : f,
                ),
              }
            : null,
        );
        toast.error(
          err instanceof Error
            ? err.message
            : "Failed to update field state rule",
        );
      }
    },
    [form?.fields, formId, token],
  );

  const handleEditFormClick = () => {
    if (!form) return;
    setEditFormData({ name: form.name, description: form.description || "" });
    setEditNameError(null);
    setEditNameValidating(false);
    setEditFormOpen(true);
  };

  const handleCreateChildClick = () => {
    if (!form) return;
    setNewChildName(`${form.name} Lines`);
    setCreateChildDialogOpen(true);
  };

  const handleCreateChild = async () => {
    if (!form || !newChildName.trim()) return;

    try {
      setCreatingChild(true);
      const child = await myappApi.tables.create(token, {
        name: newChildName,
        parent_table_id: formId,
      });
      setChildTables((prev) => [...prev, child]);
      setCreateChildDialogOpen(false);
      setNewChildName("");
      // Mark this table as a header table
      setForm((prev) => (prev ? { ...prev, is_header_table: true } : null));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create child table");
    } finally {
      setCreatingChild(false);
    }
  };

  const handleDeleteChildClick = async (child: Form) => {
    setChildToDelete(child);
    setChildDeps(null);
    setDeleteChildError(null);
    setConfirmDeleteChildText("");
    setCheckingChildDeps(true);
    setDeleteChildDialogOpen(true);

    try {
      const deps = await myappApi.tables.checkDependencies(token, child.id);
      setChildDeps(deps);
    } catch (err) {
      console.error("Failed to check line table dependencies:", err);
      setDeleteChildError(
        "Failed to check line table dependencies. Please try again.",
      );
    } finally {
      setCheckingChildDeps(false);
    }
  };

  const handleDeleteChildConfirm = async () => {
    if (!childToDelete || !token) return;

    try {
      setDeletingChild(true);
      setDeleteChildError(null);
      await myappApi.tables.deleteWithConfirm(
        token,
        childToDelete.id,
        confirmDeleteChildText,
        childToDelete.is_published,
      );
      setDeleteChildDialogOpen(false);
      setChildToDelete(null);
      setChildDeps(null);
      // Refresh the line-table list from the server. We deliberately do not
      // flip is_header_table / table_type here: the backend keeps the header
      // marked "advanced" even with zero line tables (mirroring how an
      // advanced table exists before any line is added), so touching it on the
      // client would only drift from the persisted state until the next load.
      const updatedChildren = await myappApi.tables.getChildren(token, formId);
      setChildTables(updatedChildren);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to delete line table";
      setDeleteChildError(message);
    } finally {
      setDeletingChild(false);
    }
  };

  const handleSaveForm = async () => {
    if (!form || !editFormData.name.trim()) return;

    try {
      setSaving(true);
      const updated = await myappApi.tables.update(token, formId, {
        name: editFormData.name,
        description: editFormData.description || null,
      });
      setForm((prev) => (prev ? { ...prev, ...updated } : null));
      setEditFormOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update table";
      // Show name-related errors inside the dialog, not on the page
      setEditNameError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddFieldClick = (opts?: { presetFieldTypeId?: string }) => {
    setEditingField(null);
    setFieldData({
      name: "",
      field_type_id: opts?.presetFieldTypeId ?? (fieldTypes[0]?.id || ""),
      description: null,
      is_required: false,
      is_unique: false,
      default_value: null,
      max_length: null,
      min_value: null,
      max_value: null,
      linked_table_id: null,
      linked_display_field_id: null,
      source_link_field_id: null,
      lookup_field_id: null,
      calculation_formula: null,
      is_hidden: false,
      rollup_source_field_id: null,
      rollup_function: null,
      kanban_mode: null,
      locked_at_state_id: null,
      required_from_state_id: null,
    });
    setDisplayFields([]);
    setLookupTargetFields([]);
    setSelectedRollupChildTableId(null);
    setFieldNameError(null);
    setGeneratedColumnName(null);
    setFieldDialogOpen(true);
  };

  // inheritableParentFields now comes from the useParentTable hook above —
  // shared with the per-parent auto-number config panel to keep both surfaces
  // consistent on what counts as an eligible source type.

  // The hidden parent-link field on THIS table — the inheritance trigger.
  const parentLinkField = useMemo(() => {
    if (!form?.parent_table_id || !form.fields) return null;
    return (
      form.fields.find(
        (f) =>
          f.is_hidden &&
          f.field_type?.code === "link" &&
          f.linked_table_id === form.parent_table_id
      ) ?? null
    );
  }, [form]);

  // Handler for creating a new text field from auto-number dropdown
  const handleCreateFieldFromAutoNumber = () => {
    setEditingField(null);
    setFieldData({
      name: "",
      field_type_id: fieldTypes.find((ft) => ft.code === "text")?.id || "", // Text field pre-selected
      description: null,
      is_required: false,
      is_unique: false,
      default_value: null,
      max_length: null,
      min_value: null,
      max_value: null,
      linked_table_id: null,
      linked_display_field_id: null,
      source_link_field_id: null,
      lookup_field_id: null,
      calculation_formula: null,
      is_hidden: false,
    });
    setDisplayFields([]);
    setLookupTargetFields([]);
    setFieldNameError(null);
    setGeneratedColumnName(null);
    setFieldDialogOpen(true);
  };

  // Validate field name on blur
  const handleFieldNameBlur = async () => {
    const name = fieldData.name.trim();
    if (!name) {
      setFieldNameError("Field name is required");
      setGeneratedColumnName(null);
      return;
    }

    setFieldNameValidating(true);
    try {
      const result = await myappApi.fields.validateName(
        token,
        formId,
        name,
        editingField?.id
      );
      if (result.is_valid) {
        setFieldNameError(null);
      } else {
        setFieldNameError(result.message);
      }
      setGeneratedColumnName(result.generated_column_name);
    } catch (err) {
      // If validation fails, let the save operation handle it
      setFieldNameError(null);
    } finally {
      setFieldNameValidating(false);
    }
  };

  const handleEditFieldClick = async (field: FormField) => {
    setEditingField(field);
    // Capture original formula for change detection on published tables
    setOriginalFormula(field.calculation_formula);
    setOriginalResultTypeId(field.calculation_result_field_type_id ?? null);

    // For a published Link field, fetch its dependents so the editor can decide
    // whether re-point / detach is offered. A link is "isolated" only when
    // nothing references it; otherwise the dropdown / linked-table panel stay
    // locked (matching the backend's FieldHasDependentsError gate). The ref
    // guards against a slow response from a previously-opened field.
    setEditLinkDependents(null);
    if (form?.is_published && field.field_type?.code === "link") {
      editLinkDepsReqRef.current = field.id;
      myappApi.fields
        .dependents(token, formId, field.id)
        .then((res) => {
          if (editLinkDepsReqRef.current === field.id) setEditLinkDependents(res);
        })
        .catch(() => {
          // On a check failure, leave dependents null → not isolated → the
          // panel stays locked and the backend remains the authority.
          if (editLinkDepsReqRef.current === field.id) setEditLinkDependents(null);
        });
    } else {
      editLinkDepsReqRef.current = null;
    }
    // Reset validation state
    setFieldNameError(null);
    setGeneratedColumnName(field.column_name);
    // For inherited fields, surface the "Inherit from Parent" sentinel in
    // the type dropdown so the dialog clearly reflects what was originally
    // picked. Resolution preference:
    //   0. source_field_id (canonical FK) - the BE's authoritative pointer.
    //      Set at create time for new rows, and by the user clicking the
    //      picker on existing rows. Always trust it when present; the BE
    //      cascade resolver does the same.
    //   1. ColumnName exact match - the rule cascade_service uses on the BE
    //      when source_field_id is null. Hits when the user kept the
    //      auto-populated name at create time.
    //   2. Name exact match - hits when the user renamed the child field
    //      so its column diverged, but the display names still match (rare
    //      after rename but cheap to check).
    //   3. OriginalName match (name-to-name, not name-to-column) - hits
    //      when the child was renamed AFTER create; OriginalName captures
    //      the pre-rename display name.
    // If all matchers miss, the source picker stays blank and the user
    // can manually pick. Tier 0 is what makes Fusion Leather-class rows
    // (non-conventional names that never matched any heuristic) visible
    // in the picker once the BE pointer is set.
    let reconstructedSourceFieldId = "";
    if (field.is_inherited) {
      if (field.source_field_id) {
        const direct = inheritableParentFields.find(
          (f) => f.id === field.source_field_id,
        );
        if (direct) reconstructedSourceFieldId = direct.id;
      }
      if (!reconstructedSourceFieldId) {
        const src = inheritableParentFields.find(
          (f) =>
            f.column_name === field.column_name ||
            f.name === field.name ||
            (field.original_name !== null && f.name === field.original_name),
        );
        reconstructedSourceFieldId = src?.id ?? "";
      }
    }
    setInheritSourceFieldId(reconstructedSourceFieldId);
    // Open the source read-only by default; the pencil reveals the Select.
    // (A field with no source yet is editable from the start via
    // sourceIsEditable in the render.)
    setEditingSource(false);
    setInheritError(null);
    setFieldData({
      name: field.name,
      field_type_id: field.is_inherited
        ? INHERIT_FROM_PARENT_ID
        : field.field_type_id,
      description: field.description,
      is_required: field.is_required,
      is_unique: field.is_unique,
      default_value: field.default_value,
      max_length: field.max_length,
      min_value: field.min_value,
      max_value: field.max_value,
      linked_table_id: field.linked_table_id,
      linked_display_field_id: field.linked_display_field_id,
      source_link_field_id: field.source_link_field_id,
      lookup_field_id: field.lookup_field_id,
      calculation_formula: field.calculation_formula,
      calculation_result_field_type_id: field.calculation_result_field_type_id ?? null,
      concatenation_template: field.concatenation_template,
      options_config: field.options_config,
      show_total: field.show_total,
      decimal_places: field.decimal_places,
      is_hidden: field.is_hidden,
      rollup_source_field_id: field.rollup_source_field_id,
      rollup_function: field.rollup_function,
      kanban_mode: field.kanban_mode,
      locked_at_state_id: field.locked_at_state_id ?? null,
      required_from_state_id: field.required_from_state_id ?? null,
    });

    // If editing a rollup field, determine which child table it belongs to
    if (field.field_type?.code === "rollup" && field.rollup_source_field_id) {
      try {
        const children = await myappApi.tables.getChildren(token, formId);
        for (const child of children) {
          const childFields = await myappApi.fields.list(token, child.id);
          const sourceField = childFields.find(
            (f: FormField) => f.id === field.rollup_source_field_id
          );
          if (sourceField) {
            setSelectedRollupChildTableId(child.id);
            break;
          }
        }
      } catch {
        // Non-critical — the dropdown will just be empty
      }
    } else {
      setSelectedRollupChildTableId(null);
    }

    // Load display fields if this is a linked field. Bump the load token so a
    // still-in-flight tokened load from a previous dialog session (linked-table
    // pick / re-point dismiss) can't land on top of this fresh dialog's list.
    displayFieldsLoadRef.current++;
    if (field.linked_table_id) {
      try {
        const fields = await myappApi.lookups.listFormDisplayFields(token, field.linked_table_id);
        setDisplayFields(fields);
      } catch {
        setDisplayFields([]);
      }
    } else {
      setDisplayFields([]);
    }

    // Load lookup target fields if this is a lookup field
    if (field.source_link_field_id) {
      const sourceField = form?.fields.find(f => f.id === field.source_link_field_id);
      if (sourceField?.linked_table_id) {
        try {
          // Include link fields so lookup can target FK fields (e.g., Country)
          const fields = await myappApi.lookups.listFormDisplayFields(token, sourceField.linked_table_id, true);
          setLookupTargetFields(fields);
        } catch {
          setLookupTargetFields([]);
        }
      }
    } else {
      setLookupTargetFields([]);
    }

    setFieldDialogOpen(true);
  };

  const handleSaveField = async () => {
    if (!form || !fieldData.name.trim() || !fieldData.field_type_id) return;

    // Inherit-from-Parent sentinel short-circuits the normal type-based path.
    // The field type is derived from the picked parent field, and the
    // cascade_service handles value propagation on insert/update.
    if (fieldData.field_type_id === INHERIT_FROM_PARENT_ID) {
      if (!parentLinkField) {
        setInheritError(
          "This table has no parent link, so inherit mode is only available on line tables."
        );
        return;
      }
      // Edit mode: route through PUT. On DRAFT tables the user may have
      // re-picked the source via the (now-enabled) source picker, so we
      // resolve the source from inheritSourceFieldId and use its
      // field_type_id rather than echoing editingField's. On PUBLISHED
      // tables the picker is locked so the resolved source matches the
      // reconstructed original, and editingField acts as a safety net if
      // reconstruction failed.
      if (editingField) {
        const isConversion = !editingField.is_inherited;
        const sourceField = inheritableParentFields.find(
          (f) => f.id === inheritSourceFieldId,
        );

        // Converting a non-inherited field requires the user to pick the
        // parent source to inherit from (parity with the create path).
        if (isConversion && !sourceField) {
          setInheritError("Select a parent field to inherit from.");
          return;
        }

        // On a PUBLISHED table the conversion must stay metadata-only (no
        // DDL): the column keeps its type and the cascade simply starts
        // populating it from the parent. Require the chosen parent field to be
        // the SAME type, so the existing column can hold the inherited value —
        // retyping the column and inheriting in one step is not supported here.
        // (Draft tables can retype freely; the column isn't materialised yet.)
        if (
          isConversion &&
          form?.is_published &&
          sourceField?.field_type?.id &&
          sourceField.field_type.id !== editingField.field_type_id
        ) {
          setInheritError(
            "To inherit after publish, pick a parent field of the same type as this field. " +
              "Changing the column type and inheriting at the same time isn't supported.",
          );
          return;
        }

        // Published conversion keeps the current type (metadata-only);
        // otherwise adopt the picked source's type (draft can retype, and
        // already-inherited edits echo their existing type when no new source
        // is picked).
        const newFieldTypeId =
          form?.is_published && isConversion
            ? editingField.field_type_id
            : sourceField?.field_type?.id ?? editingField.field_type_id;
        setSaving(true);
        setInheritError(null);
        try {
          const payload: FormFieldUpdate = {
            name: fieldData.name.trim(),
            description: fieldData.description,
            field_type_id: newFieldTypeId,
            is_inherited: true,
            // A converting field has no inherit link yet, so fall back to the
            // table's hidden parent-link field (guaranteed present — guarded
            // by the !parentLinkField check above).
            inherit_from_field_id:
              editingField.inherit_from_field_id ?? parentLinkField.id,
            // Carry the hidden flag so the unhide toggle works for inherited
            // fields too. The inherit-edit branch previously omitted this, so
            // toggling unhide on a hidden inherited field was silently
            // dropped (the BE preserved the hidden state). Unhiding never
            // trips the dependents guard (that only fires on the hide
            // transition), so this is safe.
            is_hidden: fieldData.is_hidden ?? false,
          };
          // Only touch source_field_id when the user actually engaged the
          // picker: either there was no source yet, or they clicked the
          // pencil to change it. When the source was shown read-only and
          // left untouched, OMIT the key entirely so the BE preserves it
          // (UNSET semantics). Sending inheritSourceFieldId unconditionally
          // would risk clearing a healthy FK if the reconstruction hadn't
          // resolved yet (the load race).
          const sourceWasEditable =
            !editingField.source_field_id || editingSource;
          if (sourceWasEditable) {
            // Persist the picker's choice as the canonical FK. Send null
            // when the picker is empty so the BE clears the pointer (back
            // to heuristic fallback / repair state).
            payload.source_field_id = inheritSourceFieldId || null;
          }
          // On draft tables only, also propagate the source's linked refs
          // so picking a link source re-mirrors the FK targets. BE update
          // rejects these on published tables and would 400 the request.
          if (!form?.is_published && sourceField) {
            payload.linked_table_id = sourceField.linked_table_id ?? null;
            payload.linked_display_field_id =
              sourceField.linked_display_field_id ?? null;
          }
          const updated = await myappApi.fields.update(token, formId, editingField.id, payload);
          setForm((prev) =>
            prev
              ? {
                  ...prev,
                  fields: prev.fields.map((f) =>
                    f.id === updated.id ? updated : f,
                  ),
                }
              : null,
          );
          setFieldDialogOpen(false);

          // Converting an EXISTING published field to inherited leaves the old
          // row values in place, but the value should now come from the
          // parent. Offer to back-fill existing rows from the parent (the
          // republish cascade overwrites every inherited column).
          if (isConversion && form?.is_published) {
            setInheritBackfillField({ id: updated.id, name: updated.name });
            setInheritBackfillPromptOpen(true);
          }
        } catch (err) {
          setInheritError(
            err instanceof Error ? err.message : "Failed to update inherited field",
          );
        } finally {
          setSaving(false);
        }
        return;
      }
      const sourceField = inheritableParentFields.find(
        (f) => f.id === inheritSourceFieldId
      );
      if (!sourceField || !sourceField.field_type) {
        setInheritError("Select a parent field to inherit.");
        return;
      }
      setSaving(true);
      setInheritError(null);
      try {
        const payload: FormFieldCreate = {
          name: fieldData.name.trim(),
          field_type_id: sourceField.field_type.id,
          description: `Inherited from ${parentTable?.name ?? "parent"}`,
          is_inherited: true,
          inherit_from_field_id: parentLinkField.id,
          // Canonical pointer to the specific parent field the user picked.
          // Without it the new field lands with SourceFieldId NULL and the
          // cascade falls back to name-matching (or writes the FK id).
          source_field_id: sourceField.id,
          linked_table_id: sourceField.linked_table_id ?? null,
          linked_display_field_id: sourceField.linked_display_field_id ?? null,
        };
        await myappApi.fields.create(token, formId, payload);
        setFieldDialogOpen(false);
        await fetchData();
      } catch (err) {
        setInheritError(
          err instanceof Error ? err.message : "Failed to create inherited field"
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    const selectedType = fieldTypes.find((ft) => ft.id === fieldData.field_type_id);

    // Validate link field has required selections
    if (selectedType?.code === "link" && !fieldData.linked_table_id) {
      setError("Link fields must specify a target table");
      return;
    }

    // Validate lookup field has required selections
    if (selectedType?.code === "lookup") {
      if (!fieldData.source_link_field_id || !fieldData.lookup_field_id) {
        setError("Lookup fields require both a source link field and a target field to fetch");
        return;
      }
    }

    // Validate calculation field has formula
    if (selectedType?.code === "calculation" && !fieldData.calculation_formula?.trim()) {
      setError("Calculation fields require a formula");
      return;
    }

    // A known-invalid v2 formula is blocked client-side to save a round
    // trip, but this is advisory only — the debounced check can be stale
    // (e.g. mid-edit) and the SAVE ITSELF re-compiles server-side and is the
    // authoritative gate (table_field_service._compile_and_stamp_calculation).
    if (
      selectedType?.code === "calculation" &&
      calcResultTypeId &&
      calcCompileState.status === "error"
    ) {
      setError(calcCompileState.error || "This formula is not valid.");
      return;
    }

    // Validate concatenation field has template
    if (selectedType?.code === "concatenation" && !fieldData.concatenation_template?.trim()) {
      setError("Concatenation fields require a template");
      return;
    }

    // Validate rollup field has required selections
    if (selectedType?.code === "rollup") {
      if (!fieldData.rollup_source_field_id || !fieldData.rollup_function) {
        setError("Rollup fields require a source field and aggregation function");
        return;
      }
    }

    // Validate options field configuration (mirror of the backend rules).
    if (selectedType?.code === "options") {
      const cfg = parseOptionsConfig(fieldData.options_config);
      const optionsError = cfg
        ? validateOptionsConfig(cfg)
        : "Configure the options for this field.";
      if (optionsError) {
        setError(optionsError);
        return;
      }
    }

    // A hidden field cannot carry workflow-state rules. The workflow
    // machine enforces Required-from / Read-only-from on every write
    // (including programmatic ones), so leaving them set while the field
    // is no longer visible would block state transitions on a field the
    // user can't see or fix. Require the rules be cleared first.
    if (
      fieldData.is_hidden &&
      (fieldData.required_from_state_id != null ||
        fieldData.locked_at_state_id != null)
    ) {
      setError(
        "Clear Required-from and Read-only-from before hiding this field. " +
          "A hidden field cannot carry workflow-state rules."
      );
      return;
    }

    // Check if field type is being changed on a published table
    const isTypeChange =
      form?.is_published &&
      editingField &&
      fieldData.field_type_id !== editingField.field_type?.id;

    if (isTypeChange && !typeChangeDialogOpen && !pendingTypeChangeApprovedRef.current) {
      // Fetch preview and show confirmation dialog
      setTypeChangeLoading(true);
      try {
        const preview = await myappApi.fields.previewTypeChange(
          token,
          formId,
          editingField.id,
          fieldData.field_type_id,
        );
        setTypeChangePreview(preview);

        if (!preview.allowed) {
          setError(preview.reason || "This type change is not supported.");
          setTypeChangeLoading(false);
          return;
        }

        // All transitions on published tables require confirmation
        setTypeChangeDialogOpen(true);
        setTypeChangeConfirmText("");
        setTypeChangeLoading(false);
        return;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to preview type change");
        setTypeChangeLoading(false);
        return;
      }
    }

    // Check if formula (or its declared result type — a v1<->v2 flip is a
    // real evaluator change even with identical formula text) is being
    // changed on a published table.
    const isFormulaChange =
      form?.is_published &&
      editingField &&
      selectedType?.code === "calculation" &&
      (originalFormula !== fieldData.calculation_formula ||
        originalResultTypeId !== (fieldData.calculation_result_field_type_id ?? null));

    if (isFormulaChange && !formulaChangeDialogOpen && !pendingFormulaChangeApprovedRef.current) {
      // Show confirmation dialog first
      setFormulaChangeDialogOpen(true);
      return;
    }

    // Check if unique constraint is being removed on a published table
    const isRemovingUnique =
      form?.is_published &&
      editingField &&
      editingField.is_unique &&
      !fieldData.is_unique;

    // Re-pointing an isolated published link to a different table. This is not
    // a type change, so it bypasses the type-change gate above — give it its
    // own "type to confirm" step (the old FK is left dormant and existing rows
    // are not re-linked). The single-shot approval ref prevents a re-open loop.
    // `editingLinkIsIsolated` is asserted explicitly here (not just trusted via
    // the linked-table picker only being editable when isolated) so the re-point
    // gate doesn't depend on UI rendering state — the backend re-checks too.
    const isRepoint =
      form?.is_published &&
      editingField &&
      editingField.field_type?.code === "link" &&
      fieldData.field_type_id === editingField.field_type?.id &&
      Boolean(fieldData.linked_table_id) &&
      fieldData.linked_table_id !== editingField.linked_table_id &&
      editingLinkIsIsolated;

    if (isRepoint && !repointDialogOpen && !pendingRepointApprovedRef.current) {
      setRepointDialogOpen(true);
      setRepointConfirmText("");
      return;
    }

    if (isRemovingUnique && !uniqueChangeDialogOpen) {
      // Show confirmation dialog - user must type "change" to proceed
      setUniqueChangeDialogOpen(true);
      setUniqueConfirmText("");
      return;
    }

    // Wave 2: inherit-source repoint on a published table. Fires the
    // snapshot warning. Repair cases (FK was NULL or pointed at a
    // hidden/missing field) skip this gate - the user is fixing a
    // broken pointer, not changing behaviour, so there is nothing for
    // existing rows to drift from. Detection: the user must have
    // engaged the picker (`editingSource`) AND the prior FK was
    // present (`editingField.source_field_id`) AND the new FK
    // differs.
    const isInheritSourceRepoint =
      form?.is_published &&
      editingField &&
      Boolean(editingField.source_field_id) &&
      editingSource &&
      inheritSourceFieldId &&
      inheritSourceFieldId !== editingField.source_field_id;

    if (
      isInheritSourceRepoint &&
      !inheritSourceChangeDialogOpen &&
      !pendingInheritSourceChangeApprovedRef.current
    ) {
      setInheritSourceChangeDialogOpen(true);
      return;
    }

    try {
      setSaving(true);
      // Captured from the update response so the re-point notification below
      // (outside this block's scope) can inspect the new fk_column_name.
      let updatedField: FormField | undefined;
      if (editingField) {
        // Update existing field
        const updated = await myappApi.fields.update(token, formId, editingField.id, {
          name: fieldData.name,
          field_type_id: fieldData.field_type_id,
          description: fieldData.description,
          is_required: fieldData.is_required,
          is_unique: fieldData.is_unique,
          default_value: fieldData.default_value,
          max_length: fieldData.max_length,
          min_value: fieldData.min_value,
          max_value: fieldData.max_value,
          linked_table_id: fieldData.linked_table_id,
          linked_display_field_id: fieldData.linked_display_field_id,
          source_link_field_id: fieldData.source_link_field_id,
          lookup_field_id: fieldData.lookup_field_id,
          // Converting to a lookup clears the column's existing values (then
          // refills from the link). The typed-name confirmation below is the
          // user's acknowledgement; pass it so the BE allows the data loss.
          acknowledge_data_loss: isConvertingToLookup,
          calculation_formula: fieldData.calculation_formula,
          calculation_result_field_type_id: fieldData.calculation_result_field_type_id ?? null,
          concatenation_template: fieldData.concatenation_template,
          options_config: fieldData.options_config,
          show_total: fieldData.show_total,
          decimal_places: fieldData.decimal_places,
          is_hidden: fieldData.is_hidden,
          rollup_source_field_id: fieldData.rollup_source_field_id,
          rollup_function: fieldData.rollup_function,
          kanban_mode: fieldData.kanban_mode,
          locked_at_state_id: fieldData.locked_at_state_id ?? null,
          required_from_state_id: fieldData.required_from_state_id ?? null,
          // We're in the normal-update branch, which by definition means
          // fieldData.field_type_id is NOT the inherit sentinel. So the
          // current intent is "not inherited", regardless of the field's
          // previous state. Sending explicit false handles both paths:
          //   - Field was never inherited: no-op (already false in DB).
          //   - Field was inherited and user switched the type dropdown to
          //     a real type on a draft table (disinherit): BE clears
          //     IsInherited + InheritFromFieldId.
          is_inherited: false,
          inherit_from_field_id: null,
        } as FormFieldUpdate);
        setForm((prev) =>
          prev
            ? {
                ...prev,
                fields: prev.fields.map((f) =>
                  f.id === updated.id ? updated : f
                ),
              }
            : null
        );
        // Refresh editingField so any subsequent save attempt in the same
        // dialog session sees the new server-side type/formula and doesn't
        // re-detect a phantom type change against the stale pre-PUT snapshot.
        setEditingField(updated);
        updatedField = updated;
      } else {
        // Create new field
        const created = await myappApi.fields.create(token, formId, fieldData);
        setForm((prev) =>
          prev ? { ...prev, fields: [...prev.fields, created] } : null
        );
      }
      setFieldDialogOpen(false);
      setError(null);

      // After a successful type change on a published table, prompt to re-publish
      if (typeChangeDialogOpen && typeChangePreview) {
        setStructureChanges((prev) => [
          ...prev,
          {
            fieldName: editingField?.name || "Unknown",
            fromType: typeChangePreview.from_type_code,
            toType: typeChangePreview.to_type_code,
          },
        ]);
        setTypeChangeDialogOpen(false);
        setStructureChanged(true);
        setRepublishPromptOpen(true);
      } else if (isRepoint) {
        // A successful re-point also flips NeedsRepublish on the backend (the
        // new FK column needs the publish cascade) but isn't a type change, so
        // the branch above never fires — surface the same re-publish prompt.
        setStructureChanged(true);
        setRepublishPromptOpen(true);
        // Tell the user what happened to the old link column, inferred from
        // the returned physical FK name (no extra API surface): canonical =
        // the unused previous column was removed; a suffixed name = the
        // previous column holds data and is kept, with the change budget
        // capped at the original + two changes (BE contract, inplace_detach).
        const canonicalFk = `SysFk${updatedField?.column_name ?? ""}`;
        const fk = updatedField?.fk_column_name;
        if (fk === canonicalFk) {
          toast.success(
            "Linked table changed. The unused previous link column was removed."
          );
        } else if (fk === `${canonicalFk}2`) {
          toast.info(
            "Linked table changed. The previous link column holds data and has " +
              "been kept (no longer used). The linked table can be changed once more."
          );
        } else if (fk === `${canonicalFk}3`) {
          toast.info(
            "Linked table changed. The previous link column holds data and has " +
              "been kept (no longer used). The linked table cannot be changed " +
              "again — convert the field to Options or Text if you need a " +
              "different list."
          );
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save field");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFieldClick = (field: FormField) => {
    setFieldToDelete(field);
    // Clear any prior blocking-dependents error on open so a previous
    // block doesn't flash in this dialog. Clearing here (not only in
    // onOpenChange) covers the custom Cancel button too, which sets
    // open=false directly and so bypasses Radix's onOpenChange.
    setDeleteFieldError(null);
    setDeleteFieldDependents(null);
    setDeleteFieldOpen(true);
    // Check dependents up front so the action can be disabled before the
    // user clicks (firmer than failing on the BE with FIELD_HAS_DEPENDENTS).
    // Guard against a stale response from a previously-opened field via the
    // request-token ref: only apply the result if this is still the latest
    // requested field.
    const reqId = field.id;
    dependentsReqRef.current = reqId;
    setCheckingDependents(true);
    myappApi.fields
      .dependents(token, formId, field.id)
      .then((res) => {
        if (dependentsReqRef.current === reqId) setDeleteFieldDependents(res);
      })
      .catch(() => {
        // On a check failure, fall back to the BE guard: leave dependents
        // null (action enabled) and let the hide/delete attempt surface the
        // structured error if the field is in fact in use.
        if (dependentsReqRef.current === reqId) setDeleteFieldDependents(null);
      })
      .finally(() => {
        if (dependentsReqRef.current === reqId) setCheckingDependents(false);
      });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !form || active.id === over.id) {
      return;
    }

    const oldIndex = form.fields.findIndex((f) => f.id === active.id);
    const newIndex = form.fields.findIndex((f) => f.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistically update local state
    const reorderedFields = arrayMove(form.fields, oldIndex, newIndex);
    setForm((prev) => (prev ? { ...prev, fields: reorderedFields } : null));

    // Persist to backend
    try {
      setReordering(true);
      const fieldIds = reorderedFields.map((f) => f.id);
      await myappApi.fields.reorder(token, formId, fieldIds);
      toast.success("Field order saved");
    } catch (err) {
      // Revert on error and show toast
      const message = err instanceof Error ? err.message : "Failed to reorder fields";
      toast.error(message);
      setForm((prev) => (prev ? { ...prev, fields: form.fields } : null));
    } finally {
      setReordering(false);
    }
  };

  const handleChildDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = childTables.findIndex((c) => c.id === active.id);
    const newIndex = childTables.findIndex((c) => c.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistically update local state, then persist to backend.
    const previous = childTables;
    const reordered = arrayMove(childTables, oldIndex, newIndex);
    setChildTables(reordered);

    try {
      await myappApi.tables.reorderChildren(
        token,
        formId,
        reordered.map((c) => c.id),
      );
      toast.success("Line table order saved");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to reorder line tables";
      toast.error(message);
      setChildTables(previous);
    }
  };

  // One-click unhide (Eye on a hidden row). No confirm — the safe direction.
  const handleUnhideField = async (field: FormField) => {
    if (unhidingFieldIds.has(field.id)) return;
    setUnhidingFieldIds((prev) => new Set(prev).add(field.id));
    try {
      const updated = await unhideField(token, formId, field.id);
      if (updated) {
        setForm((prev) =>
          prev
            ? {
                ...prev,
                fields: prev.fields.map((f) =>
                  f.id === updated.id ? updated : f
                ),
              }
            : null
        );
      }
    } finally {
      setUnhidingFieldIds((prev) => {
        const next = new Set(prev);
        next.delete(field.id);
        return next;
      });
    }
  };

  const handleDeleteFieldConfirm = async () => {
    if (!fieldToDelete || !form) return;

    try {
      setDeletingField(true);
      setDeleteFieldError(null);
      await myappApi.fields.delete(token, formId, fieldToDelete.id);

      if (form.is_published) {
        // Soft delete - mark as hidden in local state
        setForm((prev) =>
          prev
            ? {
                ...prev,
                fields: prev.fields.map((f) =>
                  f.id === fieldToDelete.id ? { ...f, is_hidden: true } : f
                ),
              }
            : null
        );
      } else {
        // Hard delete - remove from local state
        setForm((prev) =>
          prev
            ? {
                ...prev,
                fields: prev.fields.filter((f) => f.id !== fieldToDelete.id),
              }
            : null
        );
      }
      setDeleteFieldOpen(false);
      setFieldToDelete(null);
    } catch (err) {
      // Keep the dialog open and surface the structured error inside it.
      // FIELD_HAS_DEPENDENTS lists which fields block the hide/delete so
      // the user can go repoint or remove them; a flat page-level string
      // would lose that list and is easy to miss above the fold.
      setDeleteFieldError(err);
    } finally {
      setDeletingField(false);
    }
  };

  // Pairing (bridge) table gating — a bridge table needs a binding before publish.
  const isBridge = form?.table_type === "bridge";
  const hasBinding =
    isBridge && !!form?.bridge_binding && Object.keys(form.bridge_binding).length > 0;
  // Normalised binding for the read-only summary (view-structure surface).
  const bridgeSummaryConfig = useMemo(
    () => (form && hasBinding ? bridgeTableToConfig(form as unknown as Form) : null),
    [form, hasBinding],
  );

  const openPublishPreview = async () => {
    try {
      setLoadingDdl(true);
      const preview = await myappApi.tables.previewSchema(token, formId);
      setDdlPreview(preview.ddl);
      setPublishDialogOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to preview table structure");
    } finally {
      setLoadingDdl(false);
    }
  };

  // EDIT MODE for an existing binding: open the builder pre-filled from the
  // stored config. The data probe (any physical row, incl. soft-deleted —
  // matching the BE guard) decides whether the structural tier locks.
  const [bindingEditInitial, setBindingEditInitial] = useState<BridgeDemoConfig | null>(null);
  const [bindingHasData, setBindingHasData] = useState(false);
  const openBindingEdit = async () => {
    if (!bridgeSummaryConfig) return;
    let hasData = false;
    if (form?.is_published) {
      try {
        const page = await myappApi.records.list(token, formId, {
          limit: 1,
          includeDeleted: true,
          includeArchived: true,
        });
        hasData = (page.total ?? page.items?.length ?? 0) > 0;
      } catch {
        hasData = true; // unknown → assume data (lock rather than under-guard)
      }
    }
    setBindingHasData(hasData);
    setBindingEditInitial(bridgeSummaryConfig);
    setPublishAfterBinding(false);
    setBindingBuilderOpen(true);
  };

  const handlePublishClick = async () => {
    // A Pairing (bridge) table can't be published until it has a binding. Rather
    // than fire publish → 400 and dump the user on the Pairing tab, author the
    // binding inline right here and auto-continue to publish once it's saved.
    if (isBridge && !hasBinding) {
      if (!canAuthorBinding) {
        setError(
          "This Pairing table needs a binding before it can be published. Ask an organisation owner/admin to set it up.",
        );
        return;
      }
      setPublishAfterBinding(true);
      setBindingBuilderOpen(true);
      return;
    }
    await openPublishPreview();
  };

  const handleBindingSaved = (cfg: BridgeDemoConfig) => {
    // The builder already persisted the binding server-side (tables.update);
    // fold it into local form state so hasBinding flips immediately.
    setForm((prev) =>
      prev ? { ...prev, bridge_binding: cfg as unknown as Record<string, unknown> } : null,
    );
    setBindingBuilderOpen(false);
    setBindingEditInitial(null);
    if (publishAfterBinding) {
      setPublishAfterBinding(false);
      // Call openPublishPreview directly, NOT handlePublishClick — the latter's
      // hasBinding is a stale closure (form update above isn't visible yet).
      void openPublishPreview();
    }
  };

  const handlePublishConfirm = async () => {
    try {
      setPublishing(true);
      setPublishProgress(0);
      publishTimerRef.current = setInterval(() => {
        setPublishProgress((prev) => prev + Math.max(0.02, (85 - prev) * 0.015));
      }, 100);
      const published = await myappApi.tables.publish(token, formId);
      if (publishTimerRef.current) {
        clearInterval(publishTimerRef.current);
        publishTimerRef.current = null;
      }
      const { refresh_job_id: refreshJobId, ...publishedForm } = published;
      setForm((prev) => (prev ? { ...prev, ...publishedForm } : null));
      setPublishDialogOpen(false);
      // A virtual table's first load runs on the worker (dp #2213); hand its
      // job to the table page so it shows the load's progress.
      navigate({
        to: "/portal/myapp/$tableId",
        params: { tableId: String(formId) },
        search: refreshJobId ? { refreshJobId } : {},
      });
    } catch (err) {
      if (publishTimerRef.current) {
        clearInterval(publishTimerRef.current);
        publishTimerRef.current = null;
      }
      const msg = err instanceof Error ? err.message : "Failed to publish table";
      // Backstop: publish is client-side-gated so this rarely fires, but if a
      // bridge table reaches here without a binding, make it actionable (reopen
      // the inline builder) rather than dumping a dead full-screen error. Any
      // other publish error (e.g. an invalid-binding rejection) falls through to
      // setError.
      if (isBridge && /no binding configuration/i.test(msg) && canAuthorBinding) {
        setPublishDialogOpen(false);
        setPublishAfterBinding(true);
        setBindingBuilderOpen(true);
        return;
      }
      setError(msg);
    } finally {
      setPublishing(false);
      setPublishProgress(0);
    }
  };

  const handleValidateQuery = async () => {
    const query = (sourceQueryDraft ?? form?.source_query ?? "").trim();
    if (!query) {
      setQueryError("Query cannot be empty");
      return;
    }
    setQueryValidating(true);
    setQueryError(null);
    setDetectedColumns([]);
    setPreviewRows([]);
    try {
      const result = await myappApi.tables.validateQuery(token, query);
      if (result.is_valid) {
        setDetectedColumns(result.columns);
        setPreviewRows(result.preview_rows || []);
        setQueryError(null);
      } else {
        setQueryError(result.error || "Query validation failed");
      }
    } catch {
      setQueryError("Failed to validate query — check your connection");
    } finally {
      setQueryValidating(false);
    }
  };

  const handleSaveSourceQuery = async () => {
    if (!form || sourceQueryDraft === null) return;
    try {
      const updated = await myappApi.tables.update(token, formId, {
        source_query: sourceQueryDraft,
      });
      setForm((prev) => (prev ? { ...prev, ...updated } : null));
      setSourceQueryDraft(null);
      toast.success("Source query saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save source query");
    }
  };

  const handleRefreshModeChange = async (mode: "manual" | "auto_before_import" | "scheduled") => {
    if (!form) return;
    refreshModeChip.markSaving();
    try {
      const updated = await myappApi.tables.update(token, formId, {
        refresh_mode: mode,
      });
      setForm((prev) => (prev ? { ...prev, ...updated } : null));
      refreshModeChip.markSaved();
      toast.success("Refresh mode updated");
    } catch (err) {
      refreshModeChip.markError();
      setError(err instanceof Error ? err.message : "Failed to update refresh mode");
    }
  };

  const handleRefreshIntervalChange = async (minutes: number) => {
    if (!form) return;
    refreshModeChip.markSaving();
    try {
      const updated = await myappApi.tables.update(token, formId, {
        refresh_interval_minutes: minutes,
      });
      setForm((prev) => (prev ? { ...prev, ...updated } : null));
      refreshModeChip.markSaved();
    } catch (err) {
      refreshModeChip.markError();
      setError(err instanceof Error ? err.message : "Failed to update refresh interval");
    }
  };


  const handleRepairFields = async () => {
    try {
      setRepairing(true);
      setRepairMessage(null);
      setError(null);
      const repairedFields = await myappApi.fields.repair(token, formId);

      if (repairedFields.length === 0) {
        setRepairMessage("No fields needed repair.");
      } else {
        // Update local state to reflect repaired fields (is_required = false)
        setForm((prev) =>
          prev
            ? {
                ...prev,
                fields: prev.fields.map((f) => {
                  const repaired = repairedFields.find((rf) => rf.id === f.id);
                  return repaired ? { ...f, is_required: false } : f;
                }),
              }
            : null
        );
        const fieldNames = repairedFields.map((f) => f.name).join(", ");
        setRepairMessage(
          `Repaired ${repairedFields.length} field(s): ${fieldNames}. Columns are now nullable.`
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to repair fields");
    } finally {
      setRepairing(false);
    }
  };

  const handleBackfillInheritSources = async () => {
    if (!token) return;
    try {
      setInheritBackfilling(true);
      setInheritBackfillResult(null);
      setError(null);
      // Partial backfill: stamps every auto-resolvable FK, reports the rest.
      const res = await myappApi.fields.backfillInheritSources(
        token,
        formId,
        false,
      );
      setInheritBackfillResult(res);
      // Refetch so the now-stamped fields drop out of the card's count
      // (it's derived from form.fields' source_field_id).
      await fetchData();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to backfill inherited sources",
      );
    } finally {
      setInheritBackfilling(false);
    }
  };

  // WS1: back-fill existing rows from the parent after converting a field to
  // inherited. Reuses the republish endpoint with backfill_inherited=true,
  // whose cascade overwrites every inherited column on every existing row.
  const handleInheritBackfillConfirm = async () => {
    setInheritBackfillRunning(true);
    setError(null);
    try {
      const res = await myappApi.tables.republish(token, formId, true);
      // Pull the fresh row/field state so the editor reflects the backfill.
      const refreshed = await myappApi.tables.get(token, formId);
      setForm(refreshed);
      setInheritBackfillPromptOpen(false);
      if (res.needs_async) {
        setError(
          `Back-fill of ${res.record_count ?? "many"} rows has been queued and ` +
            "will complete shortly.",
        );
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to back-fill inherited values from the parent",
      );
    } finally {
      setInheritBackfillRunning(false);
    }
  };

  const handleLinkFkUpgradeConfirm = async () => {
    try {
      setLinkFkUpgrading(true);
      setLinkFkResult(null);
      setError(null);
      const result = await myappApi.fields.upgradeLinkFk(token, formId, false);
      setLinkFkConfirmOpen(false);
      const summary = describeLinkFkUpgradeResult(
        result.total_added,
        result.errors,
        result.total_populated,
        result.total_unresolved,
      );
      setLinkFkResult(summary);
      // A partial run (some fields failed to onboard) reads as a warning, not a
      // green success — the summary already names the skipped count.
      if (result.errors && result.errors.length > 0) {
        toast.warning(summary);
      } else {
        toast.success(summary);
      }
      // Re-read the count so the card flips to "all upgraded".
      await refreshLinkFkStatus();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to upgrade link table fields",
      );
    } finally {
      setLinkFkUpgrading(false);
    }
  };

  const handleFullRepairClick = async () => {
    // First, do a dry run to show preview of what will change
    try {
      setFullRepairing(true);
      setError(null);

      const preview = await myappApi.fields.fullRepair(token, formId, {
        repair_computed_columns: true,
        repair_missing_fk_columns: true,
        repair_int_to_nvarchar: true,
        repair_column_lengths: true,
        backfill_null_snapshots: true,
        dry_run: true,
      });

      setRepublishPreview({
        computed_columns_found: preview.computed_columns_found,
        missing_fk_columns_found: preview.missing_fk_columns_found,
        int_to_nvarchar_found: preview.int_to_nvarchar_found || 0,
        wrong_length_found: preview.wrong_length_found || 0,
        warnings: preview.warnings,
      });
      setRepublishPreviewOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to preview re-publish");
    } finally {
      setFullRepairing(false);
    }
  };

  const handleFullRepairConfirm = async () => {
    try {
      setFullRepairing(true);
      setFullRepairResult(null);
      setError(null);

      // Offload the repair to the worker (no 120s control-plane gateway
      // ceiling — large/composite tables used to 504 here), then poll for the
      // result. The dry-run PREVIEW above stays synchronous (fast, read-only).
      // Logic lives in runAsyncFullRepair so it's unit-testable.
      const result = await runAsyncFullRepair({
        enqueue: () => myappApi.fields.fullRepairAsync(token, formId),
        poll: () => myappApi.fields.fullRepairAsyncStatus(token, formId),
      });

      setRepublishPreviewOpen(false);
      setFullRepairResult({
        table_created: result.table_created,
        computed_columns_fixed: result.computed_columns_fixed,
        fk_columns_added: result.fk_columns_added,
        lookup_records_updated: result.lookup_records_updated,
        int_to_nvarchar_fixed: result.int_to_nvarchar_fixed,
        lengths_fixed: result.lengths_fixed,
        temp_columns_fixed: result.temp_columns_fixed,
        temp_columns_dropped: result.temp_columns_dropped,
        child_tables_published: result.child_tables_published,
        child_tables_failed: result.child_tables_failed,
        warnings: result.warnings,
        errors: result.errors,
      });

      // Show toast with summary
      const totalFixes =
        result.computed_columns_fixed +
        result.fk_columns_added +
        (result.lookup_records_updated || 0) +
        (result.int_to_nvarchar_fixed || 0) +
        (result.lengths_fixed || 0) +
        (result.temp_columns_fixed || 0) +
        (result.temp_columns_dropped || 0) +
        (result.child_tables_published || 0);

      if (result.table_created) {
        toast.success("Table created successfully");
      } else if (totalFixes > 0 || result.warnings.length > 0) {
        toast.success(`Re-publish complete: ${totalFixes} fix(es) applied`);
      } else {
        toast.info("No changes needed - structure is up to date");
      }

      // Clear the "needs re-publish" banner and change history
      setStructureChanged(false);
      setStructureChanges([]);

      // Refresh child tables if any were published to update their status
      if ((result.child_tables_published || 0) > 0) {
        const updatedChildren = await myappApi.tables.getChildren(token, formId);
        setChildTables(updatedChildren);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to re-publish structure");
    } finally {
      setFullRepairing(false);
    }
  };

  const getFieldTypeName = (fieldTypeId: string) => {
    const ft = fieldTypes.find((t) => t.id === fieldTypeId);
    return ft?.name || "Unknown";
  };

  // Token-guarded displayFields load shared by the linked-table pick and the
  // re-point dismiss restore (see displayFieldsLoadRef above).
  const loadDisplayFields = (linkedFormId: string | null) => {
    const token_ = ++displayFieldsLoadRef.current;
    if (!linkedFormId) {
      setDisplayFields([]);
      setLoadingDisplayFields(false);
      return;
    }
    setLoadingDisplayFields(true);
    myappApi.lookups
      .listFormDisplayFields(token, linkedFormId)
      .then((fields) => {
        if (displayFieldsLoadRef.current === token_) setDisplayFields(fields);
      })
      .catch(() => {
        if (displayFieldsLoadRef.current === token_) setDisplayFields([]);
      })
      .finally(() => {
        if (displayFieldsLoadRef.current === token_) setLoadingDisplayFields(false);
      });
  };

  const handleLinkedFormChange = async (linkedFormId: string | null) => {
    // Picking a (different) linked table re-arms the re-point confirmation so a
    // prior approval can't carry over to a new target.
    pendingRepointApprovedRef.current = false;
    setFieldData((prev) => ({
      ...prev,
      linked_table_id: linkedFormId,
      linked_display_field_id: null, // Reset display field when form changes
    }));
    loadDisplayFields(linkedFormId);
  };

  // Dismiss the re-point confirmation without applying it: re-arm the gate and
  // restore the field's original linked table so a dismissed dialog never
  // silently re-points on a later save. Single source for both dismiss paths
  // (the custom Cancel button sets open=false directly and so bypasses Radix's
  // onOpenChange — both must run identical cleanup).
  const dismissRepointDialog = () => {
    setRepointDialogOpen(false);
    setRepointConfirmText("");
    pendingRepointApprovedRef.current = false;
    if (editingField) {
      setFieldData((prev) => ({
        ...prev,
        linked_table_id: editingField.linked_table_id ?? null,
        linked_display_field_id: editingField.linked_display_field_id ?? null,
      }));
      // Also reload the display-field options for the RESTORED table —
      // handleLinkedFormChange replaced them with the abandoned target's
      // fields, and the picker stays editable after dismiss (isolated link),
      // so leaving them would offer the wrong table's fields. Token-guarded so
      // a slow abandoned-target fetch can't land after this one.
      loadDisplayFields(editingField.linked_table_id ?? null);
    }
  };

  // Handler for when source link field changes (for lookup fields)
  const handleSourceLinkFieldChange = async (sourceLinkFieldId: string | null) => {
    setFieldData((prev) => ({
      ...prev,
      source_link_field_id: sourceLinkFieldId,
      lookup_field_id: null, // Reset lookup field when source changes
    }));

    if (sourceLinkFieldId) {
      // Find the source link field and get its linked table
      const sourceField = form?.fields.find(f => f.id === sourceLinkFieldId);
      if (sourceField?.linked_table_id) {
        try {
          setLoadingLookupFields(true);
          // Include link fields so we can walk through relationships
          // e.g., Partner -> Country (where Country is a link field on Partner)
          const fields = await myappApi.lookups.listFormDisplayFields(token, sourceField.linked_table_id, true);
          setLookupTargetFields(fields);
        } catch {
          setLookupTargetFields([]);
        } finally {
          setLoadingLookupFields(false);
        }
      }
    } else {
      setLookupTargetFields([]);
    }
  };

  const selectedFieldType = fieldTypes.find(
    (t) => t.id === fieldData.field_type_id
  );

  // Check if the selected field type supports max_length (text types)
  const supportsMaxLength = selectedFieldType?.code
    ? ["text", "longtext", "email"].includes(selectedFieldType.code)
    : false;

  // Check if the selected field type is a date type
  const isDateType = selectedFieldType?.code
    ? ["date", "datetime"].includes(selectedFieldType.code)
    : false;

  // Check if the selected field type is a link type
  const isLinkType = selectedFieldType?.code === "link";

  // Check if the selected field type is a lookup type
  const isLookupType = selectedFieldType?.code === "lookup";

  // True when editing a published field and the picked type differs from its
  // current type — i.e. an in-place STRUCTURAL conversion (WS2). Drives the
  // link/lookup config panels to render their EDITABLE form (pick the target)
  // rather than the read-only "already configured" form.
  const isConverting =
    Boolean(editingField) &&
    Boolean(form?.is_published) &&
    selectedFieldType?.code !== editingField?.field_type?.code;
  const isConvertingToLookup = isConverting && isLookupType;

  // An isolated published Link field can be re-pointed to a different table or
  // detached to a standard / options type; the old FK column is left dormant
  // and existing rows are not re-linked (no backfill). "Isolated" = nothing
  // references it (no lookup source, rollup, inherit, …) — mirrors the backend
  // gate. While dependents are still loading (null) we treat it as NOT isolated.
  const editingIsPublishedLink =
    Boolean(editingField) &&
    Boolean(form?.is_published) &&
    editingField?.field_type?.code === "link";
  // Deliberately stricter than the delete flow's `has_blocking`: ANY dependent
  // (blocking or not) keeps the link locked here — over-locking is safe,
  // wrongly unlocking is not.
  const editingLinkIsIsolated =
    editingIsPublishedLink &&
    editLinkDependents !== null &&
    editLinkDependents.dependents.length === 0;
  const editingLinkDependentNames =
    editLinkDependents?.dependents
      .map((d) => `'${d.dependent_field_name}'`)
      .join(", ") ?? "";

  // Check if the selected field type is a calculation type
  const isCalculationType = selectedFieldType?.code === "calculation";

  // Check if the selected field type is a concatenation type
  const isConcatenationType = selectedFieldType?.code === "concatenation";

  // Check if the selected field type is a rollup type
  const isRollupType = selectedFieldType?.code === "rollup";

  // A Connector Files count is written only by Refresh Data: never
  // Required, Unique or defaulted (dp refuses all three — BE = FE).
  const isConnectorFilesType = selectedFieldType?.code === "connector_files";

  // Whether this field can participate in state-machine rules
  // (Read-only-from + state-conditional Required-from). Shares the
  // canFieldHaveStateRules combined helper with SortableFieldItem so the
  // dialog and the field card cannot drift. Inherited fields and
  // autonumber targets are excluded: their value comes from the parent
  // record or is system-assigned, so this form has no input to gate.
  //
  // is_auto_numbered is read from the SAVED `editingField` snapshot
  // (not the draft `fieldData`) because the autonumber-target flag is
  // toggled by table_service when the table-level autonumber config
  // changes, not by this dialog. In CREATE mode editingField is null,
  // which correctly resolves to isAutoNumbered=false (a newly-created
  // field cannot be the autonumber target until it is explicitly
  // designated in the table's autonumber section).
  const canHaveStateRules =
    tableStates.length > 0 &&
    canFieldHaveStateRules({
      typeCode: selectedFieldType?.code,
      isInherited: fieldData.is_inherited === true,
      isAutoNumbered: editingField?.is_auto_numbered === true,
    });

  // Load child tables when rollup type is selected
  useEffect(() => {
    if (!isRollupType || !form?.is_header_table) {
      setRollupChildTables([]);
      return;
    }
    myappApi.tables
      .getChildren(token, formId)
      .then((children: Form[]) => {
        setRollupChildTables(
          children.map((child) => ({ id: child.id, name: child.name, fields: [] }))
        );
      })
      .catch(() => setRollupChildTables([]));

  }, [isRollupType, form?.is_header_table, token, formId]);

  // Load fields for the selected child table
  useEffect(() => {
    if (!selectedRollupChildTableId) return;
    myappApi.fields
      .list(token, selectedRollupChildTableId)
      .then((fields: FormField[]) => {
        setRollupChildTables((prev) =>
          prev.map((ct) =>
            ct.id === selectedRollupChildTableId
              ? {
                  ...ct,
                  fields: fields
                    .filter((f) => !f.is_hidden && f.field_type)
                    .map((f) => ({
                      id: f.id,
                      name: f.name,
                      code: f.field_type?.code || "",
                    })),
                }
              : ct
          )
        );
      })
      .catch(() => {});

  }, [selectedRollupChildTableId, token]);

  // The table's connector-attachments binding (`null` = none): the Connector
  // Files field type is offered only when it exists.
  const { data: connectorBinding = null } = useConnectorAttachmentBinding(formId, {
    enabled: form?.table_type === "virtual" && !!form?.is_published,
  });

  // Group field types: special types (link, lookup, calculation, concatenation, sortorder, rollup) at top
  const specialTypeCodes = new Set(["link", "lookup", "calculation", "concatenation", "sortorder", "rollup", "options", "connector_files"]);
  // Connector Files: only on a published virtual table bound to connector
  // files, and only one per table (dp refuses any other with a 400).
  const canAddFilesField =
    form?.table_type === "virtual" &&
    connectorBinding !== null &&
    !form.fields.some((f) => f.field_type?.code === "connector_files");
  const specialFieldTypes = fieldTypes.filter(
    (ft) =>
      specialTypeCodes.has(ft.code) &&
      (ft.code !== "rollup" || form?.is_header_table) &&
      (ft.code !== "connector_files" || canAddFilesField)
  );
  const dataFieldTypes = fieldTypes.filter((ft) => !specialTypeCodes.has(ft.code));

  // Virtual "Inherit from Parent" option — not a real FieldType row in the DB,
  // just a UI sentinel that reroutes the Add Field dialog to the inherit flow.
  // Only offered on line tables (parent_table_id set) with inheritable parent fields.
  const INHERIT_FROM_PARENT_ID = "__inherit_from_parent__";
  const isInheritMode = fieldData.field_type_id === INHERIT_FROM_PARENT_ID;

  // Check if this is a text type that can be switched (text <-> longtext)
  const _isTextType = selectedFieldType?.code
    ? ["text", "longtext"].includes(selectedFieldType.code)
    : false;

  // Get link fields from this form (for lookup field selection)
  const linkFields = form?.fields.filter(f => f.field_type?.code === "link") || [];

  // Get numeric, lookup, rollup AND calculation fields from this form (for
  // calculation field references). Calculation IS now included: calc-on-calc is
  // supported via dependency-ordered evaluation with cycle detection
  // (evaluateAllCalculations on the FE, apply_calculations_to_row on the BE).
  // The field being edited is excluded so a calc can't reference itself.
  //
  // This numeric-only list is still correct for legacy "basic arithmetic"
  // formulas (+, -, *, / only) — the v1 evaluator below (Test Calculation)
  // is numeric-input-only and stays scoped to this set.
  const numericFields = form?.fields.filter(
    f =>
      f.field_type &&
      ["int", "bigint", "decimal", "money", "percentage", "lookup", "rollup", "calculation"].includes(f.field_type.code) &&
      f.id !== editingField?.id
  ) || [];

  // Once a Result type is declared, the formula compiles as full T-SQL
  // (see tsql_calc.py on the backend), which places no field-type
  // restriction on what compiles — so this list is a curated SUBSET of
  // what would compile, not everything that would. Excluded:
  //   - "link": referencing the raw FK id is rarely useful; a "lookup"
  //     field already surfaces the linked record's data as a scalar.
  //   - "concatenation" / "attachment" / "signature" / "sortorder":
  //     these are written by paths that run AFTER or AROUND calc
  //     evaluation (concatenation is applied post-calc so a calc can feed
  //     it; attachment/signature counts and sortorder are written by their
  //     own update paths, not the record write that runs calcs) — a v2
  //     calc referencing one compiles cleanly but reads a stale or NULL
  //     value, not a compile error. See data_service.py's calc-before-
  //     concat ordering and the _apply_concatenation_values comment.
  const EXCLUDED_CALC_REF_TYPES = new Set([
    "link", "concatenation", "attachment", "signature", "sortorder",
  ]);
  const calcAvailableFields = calcResultTypeId
    ? form?.fields.filter(
        f => f.field_type && !EXCLUDED_CALC_REF_TYPES.has(f.field_type.code) && f.id !== editingField?.id
      ) || []
    : numericFields;

  // Declarable result types for a v2 (T-SQL) calculation field — mirrors
  // tsql_calc._ASSIGNABLE's key set on the backend, curated to the types a
  // designer is realistically choosing between (bit/guid/sortorder/email
  // omitted as edge cases the backend still accepts if set another way).
  const calcResultTypeOptions = fieldTypes.filter((t) =>
    ["int", "decimal", "money", "text", "longtext", "date", "datetime", "time", "bit"].includes(t.code)
  );

  // Get text field types for the switcher
  const _textFieldTypes = fieldTypes.filter((t) =>
    ["text", "longtext"].includes(t.code)
  );

  // Per-parent "no prefix" ↔ unique-target conflict (mirrors the
  // lara-data-plane rule): with scope per_parent and NO prefix source
  // field, the target receives the bare per-parent sequence (00001,
  // 00002, …) which repeats across parents — so the target field must
  // not be unique. The backend rejects the combination; these flags let
  // the UI block it up front (see AUTO_NUMBER_UNIQUE_CONFLICT_MESSAGE).
  const autoNumberTargetIsUnique =
    form?.fields?.some(
      (f) => f.id === form.auto_number_target_field_id && f.is_unique,
    ) === true;
  // True when the field open in the dialog is the per-parent,
  // prefix-less auto-number target — enabling Unique on it would create
  // the invalid combination, so the toggle is disabled with an
  // explanation. Requires auto-numbering to be ENABLED: the backend
  // honours "disable auto-numbering" as an escape route, so a dormant
  // config must not lock the Unique switch.
  const isPrefixlessAutoNumberTarget =
    Boolean(editingField) &&
    editingField?.id === form?.auto_number_target_field_id &&
    form?.auto_number_enabled === true &&
    form?.auto_number_scope === "per_parent" &&
    !form?.auto_number_source_field_id;

  // Conversion rules on published tables:
  // - One-way: cannot convert OUT of a structural type (link/lookup/sortorder).
  // - Link & Lookup ARE now valid conversion TARGETS (WS2 in-place
  //   conversion); the BE validates the specific source→target via the
  //   STRUCTURAL transition matrix and returns a clear error for unsupported
  //   pairs (e.g. date→link). Only sortorder stays blocked as a target.
  const blockedAsCurrentType = new Set(["link", "lookup", "sortorder"]);
  const blockedAsTarget = new Set(["sortorder"]);

  // Targets an ISOLATED published link may be detached to live in
  // `@/lib/myapp/link-detach` (LINK_DETACH_TARGET_CODES) so the single FE source
  // mirrors the backend STRUCTURAL_DETACH matrix. "link" itself stays available
  // so the user can re-point to a different table instead of converting away.
  const getFieldTypesForPublishedEdit = () => {
    if (!editingField || !form?.is_published) return { special: specialFieldTypes, data: dataFieldTypes };
    const currentCode = editingField.field_type?.code;
    if (currentCode && blockedAsCurrentType.has(currentCode)) {
      // An isolated link can be re-pointed (keep "link") or detached to a
      // standard / options type. Anything else referencing it keeps the link
      // locked to itself.
      if (currentCode === "link" && editingLinkIsIsolated) {
        const allowed = fieldTypes.filter(
          (ft) => ft.code === "link" || LINK_DETACH_TARGET_CODES.has(ft.code),
        );
        return {
          special: allowed.filter((ft) => specialTypeCodes.has(ft.code)),
          data: allowed.filter((ft) => !specialTypeCodes.has(ft.code)),
        };
      }
      // Current type is structural — only show itself (no convert-out).
      const current = fieldTypes.filter((ft) => ft.code === currentCode);
      return { special: current.filter((ft) => specialTypeCodes.has(ft.code)), data: current.filter((ft) => !specialTypeCodes.has(ft.code)) };
    }
    // Offer all targets except sortorder (link/lookup included as conversions).
    const allowed = fieldTypes.filter((ft) => !blockedAsTarget.has(ft.code));
    return {
      special: allowed.filter((ft) => specialTypeCodes.has(ft.code)),
      data: allowed.filter((ft) => !specialTypeCodes.has(ft.code)),
    };
  };
  const publishedEditTypes = getFieldTypesForPublishedEdit();

  // Check if there are hidden fields on a published form (may need repair)
  const hasHiddenFields =
    form?.is_published && form?.fields.some((f) => f.is_hidden);

  // Wave 0b: inherited fields not yet linked to their parent source by FK.
  // These still resolve via name heuristics (fragile on a rename), so offer
  // a one-click backfill. Hidden fields are excluded to match the backfill's
  // own scope (it skips IsHidden rows).
  const inheritFieldsNeedingBackfill = (form?.fields ?? []).filter(
    (f) => f.is_inherited && !f.is_hidden && !f.source_field_id,
  );

  if (loading || permsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading table...</div>
      </div>
    );
  }

  if (permissionDenied) {
    return (
      <PermissionDenied
        message="You don't have permission to edit this table's structure."
        backTo={`/portal/myapp/${tableId}`}
        backLabel="Back to Table"
      />
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 px-4">
        <div className="text-destructive text-center max-w-2xl break-words whitespace-pre-wrap">{error}</div>
        <Button onClick={fetchData}>Retry</Button>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-muted-foreground">Form not found</div>
        <Link to="/portal/myapp/tables" search={{}}>
          <Button>Back to Tables</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-3 mb-6 md:flex-row md:items-center md:gap-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {form.parent_table_id ? (
            // Line table of an Advanced header: back goes up to the parent's
            // edit page (header fields + child line tables), not to this
            // line table's data view - which for a draft just says
            // "not published yet" and offers no way back to the header.
            <Link
              to="/portal/myapp/$tableId/edit"
              params={{ tableId: String(form.parent_table_id) }}
              search={{}}
            >
              <Button variant="ghost" size="icon" className="shrink-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
          ) : (
            <Link
              to="/portal/myapp/$tableId"
              params={{ tableId: String(formId) }}
              search={{}}
            >
              <Button variant="ghost" size="icon" className="shrink-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <h1 className="text-2xl font-bold">{form.name}</h1>
              {form.is_published ? (
                <Badge variant="info">Published</Badge>
              ) : (
                <Badge variant="secondary">Draft</Badge>
              )}
            </div>
            {form.description && (
              <p className="text-muted-foreground">{form.description}</p>
            )}
          </div>
        </div>
        {canStructure && (
          <div className="flex gap-2">
            {/* Add Record opens the data-entry form in a new tab so the
                builder can preview what an end-user submission looks like
                without losing the editor context. asChild + <a> with
                target="_blank" preserves the editor session in the
                original tab; window.open shortcuts would lose it on a
                blocked-popup browser config.

                Hidden entirely for:
                - Virtual tables: data is sourced from a SQL view and
                  refreshed via Refresh now; direct user submission has
                  no write path and would lead to a broken form.
                - Child line tables (parent_table_id set): users add
                  lines via the parent's composite form (HeaderLineForm)
                  which handles autonumber-per-parent + inherited-field
                  cascade; the standalone child submit route bypasses
                  both and is not the canonical entry path.

                Note: this uses `!form.parent_table_id` rather than
                the sibling `!form.is_header_table` convention used in
                index.tsx record-write buttons. The two are deliberately
                different here: index.tsx's gates hide record buttons
                on COMPOSITE PARENTS (because those use a different
                entry path on the data page); this header's Add Record
                SHOULD show on composite parents because their submit
                route correctly dispatches to HeaderLineForm. So the
                discriminator is "is a child line table" (rules out
                child tables only) not "is a composite parent" (rules
                out the very tables we want to support).

                For ordinary header/simple/advanced tables, gated on
                is_published because an unpublished table has no SQL
                table to write to. The disabled-with-tooltip variant
                explains the publish prerequisite so the builder
                discovers it. */}
            {!form.parent_table_id && form.table_type !== "virtual" && (
              <>
                {form.is_published ? (
                  // Brand-blue default variant matches the sibling
                  // Add Record button on the data-grid toolbar
                  // (index.tsx ~line 1057, also default variant). The
                  // other header actions on this page use outline;
                  // Add Record is the primary CTA so it gets the
                  // prominent treatment.
                  <Button asChild>
                    <a
                      href={`/portal/myapp/${form.id}/submit`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Record
                    </a>
                  </Button>
                ) : (
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {/* Wrap disabled button in a span so the
                            tooltip still fires on hover (disabled
                            buttons swallow pointer events in some
                            browsers). aria-label on the disabled
                            button carries the same context as the
                            visual tooltip so screen-reader users hear
                            the publish-first message without depending
                            on the Radix tooltip's hover-only
                            behaviour. */}
                        <span className="inline-flex">
                          {/* Default variant matches the published case
                              + the data-grid toolbar pattern. Disabled
                              state gives the muted-blue treatment which
                              still reads as "the same button,
                              unavailable right now". */}
                          <Button
                            disabled
                            aria-label="Add Record. Publish the table before adding records."
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add Record
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs">
                        Publish the table before adding records.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </>
            )}
            {/* Edit Details is available pre- and post-publish. The dialog
                itself gracefully handles the published case: only Name +
                Description are editable, with helper text explaining the
                physical TableName stays frozen. Backend allows the rename
                via the OriginalName + display-only path in table_service. */}
            <Button variant="outline" onClick={handleEditFormClick}>
              <Save className="h-4 w-4 mr-2" />
              Edit Details
            </Button>
            {/* Publish only appears pre-publish, and only on header/standalone
                tables — line tables cascade-publish with their header. */}
            {!form.is_published && !form.parent_table_id && (
              <Button
                onClick={handlePublishClick}
                // Hold off on a bridge table until RBAC resolves — the publish
                // path branches on `canAuthorBinding` (bypassesGroups), which is
                // false until then, so an early click would falsely deny an admin.
                disabled={form.fields.length === 0 || loadingDdl || (isBridge && rbacResolving)}
              >
                <Rocket className="h-4 w-4 mr-2" />
                {loadingDdl ? "Loading..." : "Publish"}
              </Button>
            )}
          </div>
        )}
      </div>
      {/* Published Warning */}
      {form.is_published && (
        <div className="flex items-center gap-2 p-4 mb-6 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>
            This form has been published. The database table{" "}
            <code className="bg-yellow-100 px-1 rounded">{form.table_name}</code>{" "}
            has been created. You can still edit field names and descriptions, but
            structural properties (type, required, unique, max length) are locked.
          </span>
        </div>
      )}
      {/* Needs Re-publish Banner - hides the action button on line tables;
          the structure changes are still listed so admins know what's
          pending, but the action lives on the parent (cascade fixes the
          whole family). */}
      {form.is_published && (structureChanged || form.needs_republish) && (
        <div className="p-4 mb-6 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span className="flex-1">
              {form.parent_table_id
                ? "Table structure has been modified. Re-publish from the parent table to apply changes; the cascade covers every line and version table."
                : "Table structure has been modified. Re-publish when you're ready."}
            </span>
            {/* Button visible only on parent / standalone tables. Line
                tables route through the parent so the cascade runs
                once and covers every sibling. */}
            {!form.parent_table_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleFullRepairClick}
                disabled={fullRepairing}
                className="border-amber-300 text-amber-800 hover:bg-amber-100"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {fullRepairing ? "Checking..." : "Re-publish Structure"}
              </Button>
            )}
          </div>
          {structureChanges.length > 0 && (
            <ul className="text-sm text-amber-700 ml-7 list-disc list-inside">
              {structureChanges.map((c, i) => (
                <li key={i}>
                  <strong>{c.fieldName}</strong>: {c.fromType} → {c.toType}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {/* Repair Message */}
      {repairMessage && (
        <div className="flex items-center gap-2 p-4 mb-6 rounded-lg bg-green-50 border border-green-200 text-green-800">
          <Wrench className="h-5 w-5 flex-shrink-0" />
          <span>{repairMessage}</span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={() => setRepairMessage(null)}
          >
            Dismiss
          </Button>
        </div>
      )}
      {/*
        Line-table guidance card. Shown ONLY on line tables (parent_table_id
        set) when we'd otherwise show the Repair Fields or Re-publish
        Structure cards below. Tells the admin to go run the action on
        the parent. The BE's cascade in /repair/full handles the whole
        header → Lines → Versions family in one click, and running it on
        a line table directly hits SQL paths (e.g. the inherited-value
        backfill UPDATE) that assume an aligned parent and 500 when the
        parent has stale link metadata that hasn't been cleared yet
        (Anel's repro on 2026-05-27 via Re-publish Structure on the
        CsaPaperRegister2026Lines table). Hiding the buttons on children
        prevents that whole class of repro.
      */}
      {form.parent_table_id && form.is_published && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Wrench className="mt-0.5 h-5 w-5 text-blue-700 shrink-0" />
              <div className="flex-1">
                <h3 className="font-medium text-blue-800">
                  Maintenance is managed from the parent table
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  This is a line table. To repair the structure or re-publish, open
                  the parent table and run the action there. The cascade covers
                  every line and version table in one go. Running maintenance on
                  a line table directly can fail if the parent is out of sync.
                </p>
                <div className="mt-3">
                  <Link
                    to="/portal/myapp/$tableId/edit"
                    params={{ tableId: String(form.parent_table_id) }}
                    search={{}}
                  >
                    <Button
                      variant="outline"
                      className="border-blue-300 text-blue-800 hover:bg-blue-100"
                    >
                      Open parent table
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Backfill inherited sources - Wave 0b. Shown on PUBLISHED tables
          (typically line tables) that have inherited fields not yet linked
          to their parent source by FK. On draft tables the field-type
          dropdown is editable so the user can set the source directly;
          the backfill affordance is for the published case where the
          picker is read-only. One click stamps every auto-resolvable FK;
          anything it can't match is listed for the user to fix with the
          source picker. */}
      {form.is_published && inheritFieldsNeedingBackfill.length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-amber-800">
                  Inherited fields need their source link
                </h3>
                <p className="text-sm text-amber-700">
                  {inheritFieldsNeedingBackfill.length} inherited field
                  {inheritFieldsNeedingBackfill.length === 1 ? "" : "s"} still
                  resolve to the parent by name, which breaks if the parent is
                  renamed. Backfill links each one to its source by ID.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleBackfillInheritSources}
                disabled={inheritBackfilling}
                className="border-amber-300 text-amber-800 hover:bg-amber-100"
              >
                <Wrench className="h-4 w-4 mr-2" />
                {inheritBackfilling
                  ? "Backfilling..."
                  : `Backfill ${inheritFieldsNeedingBackfill.length} source link${
                      inheritFieldsNeedingBackfill.length === 1 ? "" : "s"
                    }`}
              </Button>
            </div>

            {/* Result: list any that couldn't be auto-matched so the user
                can set their source with the picker (jump + highlight). */}
            {inheritBackfillResult &&
              inheritBackfillResult.unresolvable.length > 0 && (
                <div className="mt-4 pt-4 border-t border-amber-200">
                  <p className="text-sm text-amber-800">
                    Linked {inheritBackfillResult.populated} field
                    {inheritBackfillResult.populated === 1 ? "" : "s"}.{" "}
                    {inheritBackfillResult.unresolvable.length} could not be
                    matched automatically. Open each and pick its source
                    field:
                  </p>
                  <ul className="mt-1 list-disc pl-5 text-sm">
                    {inheritBackfillResult.unresolvable.map((u) => (
                      <li key={u.field_id}>
                        <button
                          type="button"
                          className="text-amber-900 underline hover:no-underline"
                          onClick={() => {
                            // Open the field editor directly so the user
                            // lands on the source picker. Falls back to a
                            // jump+highlight if the field can't be found
                            // (shouldn't happen: form.fields is the very
                            // source the unresolvable list came from).
                            const f = form.fields.find(
                              (x) => x.id === u.field_id,
                            );
                            if (f) {
                              void handleEditFieldClick(f);
                            } else {
                              jumpToMatrixRow(u.field_id, "");
                            }
                          }}
                        >
                          {u.field_name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
          </CardContent>
        </Card>
      )}
      {/* Repair Hidden Fields - shown on published parent / standalone
          tables with hidden fields. Hidden on line tables because the
          parent's cascade handles them. */}
      {hasHiddenFields && !form.parent_table_id && (
        <Card className="mb-6 border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-orange-800">Hidden Fields Detected</h3>
                <p className="text-sm text-orange-700">
                  This form has hidden fields. If you&apos;re having issues creating records,
                  use the repair function to ensure hidden columns are nullable.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleRepairFields}
                disabled={repairing}
                className="border-orange-300 text-orange-800 hover:bg-orange-100"
              >
                <Wrench className="h-4 w-4 mr-2" />
                {repairing ? "Repairing..." : "Repair Fields"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Re-publish Structure - shown on published parent / standalone
          tables. Hidden on line tables because the parent's cascade
          handles them (and running this directly on a child can fail
          if the parent hasn't been repaired first). */}
      {form.is_published && !form.parent_table_id && (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-blue-800">Re-publish Structure</h3>
                <p className="text-sm text-blue-700">
                  Safely update table structure to match current field definitions. All existing data is preserved.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={handleFullRepairClick}
                disabled={fullRepairing}
                className="border-blue-300 text-blue-800 hover:bg-blue-100"
              >
                <Wrench className="h-4 w-4 mr-2" />
                {fullRepairing ? "Checking..." : "Re-publish Structure"}
              </Button>
            </div>

            {/* Re-publish results */}
            {fullRepairResult && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Re-publish Results:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  {fullRepairResult.computed_columns_fixed > 0 && (
                    <li>• {fullRepairResult.computed_columns_fixed} computed column(s) converted to physical</li>
                  )}
                  {fullRepairResult.fk_columns_added > 0 && (
                    <li>• {fullRepairResult.fk_columns_added} FK column(s) added for link fields</li>
                  )}
                  {(fullRepairResult.int_to_nvarchar_fixed ?? 0) > 0 && (
                    <li>• {fullRepairResult.int_to_nvarchar_fixed} link column(s) migrated to correct type</li>
                  )}
                  {(fullRepairResult.lengths_fixed ?? 0) > 0 && (
                    <li>• {fullRepairResult.lengths_fixed} column length(s) corrected</li>
                  )}
                  {fullRepairResult.lookup_records_updated > 0 && (
                    <li>• {fullRepairResult.lookup_records_updated} record(s) backfilled with lookup values</li>
                  )}
                  {(fullRepairResult.child_tables_published ?? 0) > 0 && (
                    <li className="text-green-700">• {fullRepairResult.child_tables_published} line table(s) published</li>
                  )}
                  {fullRepairResult.computed_columns_fixed === 0 &&
                    fullRepairResult.fk_columns_added === 0 &&
                    (fullRepairResult.int_to_nvarchar_fixed ?? 0) === 0 &&
                    (fullRepairResult.lengths_fixed ?? 0) === 0 &&
                    fullRepairResult.lookup_records_updated === 0 &&
                    (fullRepairResult.child_tables_published ?? 0) === 0 && (
                      <li className="text-green-700">✓ No changes needed - structure is up to date</li>
                    )}
                </ul>
                {fullRepairResult.warnings.length > 0 && (
                  <div className="mt-2">
                    <h5 className="text-sm font-medium text-amber-700">Warnings:</h5>
                    <ul className="text-sm text-amber-600">
                      {fullRepairResult.warnings.map((w, i) => (
                        <li key={i}>• {w}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {fullRepairResult.errors.length > 0 && (
                  <div className="mt-2">
                    <h5 className="text-sm font-medium text-red-700">Errors:</h5>
                    <ul className="text-sm text-red-600">
                      {fullRepairResult.errors.map((e, i) => (
                        <li key={i}>• {e}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(fullRepairResult.child_tables_failed?.length ?? 0) > 0 && (
                  <div className="mt-2">
                    <h5 className="text-sm font-medium text-red-700">Line Table Publish Failures:</h5>
                    <ul className="text-sm text-red-600">
                      {fullRepairResult.child_tables_failed?.map((failure, i) => (
                        <li key={i}>• {failure.name}: {failure.error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {/* Upgrade Link Table Fields — move link fields onto the per-field SysFk
          scheme so colliding multi-link fields stop sharing one FK column.
          Shown only on published parent/standalone tables (the cascade covers
          line/version tables) and only when there is something to upgrade or a
          recent result to report. Additive, idempotent and safe. */}
      {form.is_published &&
        !form.parent_table_id &&
        (linkFkStatus.canUpgrade || linkFkResult) && (
          <Card className="mb-6 border-indigo-200 bg-indigo-50">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-medium text-indigo-800">
                    Upgrade Link Table Fields
                  </h3>
                  <p className="text-sm text-indigo-700">{linkFkStatus.message}</p>
                  {linkFkResult && (
                    <p className="mt-2 text-sm font-medium text-green-700">
                      ✓ {linkFkResult}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setLinkFkConfirmOpen(true)}
                  disabled={!linkFkStatus.canUpgrade || linkFkUpgrading}
                  className="border-indigo-300 text-indigo-800 hover:bg-indigo-100 shrink-0"
                >
                  <Wrench className="h-4 w-4 mr-2" />
                  {linkFkUpgrading ? "Upgrading..." : linkFkStatus.buttonLabel}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      <AlertDialog open={linkFkConfirmOpen} onOpenChange={setLinkFkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Upgrade link table fields?</AlertDialogTitle>
            <AlertDialogDescription>
              This adds a dedicated foreign-key column (SysFk…) for each link
              field on this table and its line/version tables, then routes future
              saves through it. It also reconstructs each link's FK for existing
              rows from its stored display value — only where that value matches a
              single linked record; rows whose value isn't unique (or whose
              display field has since changed) are left unlinked. Your records'
              displayed values are never changed. You can re-run this at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={linkFkUpgrading}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleLinkFkUpgradeConfirm();
              }}
              disabled={linkFkUpgrading}
            >
              {linkFkUpgrading ? "Upgrading..." : "Upgrade"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Table Info */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Table Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Table Name:</span>
              <code className="ml-2 bg-muted px-2 py-1 rounded">
                {form.table_name}
              </code>
            </div>
            <div>
              <span className="text-muted-foreground">Primary Key:</span>
              <code className="ml-2 bg-muted px-2 py-1 rounded">
                {form.primary_key_column}
              </code>
            </div>
          </div>

          {/* Lock Table */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <div>
                  <Label className="text-sm font-medium">Lock Table</Label>
                  <p className="text-xs text-muted-foreground">
                    Prevent imports, edits, and deletes on data rows
                  </p>
                </div>
              </div>
              <Switch
                checked={form.is_locked}
                onCheckedChange={async (checked) => {
                  // Optimistic toggle — the Switch flips instantly. If the
                  // PUT fails we roll back. Avoids the stall where the
                  // visual state lags the server response.
                  const previous = form.is_locked;
                  setForm((prev) => (prev ? { ...prev, is_locked: checked } : null));
                  try {
                    await myappApi.tables.update(token, formId, { is_locked: checked });
                    toast.success(checked ? "Table locked" : "Table unlocked");
                  } catch (err) {
                    setForm((prev) => (prev ? { ...prev, is_locked: previous } : null));
                    setError(err instanceof Error ? err.message : "Failed to update lock");
                  }
                }}
              />
            </div>
          </div>

          {/* Auto-Numbering Settings */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Auto-Numbering</Label>
                <SaveChip status={autoNumberChip.status} />
              </div>
              <Switch
                checked={form.auto_number_enabled}
                onCheckedChange={async (checked) => {
                  // Re-enabling over a dormant per-parent + no-prefix +
                  // unique-target config would recreate the invalid
                  // combination — block it with the shared message, same
                  // as the other auto-number doors (backend parity).
                  if (
                    checked &&
                    form.auto_number_scope === "per_parent" &&
                    !form.auto_number_source_field_id &&
                    autoNumberTargetIsUnique
                  ) {
                    setError(AUTO_NUMBER_UNIQUE_CONFLICT_MESSAGE);
                    return;
                  }
                  autoNumberChip.markSaving();
                  try {
                    const updated = await myappApi.tables.update(token, formId, { auto_number_enabled: checked });
                    setForm((prev) => (prev ? { ...prev, ...updated } : null));
                    autoNumberChip.markSaved();
                  } catch (err) {
                    autoNumberChip.markError();
                    setError(err instanceof Error ? err.message : "Failed to update auto-numbering");
                  }
                }}
              />
            </div>
            {form.auto_number_enabled && (
              <div className="grid grid-cols-2 gap-4">
                {/* Target Field Selector */}
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="auto-number-target" className="text-xs text-muted-foreground">
                    Target Field
                  </Label>
                  <Select
                    value={form.auto_number_target_field_id ? String(form.auto_number_target_field_id) : ""}
                    onValueChange={async (value) => {
                      // Handle "Create new field" option
                      if (value === "__create_new__") {
                        handleCreateFieldFromAutoNumber();
                        return;
                      }
                      // Per-parent with no prefix source: a unique field
                      // cannot be the target (backend parity). Items are
                      // disabled below; this guards a stale menu.
                      if (
                        form.auto_number_scope === "per_parent" &&
                        !form.auto_number_source_field_id &&
                        form.fields?.some((f) => f.id === value && f.is_unique)
                      ) {
                        setError(AUTO_NUMBER_UNIQUE_CONFLICT_MESSAGE);
                        return;
                      }
                      autoNumberChip.markSaving();
                      try {
                        const targetFieldId = value || null;
                        const updated = await myappApi.tables.update(token, formId, { auto_number_target_field_id: targetFieldId });
                        setForm((prev) => (prev ? { ...prev, ...updated } : null));
                        autoNumberChip.markSaved();
                      } catch (err) {
                        autoNumberChip.markError();
                        setError(err instanceof Error ? err.message : "Failed to update target field");
                      }
                    }}
                  >
                    <SelectTrigger id="auto-number-target">
                      <SelectValue placeholder="Select a text field" />
                    </SelectTrigger>
                    <SelectContent>
                      {form.fields
                        ?.filter((f) => {
                          // Only show text-type fields
                          const isTextField = f.field_type?.code === "text";
                          // Exclude already auto-numbered fields (unless it's the current target)
                          const isCurrentTarget = f.id === form.auto_number_target_field_id;
                          return isTextField && (!f.is_auto_numbered || isCurrentTarget);
                        })
                        .map((f) => (
                          <SelectItem
                            key={f.id}
                            value={String(f.id)}
                            // Unique fields cannot be targeted while the
                            // scope is per-parent with no prefix source —
                            // bare sequences repeat across parents.
                            disabled={
                              form.auto_number_scope === "per_parent" &&
                              !form.auto_number_source_field_id &&
                              f.is_unique
                            }
                          >
                            {f.name} ({f.column_name})
                          </SelectItem>
                        ))}
                      <SelectSeparator />
                      <SelectItem value="__create_new__" className="text-primary">
                        <span className="flex items-center gap-2">
                          <Plus className="h-4 w-4" />
                          Create new text field
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {form.auto_number_target_field_id
                      ? "Value will be stored in this field when records are created."
                      : "Select or create a text field to store the auto-number."}
                  </p>
                </div>

                {/* Scope selector — only offered on line tables. Lets the user
                    pick between a table-wide counter (global, default) and a
                    per-parent counter that resets for each header row. */}
                {form.parent_table_id && (
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="auto-number-scope" className="text-xs text-muted-foreground">
                      Scope
                    </Label>
                    <Select
                      value={form.auto_number_scope ?? "global"}
                      onValueChange={async (value) => {
                        // Switching to per-parent starts prefix-less (no
                        // source field yet), which cannot target a unique
                        // field — mirror the backend rule up front.
                        if (
                          value === "per_parent" &&
                          !form.auto_number_source_field_id &&
                          autoNumberTargetIsUnique
                        ) {
                          setError(AUTO_NUMBER_UNIQUE_CONFLICT_MESSAGE);
                          return;
                        }
                        autoNumberChip.markSaving();
                        try {
                          const updated = await myappApi.tables.update(
                            token,
                            formId,
                            { auto_number_scope: value as "global" | "per_parent" },
                          );
                          setForm((prev) => (prev ? { ...prev, ...updated } : null));
                          autoNumberChip.markSaved();
                        } catch (err) {
                          autoNumberChip.markError();
                          setError(
                            err instanceof Error
                              ? err.message
                              : "Failed to update scope",
                          );
                        }
                      }}
                    >
                      <SelectTrigger id="auto-number-scope">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="global">
                          Global — single counter for the whole table
                        </SelectItem>
                        <SelectItem value="per_parent">
                          Per-parent — counter resets for each header row
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {form.auto_number_scope === "per_parent"
                        ? "Each line gets a number scoped to its parent (e.g. SO123-001, SO123-002). Use a field on this line as the prefix — inherit one from the parent first if needed."
                        : "All lines share one sequence (e.g. 00001, 00002)."}
                    </p>
                  </div>
                )}

                {/* Source-field picker — per-parent scope only. Picks a field
                    on THIS table whose value becomes the dynamic prefix. The
                    field can be user-entered, calculated, or (most commonly)
                    inherited from the parent via the "Inherit from Parent"
                    flow in the Add Field dialog. */}
                {form.parent_table_id && form.auto_number_scope === "per_parent" && (
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="auto-number-source" className="text-xs text-muted-foreground">
                      Prefix Source Field (on this line)
                    </Label>
                    {/* Help text rendered ABOVE the dropdown so the
                        "inherit from parent" CTA stays visible while the
                        user is choosing - the dropdown menu would
                        otherwise overlay anything beneath it. */}
                    {inheritableParentFields.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Need the parent's number (e.g. Requisition Number) here?
                        {" "}
                        <button
                          type="button"
                          className="text-primary underline-offset-2 hover:underline font-medium"
                          onClick={() =>
                            handleAddFieldClick({ presetFieldTypeId: INHERIT_FROM_PARENT_ID })
                          }
                        >
                          Inherit a field from the parent
                        </button>
                        {" "}onto this line, then pick it below. A parent link field also works - its display value is kept in sync with the parent automatically.
                      </p>
                    )}
                    <Select
                      value={
                        form.auto_number_source_field_id
                          ? String(form.auto_number_source_field_id)
                          : "none"
                      }
                      onValueChange={async (value) => {
                        // Defence in depth: the "none" item is disabled when
                        // the target is unique, but block here too so a stale
                        // menu can never send the invalid combination.
                        if (value === "none" && autoNumberTargetIsUnique) {
                          setError(AUTO_NUMBER_UNIQUE_CONFLICT_MESSAGE);
                          return;
                        }
                        autoNumberChip.markSaving();
                        try {
                          const NIL_UUID = "00000000-0000-0000-0000-000000000000";
                          // "none" = explicit "no prefix" choice; the nil
                          // UUID tells the backend to clear the source field.
                          const sourceId = value === "none" ? NIL_UUID : value;
                          const updated = await myappApi.tables.update(
                            token,
                            formId,
                            { auto_number_source_field_id: sourceId },
                          );
                          setForm((prev) => (prev ? { ...prev, ...updated } : null));
                          autoNumberChip.markSaved();
                        } catch (err) {
                          autoNumberChip.markError();
                          setError(
                            err instanceof Error
                              ? err.message
                              : "Failed to update source field",
                          );
                        }
                      }}
                    >
                      <SelectTrigger id="auto-number-source">
                        {/* No placeholder: the value is always definite —
                            either a real source field or the explicit
                            "No prefix" choice below. */}
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" disabled={autoNumberTargetIsUnique}>
                          No prefix — numbers only (00001, 00002…)
                        </SelectItem>
                        {form.fields
                          ?.filter((f) => {
                            // Match the backend whitelist in table_service.update.
                            // Parent-link fields are also allowed - the line's
                            // display column holds a denormalised snapshot of
                            // the parent's value (cascade-kept fresh on
                            // reparent), which is exactly what prefix needs.
                            const allowed = new Set([
                              "text",
                              "longtext",
                              "int",
                              "bigint",
                              "email",
                              "concatenation",
                              "calculation",
                            ]);
                            const isParentLink =
                              f.field_type?.code === "link" &&
                              !f.is_hidden &&
                              !!form.parent_table_id &&
                              f.linked_table_id === form.parent_table_id;
                            return (
                              !f.is_hidden &&
                              f.field_type?.code &&
                              (allowed.has(f.field_type.code) || isParentLink) &&
                              f.id !== form.auto_number_target_field_id
                            );
                          })
                          .map((f) => (
                            <SelectItem key={f.id} value={String(f.id)}>
                              {f.name}
                              {f.is_inherited ? (
                                <span className="text-muted-foreground ml-2">
                                  (inherited from {parentTable?.name ?? "parent"})
                                </span>
                              ) : null}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    {autoNumberTargetIsUnique && (
                      // Muted while a source is selected (explains why the
                      // "No prefix" item is disabled); destructive if the
                      // invalid prefix-less + unique state somehow exists.
                      <p
                        className={
                          form.auto_number_source_field_id
                            ? "text-xs text-muted-foreground"
                            : "text-xs text-destructive"
                        }
                      >
                        {AUTO_NUMBER_UNIQUE_CONFLICT_MESSAGE}
                      </p>
                    )}
                  </div>
                )}

                {/* Static Prefix input — hidden in per-parent mode since the
                    source field replaces the static prefix. */}
                {form.auto_number_scope !== "per_parent" && (
                <div className="space-y-2">
                  <Label htmlFor="auto-number-prefix" className="text-xs text-muted-foreground">
                    Prefix
                  </Label>
                  <Input
                    id="auto-number-prefix"
                    placeholder="e.g., SO, INV, CUST"
                    // Uncontrolled (defaultValue + onBlur) — saving per-
                    // keystroke via `value + async onChange` caused a typing
                    // race where fast input arrived out of order ("ABCD" →
                    // "ACBD") because the controlled re-render lagged the
                    // user's keystrokes. Save on blur is enough; auto-number
                    // prefix only affects future records.
                    key={`prefix-${form.auto_number_prefix ?? ""}`}
                    defaultValue={form.auto_number_prefix || ""}
                    maxLength={20}
                    onBlur={async (e) => {
                      const prefix = e.target.value || null;
                      if (prefix === (form.auto_number_prefix ?? null)) return;
                      autoNumberChip.markSaving();
                      try {
                        const updated = await myappApi.tables.update(token, formId, { auto_number_prefix: prefix });
                        setForm((prev) => (prev ? { ...prev, ...updated } : null));
                        autoNumberChip.markSaved();
                      } catch (err) {
                        autoNumberChip.markError();
                        setError(err instanceof Error ? err.message : "Failed to update prefix");
                      }
                    }}
                  />
                </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="auto-number-delimiter" className="text-xs text-muted-foreground">
                    Delimiter
                  </Label>
                  <Select
                    value={form.auto_number_delimiter || "none"}
                    onValueChange={async (value) => {
                      autoNumberChip.markSaving();
                      try {
                        const delimiter = value === "none" ? "" : value;
                        const updated = await myappApi.tables.update(token, formId, { auto_number_delimiter: delimiter });
                        setForm((prev) => (prev ? { ...prev, ...updated } : null));
                        autoNumberChip.markSaved();
                      } catch (err) {
                        autoNumberChip.markError();
                        setError(err instanceof Error ? err.message : "Failed to update delimiter");
                      }
                    }}
                  >
                    <SelectTrigger id="auto-number-delimiter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="-">Hyphen (-)</SelectItem>
                      <SelectItem value="/">Slash (/)</SelectItem>
                      <SelectItem value=".">Dot (.)</SelectItem>
                      <SelectItem value="_">Underscore (_)</SelectItem>
                      <SelectItem value="none">None</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="auto-number-padding" className="text-xs text-muted-foreground">
                    Number of Characters
                  </Label>
                  <Input
                    id="auto-number-padding"
                    type="number"
                    min={1}
                    max={(() => {
                      const targetField = form.fields?.find((f) => f.id === form.auto_number_target_field_id);
                      return targetField?.max_length || 10;
                    })()}
                    // Uncontrolled to avoid per-keystroke typing race —
                    // see note on auto-number-prefix above.
                    key={`padding-${form.auto_number_padding ?? 5}`}
                    defaultValue={form.auto_number_padding || 5}
                    onBlur={async (e) => {
                      const val = parseInt(e.target.value);
                      if (!val || val < 1) return;
                      const targetField = form.fields?.find((f) => f.id === form.auto_number_target_field_id);
                      const maxAllowed = targetField?.max_length || 10;
                      const clamped = Math.min(val, maxAllowed);
                      if (clamped === form.auto_number_padding) return;
                      autoNumberChip.markSaving();
                      try {
                        const updated = await myappApi.tables.update(token, formId, { auto_number_padding: clamped });
                        setForm((prev) => (prev ? { ...prev, ...updated } : null));
                        autoNumberChip.markSaved();
                      } catch (err) {
                        autoNumberChip.markError();
                        setError(err instanceof Error ? err.message : "Failed to update number of characters");
                      }
                    }}
                  />
                </div>
                <div className="col-span-2 text-xs text-muted-foreground bg-muted px-3 py-2 rounded">
                  {(() => {
                    const padding = form.auto_number_padding || 5;
                    const delim =
                      form.auto_number_delimiter && form.auto_number_delimiter !== "none"
                        ? form.auto_number_delimiter
                        : "";
                    const pad = (n: number) =>
                      String(n).padStart(padding, "0");
                    if (form.auto_number_scope === "per_parent") {
                      if (!form.auto_number_source_field_id) {
                        // "No prefix" chosen: the target receives the bare
                        // zero-padded per-parent sequence, equal per row to
                        // the system line number. No square-bracket prefix
                        // placeholder here — none will ever be applied.
                        return (
                          <>
                            Preview: <code>{pad(1)}</code>
                            {" → "}
                            <code>{pad(2)}</code>
                            <div className="mt-1">
                              Lines are numbered per parent record:{" "}
                              <code>{pad(1)}</code>, <code>{pad(2)}</code>, …
                              matching the line number.
                            </div>
                          </>
                        );
                      }
                      const sourceField = form.fields?.find(
                        (f) => f.id === form.auto_number_source_field_id,
                      );
                      // Use square-bracket placeholders to make it obvious
                      // the real runtime value comes from the source field —
                      // not from the literal text shown here.
                      const placeholder = sourceField
                        ? `[${sourceField.name}]`
                        : "[Prefix]";
                      return (
                        <>
                          Preview: <code>{placeholder}{delim}{pad(1)}</code>
                          {" → "}
                          <code>{placeholder}{delim}{pad(42)}</code>
                          <div className="mt-1">
                            The prefix is taken from the selected source field on the line
                            row. If the line row has <code>SO123</code> in that column,
                            lines will be <code>SO123{delim}{pad(1)}</code>,{" "}
                            <code>SO123{delim}{pad(2)}</code>, etc.
                          </div>
                        </>
                      );
                    }
                    const prefix = form.auto_number_prefix || "";
                    const sep = prefix && delim ? delim : "";
                    return (
                      <>
                        Preview: <code>{prefix}{sep}{pad(1)}</code>
                        {" → "}
                        <code>{prefix}{sep}{pad(42)}</code>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Submit Guard */}
          <SubmitGuardSection
            token={token}
            formId={formId}
            fields={form.fields ?? []}
            value={{
              submit_guard_status_field_id: form.submit_guard_status_field_id,
              submit_guard_field_id: form.submit_guard_field_id,
              submit_guard_min: form.submit_guard_min,
              submit_guard_max: form.submit_guard_max,
            }}
            onSaved={(updates) =>
              setForm((prev) => (prev ? { ...prev, ...updates } : null))
            }
          />

          {/* Table Type */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Table Type</Label>
            </div>
            {form.is_published ? (
              <div>
                <Badge variant="secondary">
                  {form.table_type === "simple" && "Simple — Standalone table"}
                  {form.table_type === "advanced" && "Advanced — Header with children"}
                  {form.table_type === "latest" && "Latest Only — Replaces all on import"}
                  {form.table_type === "versioned" && "Versioned — Version-tracked imports"}
                  {form.table_type === "virtual" && "Virtual — Query existing data"}
                  {form.table_type === "bridge" && "Pairing — Bridge/junction table"}
                </Badge>
                <p className="text-xs text-muted-foreground mt-1">
                  Table type cannot be changed after publishing.
                </p>
                {bridgeSummaryConfig && (
                  <BindingSummaryCard
                    config={bridgeSummaryConfig}
                    bridgeFields={form.fields}
                    bridgeTableName={form.name}
                    className="mt-3"
                  />
                )}
                {form.area_mode !== "none" && (
                  <div className="mt-3 border-t pt-3 bg-blue-50/50 -mx-3 px-3 pb-3 rounded-b-lg">
                    <Label className="text-sm font-semibold">Area Categorisation</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="default" className="block w-fit">
                        {form.area_mode === "areas_only" ? "Areas Only" : "Areas & Sub-areas"}
                      </Badge>
                      {form.area_set_id && (
                        <span className="text-sm text-muted-foreground">
                          — {areaSets.find((a) => a.id === form.area_set_id)?.name ?? "Loading..."}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Area categorisation cannot be changed after publishing.
                    </p>
                  </div>
                )}
              </div>
            ) : form.table_type === "virtual" ? (
              <>
                <Badge variant="secondary">Virtual — Query existing data</Badge>
                <p className="text-xs text-muted-foreground mt-1">
                  Configure the source query and refresh settings below.
                </p>
              </>
            ) : form.table_type === "bridge" ? (
              <>
                <Badge variant="secondary">Pairing — Bridge/junction table</Badge>
                {hasBinding ? (
                  <div className="mt-2 flex items-center gap-2 text-sm text-green-700">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    <span>Pairing binding configured.</span>
                    {canAuthorBinding && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-sm"
                        onClick={() => void openBindingEdit()}
                      >
                        Edit binding
                      </Button>
                    )}
                    {canAuthorBinding && (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-sm"
                        onClick={() => navigate({ to: "/portal/myapp/bridge" })}
                      >
                        Manage in Pairing
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-start gap-2 text-sm text-amber-700">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>No pairing binding yet — required before you can publish.</span>
                    </div>
                    {canAuthorBinding && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setPublishAfterBinding(false);
                          setBindingBuilderOpen(true);
                        }}
                      >
                        <LinkIcon className="h-4 w-4 mr-2" />
                        Set up binding
                      </Button>
                    )}
                  </div>
                )}
                {bridgeSummaryConfig && (
                  <BindingSummaryCard
                    config={bridgeSummaryConfig}
                    bridgeFields={form.fields}
                    bridgeTableName={form.name}
                    className="mt-3"
                  />
                )}
              </>
            ) : (
              <>
                <Select
                  value={form.table_type || "simple"}
                  onValueChange={async (val) => {
                    try {
                      const updated = await myappApi.tables.update(token, formId, {
                        table_type: val as "simple" | "advanced" | "latest" | "versioned",
                      });
                      setForm((prev) => (prev ? { ...prev, ...updated } : null));
                    } catch (err) {
                      setError(err instanceof Error ? err.message : "Failed to update table type");
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Capture Tables</SelectLabel>
                      <SelectItem value="simple">Simple — Standalone table</SelectItem>
                      <SelectItem value="advanced">Advanced — With children</SelectItem>
                    </SelectGroup>
                    <SelectSeparator />
                    <SelectGroup>
                      <SelectLabel>Data Tables</SelectLabel>
                      <SelectItem value="latest">Latest Only — Replace all</SelectItem>
                      <SelectItem value="versioned">Versioned — Version-tracked</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {(form.table_type || "simple") === "simple"
                    ? "Standard form-based table. Add records manually or import with conflict options."
                    : (form.table_type || "simple") === "advanced"
                      ? "Header/line pattern for invoices, orders, quotes."
                      : (form.table_type || "simple") === "latest"
                        ? "Each import replaces all existing data. Use for reference/lookup tables."
                        : form.version_mode === "version_and_milestone"
                          ? "Organise data by named versions and milestones."
                          : form.version_mode === "version_only"
                            ? "Organise data by named versions."
                            : "Track versions by filename. Import replaces only the selected version's data."}
                </p>

                {/* Version Mode selector — only for versioned tables */}
                {(form.table_type || "simple") === "versioned" && (
                  <div className="mt-3 border-t pt-3">
                    <Label className="text-xs font-medium text-muted-foreground">Version Mode</Label>
                    <Select
                      value={form.version_mode || "filename"}
                      onValueChange={async (val) => {
                        try {
                          const updated = await myappApi.tables.update(token, formId, {
                            version_mode: val as "filename" | "version_only" | "version_and_milestone",
                          });
                          setForm((prev) => (prev ? { ...prev, ...updated } : null));
                        } catch (err) {
                          setError(err instanceof Error ? err.message : "Failed to update version mode");
                        }
                      }}
                    >
                      <SelectTrigger className="w-full mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="filename">By Filename</SelectItem>
                        <SelectItem value="version_only">Versions Only</SelectItem>
                        <SelectItem value="version_and_milestone">Versions &amp; Milestones</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Area categorisation — only for latest/versioned tables */}
                {((form.table_type || "simple") === "latest" || (form.table_type || "simple") === "versioned") && (
                  <div className="mt-3 border-t pt-3">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs font-medium text-muted-foreground">Area Categorisation</Label>
                      <SaveChip status={areaSetChip.status} />
                    </div>
                    <Select
                      value={form.area_mode || "none"}
                      onValueChange={async (val) => {
                        areaSetChip.markSaving();
                        try {
                          const updated = await myappApi.tables.update(token, formId, {
                            area_mode: val as "none" | "areas_only" | "areas_and_subareas",
                          });
                          setForm((prev) => (prev ? { ...prev, ...updated } : null));
                          areaSetChip.markSaved();
                        } catch (err) {
                          areaSetChip.markError();
                          toast.error(err instanceof Error ? err.message : "Failed to update area mode");
                        }
                      }}
                    >
                      <SelectTrigger className="w-full mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not Applicable</SelectItem>
                        <SelectItem value="areas_only">Areas Only</SelectItem>
                        <SelectItem value="areas_and_subareas">Areas &amp; Sub-areas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {/* Import defaults — published or not, editors only (managed modes) */}
            {canStructure &&
              form.table_type === "versioned" &&
              (form.version_mode === "version_only" || form.version_mode === "version_and_milestone") && (
                <TableImportDefaults
                  token={token}
                  tableId={formId}
                  versionMode={form.version_mode}
                  defaultVersionId={form.default_version_id}
                  defaultMilestoneId={form.default_milestone_id}
                  refreshKey={versionsRefreshKey}
                  onSaved={(updated) => setForm((prev) => (prev ? { ...prev, ...updated } : null))}
                />
              )}
          </div>
        </CardContent>
      </Card>

      {/* Versions panel — shown on edit page for versioned tables with managed version modes */}
      {form.table_type === "versioned" && (form.version_mode === "version_only" || form.version_mode === "version_and_milestone") && (
        <VersionMilestonePanel
          tableId={formId}
          showMilestones={form.version_mode === "version_and_milestone"}
          onVersionsChanged={() => {
            setVersionsRefreshKey((k) => k + 1);
            // Deleting a version/milestone clears the table's import default
            // server-side; pick that up so the default selects don't go stale
            myappApi.tables
              .get(token, formId)
              .then((t) =>
                setForm((prev) =>
                  prev
                    ? { ...prev, default_version_id: t.default_version_id, default_milestone_id: t.default_milestone_id }
                    : null,
                ),
              )
              .catch(() => {});
          }}
        />
      )}
      {/* Area Set picker — shown when area_mode is enabled */}
      {form.area_mode !== "none" && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Area Set</CardTitle>
              <SaveChip status={areaSetChip.status} />
            </div>
            <CardDescription>
              Link this table to an organisation-level area set.
              Manage area sets on the <Link to="/portal/myapp/areas" className="text-primary underline underline-offset-2">Areas page</Link>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingAreaSets ? (
              <p className="text-sm text-muted-foreground">Loading area sets...</p>
            ) : areaSets.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No area sets exist yet. <Link to="/portal/myapp/areas" className="text-primary underline underline-offset-2">Create one</Link> to get started.
              </p>
            ) : (
              <Select
                value={form.area_set_id || "none"}
                onValueChange={async (val) => {
                  areaSetChip.markSaving();
                  try {
                    const updated = await myappApi.tables.update(token, formId, {
                      area_set_id: val === "none" ? "00000000-0000-0000-0000-000000000000" : val,
                    });
                    setForm((prev) => (prev ? { ...prev, ...updated } : null));
                    areaSetChip.markSaved();
                    toast.success(val === "none" ? "Area set unlinked" : "Area set linked");
                  } catch (err) {
                    areaSetChip.markError();
                    toast.error(err instanceof Error ? err.message : "Failed to update area set");
                  }
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select an area set" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    <span className="text-muted-foreground">No area set linked</span>
                  </SelectItem>
                  {areaSets.map((as) => (
                    <SelectItem key={as.id} value={as.id}>
                      {as.name}
                      {as.description && (
                        <span className="text-muted-foreground ml-1">— {as.description}</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardContent>
        </Card>
      )}
      {/* Sharing — Authenticated Entry Link + QR */}
      {form.is_published && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <LinkIcon className="h-4 w-4" />
              Authenticated Entry
            </CardTitle>
            <CardDescription>
              Share this link with logged-in users so they can submit data directly.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6 items-start">
              <div className="flex-1 space-y-2">
                <Input
                  value={`${window.location.origin}/portal/myapp/${formId}/submit`}
                  readOnly
                  className="font-mono text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/portal/myapp/${formId}/submit`);
                      toast.success("Authenticated entry link copied");
                    }}
                  >
                    Copy Link
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`${window.location.origin}/portal/myapp/${formId}/submit`, "_blank")}
                  >
                    Open
                  </Button>
                </div>
              </div>
              <QRCodeDisplay
                url={`${window.location.origin}/portal/myapp/${formId}/submit`}
                label="Authenticated Entry"
              />
            </div>
          </CardContent>
        </Card>
      )}
      {/* Anonymous Entry */}
      <AnonymousSettingsPanel tableId={formId} isPublished={form.is_published} />

      {/* Workflow States + Field Lockdown — only relevant for top-level
          Simple / Advanced tables (composite children inherit parent state). */}
      {form &&
        (form.table_type === "simple" || form.table_type === "advanced") &&
        !form.parent_table_id && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base">Workflow states</CardTitle>
              <CardDescription>
                Define linear states for records on this table. Each field
                can declare the state at which it locks; records advance
                through states with role-gated transitions.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TableStatesEditor
                token={token}
                tableId={formId}
                fields={(form.fields ?? []).map((f) => ({
                  // Matrix is now a read-only visual; the per-field rule
                  // controls live inline on each field card via
                  // SortableFieldItem's Locks-at / Required-from selects.
                  // Required-from indicator is still rendered statically in
                  // the matrix so the at-a-glance summary stays complete.
                  id: f.id,
                  name: f.name,
                  locked_at_state_id: f.locked_at_state_id ?? null,
                  required_from_state_id: f.required_from_state_id ?? null,
                }))}
                onStatesChanged={handleTableStatesChanged}
                highlightedFieldId={highlightedFieldId}
              />
            </CardContent>
          </Card>
        )}

      {/* Virtual Table Configuration — source query & refresh settings */}
      {form.table_type === "virtual" && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4" />
              Source Query &amp; Refresh
            </CardTitle>
            <CardDescription>
              Define the SQL query that populates this table and how it refreshes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Source Query */}
            <div className="space-y-2">
              <Label>Source Query (SQL SELECT)</Label>
              <Textarea
                placeholder="SELECT AccountCode, AccountName FROM [dbo].[ChartOfAccounts]"
                value={sourceQueryDraft ?? form.source_query ?? ""}
                onChange={(e) => setSourceQueryDraft(e.target.value)}
                className="font-mono text-xs min-h-[160px]"
                rows={8}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleValidateQuery}
                  disabled={queryValidating || !(sourceQueryDraft ?? form.source_query)?.trim()}
                >
                  {queryValidating ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Validating...</>
                  ) : (
                    <><Play className="h-3.5 w-3.5 mr-1" />Validate &amp; Preview Columns</>
                  )}
                </Button>
                {sourceQueryDraft !== null && sourceQueryDraft !== form.source_query && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveSourceQuery}
                  >
                    <Save className="h-3.5 w-3.5 mr-1" />
                    Save Query
                  </Button>
                )}
                {detectedColumns.length > 0 && (
                  <span className="text-xs text-green-600">
                    {detectedColumns.length} column{detectedColumns.length !== 1 ? "s" : ""} detected
                  </span>
                )}
              </div>
              {queryError && (
                <p className="text-sm text-destructive">{queryError}</p>
              )}
              {detectedColumns.length > 0 && (
                <div className="rounded border bg-muted/50 p-3">
                  <p className="text-xs font-medium mb-1.5 text-muted-foreground">Detected Columns</p>
                  <div className="grid grid-cols-3 gap-1 text-xs">
                    {detectedColumns.map((col) => (
                      <div key={col.name} className="flex items-center gap-1">
                        <span className="font-mono">{col.name}</span>
                        <span className="text-muted-foreground">({col.suggested_field_type})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <SampleDataTable columns={detectedColumns} rows={previewRows} />
            </div>

            {/* Unique Key Column — a linked table without one cannot refresh (dp #2139) */}
            <div className="border-t pt-4">
              <UniqueKeyPicker
                // Remount on a saved query change so a check made against the old query cannot be saved.
                key={form.source_query ?? ""}
                token={token}
                form={form}
                queryHasUnsavedChanges={sourceQueryDraft !== null && sourceQueryDraft !== form.source_query}
                onSaved={(updated) => {
                  setForm((prev) => (prev ? { ...prev, ...updated } : null));
                  toast.success("Unique key set");
                }}
              />
            </div>

            {/* Refresh Mode */}
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center gap-2">
                <Label>Refresh Mode</Label>
                <SaveChip status={refreshModeChip.status} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <SelectableCard
                  selected={form.refresh_mode === "auto_before_import"}
                  onClick={() => handleRefreshModeChange("auto_before_import")}
                  icon={Play}
                  label="Before Use"
                  description="Auto-refresh when data is accessed"
                  badge="Recommended"
                />
                <SelectableCard
                  selected={form.refresh_mode === "manual"}
                  onClick={() => handleRefreshModeChange("manual")}
                  icon={RefreshCw}
                  label="Manual"
                  description="Click to refresh"
                />
                <SelectableCard
                  selected={form.refresh_mode === "scheduled"}
                  onClick={() => handleRefreshModeChange("scheduled")}
                  icon={History}
                  label="Scheduled"
                  description="Refresh on a timer"
                />
              </div>
              {form.refresh_mode === "scheduled" && (
                <div className="space-y-1.5 mt-2">
                  <Label className="text-xs">Refresh Interval (minutes)</Label>
                  <Input
                    type="number"
                    min={5}
                    max={1440}
                    defaultValue={form.refresh_interval_minutes ?? 60}
                    onBlur={(e) => {
                      const val = Math.max(5, Math.min(1440, Number(e.target.value) || 60));
                      handleRefreshIntervalChange(val);
                    }}
                    className="w-32"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Min 5 min, max 1440 min (24 hours)
                  </p>
                </div>
              )}
            </div>

            {/* Refresh Status & Action */}
            {(form.last_refresh_at_utc || form.is_published) && (
              <div className="space-y-2 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label>Refresh Status</Label>
                  {form.is_published && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleVirtualRefresh}
                      disabled={virtualRefreshing}
                    >
                      {virtualRefreshing ? (
                        <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Refreshing...</>
                      ) : (
                        <><Database className="h-3.5 w-3.5 mr-1" />Refresh Now</>
                      )}
                    </Button>
                  )}
                </div>
                {form.last_refresh_at_utc ? (
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <div>
                      Last refresh: {new Date(form.last_refresh_at_utc).toLocaleString()}
                      {form.last_refresh_status && (
                        <Badge
                          variant={form.last_refresh_status === "success" ? "secondary" : "destructive"}
                          className="ml-2 text-[10px] px-1 py-0"
                        >
                          {form.last_refresh_status}
                        </Badge>
                      )}
                    </div>
                    {form.last_refresh_row_count != null && (
                      <div>{form.last_refresh_row_count} row{form.last_refresh_row_count !== 1 ? "s" : ""}</div>
                    )}
                    {form.last_refresh_duration_ms != null && (
                      <div>{(form.last_refresh_duration_ms / 1000).toFixed(1)}s</div>
                    )}
                    {form.last_refresh_error && (
                      <p className="text-destructive mt-1">{form.last_refresh_error}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">Not yet refreshed</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Fields */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Fields ({form.fields.filter(
                (f) =>
                  !(
                    f.is_hidden &&
                    f.field_type?.code === "link" &&
                    f.linked_table_id === form.parent_table_id
                  )
              ).length})</CardTitle>
              <CardDescription>
                Define the columns for your data table
              </CardDescription>
            </div>
            {canStructure && (
              <div className="flex gap-2">
                {!form.is_published && (
                  <Button variant="outline" onClick={() => setBulkFieldDialogOpen(true)}>
                    <Layers className="h-4 w-4 mr-2" />
                    Bulk Add
                  </Button>
                )}
                {form.fields.length > 0 && (
                  <Button variant="outline" onClick={() => setBulkFieldEditDialogOpen(true)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Bulk Edit
                  </Button>
                )}
                <Button onClick={() => handleAddFieldClick()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Field
                </Button>
              </div>
            )}
          </div>
          {form.fields.length > 10 && (
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter fields..."
                value={fieldFilter}
                onChange={(e) => setFieldFilter(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          )}
        </CardHeader>
        <CardContent>
          {/* Check editable fields (excluding hidden parent link field for line tables) */}
          {form.fields.filter(
            (f) =>
              !(
                f.is_hidden &&
                f.field_type?.code === "link" &&
                f.linked_table_id === form.parent_table_id
              )
          ).length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No fields yet. Add your first field to define your data structure.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              {/* Filter out hidden parent link field (auto-created system field for line tables) */}
              {(() => {
                const editableFields = form.fields.filter(
                  (f) =>
                    !(
                      f.is_hidden &&
                      f.field_type?.code === "link" &&
                      f.linked_table_id === form.parent_table_id
                    )
                );
                const lowerFilter = fieldFilter.toLowerCase().trim();
                const displayFields = lowerFilter
                  ? editableFields.filter(
                      (f) =>
                        f.name.toLowerCase().includes(lowerFilter) ||
                        f.column_name.toLowerCase().includes(lowerFilter)
                    )
                  : editableFields;
                return (
                  <SortableContext
                    items={editableFields.map((f) => f.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {reordering && (
                        <div className="text-xs text-muted-foreground mb-2">
                          Saving order...
                        </div>
                      )}
                      {lowerFilter && (
                        <div className="text-xs text-muted-foreground mb-1">
                          Showing {displayFields.length} of {editableFields.length} fields
                        </div>
                      )}
                      {displayFields.map((field) => (
                        <SortableFieldItem
                          key={field.id}
                          field={field}
                          isPublished={form.is_published}
                          canStructure={canStructure}
                          getFieldTypeName={getFieldTypeName}
                          onEdit={handleEditFieldClick}
                          onDelete={handleDeleteFieldClick}
                          onUnhide={handleUnhideField}
                          unhiding={unhidingFieldIds.has(field.id)}
                          tableStates={tableStates}
                          onJumpToMatrixRow={jumpToMatrixRow}
                          onFieldRuleChange={handleFieldRuleChange}
                        />
                      ))}
                      {lowerFilter && displayFields.length === 0 && (
                        <div className="text-center py-4 text-muted-foreground text-sm">
                          No fields match &ldquo;{fieldFilter}&rdquo;
                        </div>
                      )}
                    </div>
                  </SortableContext>
                );
              })()}
            </DndContext>
          )}
        </CardContent>
      </Card>
      {/* Child Tables (Header/Line Pattern) - Only for advanced tables */}
      {form.table_type === "advanced" && !form.parent_table_id && (
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>Line Tables</CardTitle>
                </div>
                <CardDescription>
                  {form.is_header_table
                    ? "This is a header table with the following line tables"
                    : "Create line tables for header/line patterns (e.g., Order Header + Order Lines)"}
                </CardDescription>
              </div>
              {!form.parent_table_id && canCreateLineTable && (
                <Button variant="outline" onClick={handleCreateChildClick}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Line Table
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {childTables.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground text-sm">
                {form.parent_table_id
                  ? "This is a line table - child tables are not supported for line tables."
                  : "No line tables yet. Create a line table for header/line patterns."}
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleChildDragEnd}
              >
                <SortableContext
                  items={childTables.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {childTables.map((child) => (
                      <SortableChildTableRow
                        key={child.id}
                        child={child}
                        canStructure={canStructure}
                        canReorder={isOrgAdmin}
                        onDelete={handleDeleteChildClick}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </CardContent>
        </Card>
      )}
      {/* Parent Table Info */}
      {form.parent_table_id && (
        <Card className="mb-6 border-blue-200 bg-blue-50/50">
          <CardContent className="py-3">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <Info className="h-4 w-4" />
              <div>
                <p>This is a line table. Records in this table are linked to a parent header record.</p>
                {!form.is_published && (
                  <p className="mt-1 text-blue-600">
                    This table will be automatically published when the header table is published.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {/* Create Child Table Dialog */}
      <Dialog open={createChildDialogOpen} onOpenChange={setCreateChildDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Line Table</DialogTitle>
            <DialogDescription>
              Create a child table for the header/line pattern. For example,
              if this is an &quot;Order&quot; header table, you might create an
              &quot;Order Lines&quot; table.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="child-name">Line Table Name</Label>
              <Input
                id="child-name"
                placeholder="e.g., Order Lines"
                value={newChildName}
                onChange={(e) => setNewChildName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newChildName.trim()) {
                    handleCreateChild();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateChildDialogOpen(false)}
              disabled={creatingChild}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateChild}
              disabled={creatingChild || !newChildName.trim()}
            >
              <Layers className="h-4 w-4 mr-2" />
              {creatingChild ? "Creating..." : "Create Line Table"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Line Table Dialog */}
      <Dialog open={deleteChildDialogOpen} onOpenChange={setDeleteChildDialogOpen}>
        <DialogContent>
          {(() => {
            // Published line tables may only be deleted once empty, so the
            // confirm button stays disabled while a published child still
            // holds records. Drafts and empty published children are fine.
            // Decision logic lives in @/lib/myapp/line-table-delete (unit-tested).
            const { publishedWithData, blocked, showConfirmInput } =
              evaluateLineTableDelete(
                !!childToDelete?.is_published,
                childDeps,
              );
            const canConfirm = canConfirmLineTableDelete({
              checking: checkingChildDeps,
              gate: { publishedWithData, blocked, showConfirmInput },
              confirmText: confirmDeleteChildText,
              tableName: childToDelete?.name ?? "",
            });
            return (
              <>
                <DialogHeader>
                  <DialogTitle>Delete line table?</DialogTitle>
                  <DialogDescription>
                    {checkingChildDeps
                      ? "Checking for dependencies..."
                      : childDeps && !childDeps.can_delete
                        ? `"${childToDelete?.name}" cannot be deleted because it is referenced by other tables.`
                        : publishedWithData
                          ? `"${childToDelete?.name}" is published and still contains data.`
                          : childToDelete?.is_published
                            ? `This will permanently delete the line table "${childToDelete?.name}" and drop its (empty) data table.`
                            : `This will permanently delete the line table "${childToDelete?.name}" and its data table.`}
                  </DialogDescription>
                </DialogHeader>

                {checkingChildDeps && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                )}

                {!checkingChildDeps && childDeps && !childDeps.can_delete && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm">
                    <p className="font-medium text-destructive mb-2">Referenced by:</p>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                      {childDeps.dependencies.map((dep, i) => (
                        <li key={i}>
                          <span className="font-medium">{dep.table_name}</span>
                          {" "}&rarr; {dep.field_name}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-muted-foreground">
                      Remove these references first, then try again.
                    </p>
                  </div>
                )}

                {!checkingChildDeps && childDeps?.can_delete && publishedWithData && (
                  <div className="rounded-md border border-orange-500/50 bg-orange-500/5 p-3 text-sm">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                      <p className="text-orange-700 dark:text-orange-400">
                        This line table is published and still contains{" "}
                        {childDeps.record_count?.toLocaleString()} record
                        {childDeps.record_count === 1 ? "" : "s"}. Purge its data
                        before deleting it.
                      </p>
                    </div>
                  </div>
                )}

                {/* Type-to-confirm input (only when deletion is allowed) */}
                {!checkingChildDeps && childDeps?.can_delete && !publishedWithData && (
                  <div className="space-y-2">
                    <Label className="text-sm">
                      Type <span className="font-bold">{childToDelete?.name}</span> to confirm
                    </Label>
                    <Input
                      value={confirmDeleteChildText}
                      onChange={(e) => setConfirmDeleteChildText(e.target.value)}
                      placeholder={childToDelete?.name || ""}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && canConfirm && !deletingChild) {
                          handleDeleteChildConfirm();
                        }
                      }}
                    />
                  </div>
                )}

                {deleteChildError && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
                    {deleteChildError}
                  </div>
                )}

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setDeleteChildDialogOpen(false)}
                    disabled={deletingChild}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteChildConfirm}
                    disabled={deletingChild || !canConfirm}
                  >
                    {deletingChild ? "Deleting..." : "Delete"}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      {/* Edit Table Details Dialog */}
      <Dialog open={editFormOpen} onOpenChange={setEditFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Table Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Table Name</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => {
                  const newName = e.target.value;
                  setEditFormData((prev) => ({ ...prev, name: newName }));
                  setEditNameError(null);

                  // Cancel any pending validation
                  if (editNameTimerRef.current) {
                    clearTimeout(editNameTimerRef.current);
                  }

                  const trimmed = newName.trim();
                  // Skip validation if name unchanged or empty
                  if (!trimmed || trimmed.toLowerCase() === form?.name.toLowerCase()) {
                    setEditNameValidating(false);
                    return;
                  }

                  // Debounced server-side validation
                  setEditNameValidating(true);
                  editNameTimerRef.current = setTimeout(async () => {
                    try {
                      const result = await myappApi.tables.validateName(
                        token,
                        trimmed,
                        formId,
                      );
                      if (!result.is_valid) {
                        setEditNameError(result.message);
                      }
                    } catch {
                      // Non-critical - backend will still enforce on save
                    } finally {
                      setEditNameValidating(false);
                    }
                  }, 400);
                }}
                className={editNameError ? "border-red-500 focus-visible:ring-red-500" : ""}
              />
              {/* Post-publish help: a rename here only changes the display
                  name. The physical SQL identifier stays frozen so any
                  SQL views, Power BI imports, integrations, or external
                  scripts referencing the table by its physical name keep
                  working. Mirrors the field-rename guard already in
                  place for published fields. */}
              {form?.is_published && (
                <p className="text-xs text-muted-foreground">
                  Display name only. The database table stays as{" "}
                  <code className="text-xs">{form.table_name}</code> so
                  integrations and SQL views keep working.
                </p>
              )}
              {editNameValidating && (
                <p className="text-xs text-muted-foreground">Checking name...</p>
              )}
              {editNameError && (
                <p className="text-sm text-destructive">{editNameError}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editFormData.description}
                onChange={(e) =>
                  setEditFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFormOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveForm}
              disabled={saving || !!editNameError || editNameValidating || !editFormData.name.trim()}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Add/Edit Field Dialog */}
      <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>
              {editingField ? "Edit Field" : "Add Field"}
            </DialogTitle>
            {form.is_published && editingField && (
              <DialogDescription>
                Some properties are locked because the table is published.
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="space-y-4 py-4 overflow-y-auto flex-1 px-2">
            <div className="space-y-2">
              <Label htmlFor="field-name">Field Name</Label>
              <Input
                id="field-name"
                placeholder="e.g., Customer Name"
                value={fieldData.name}
                onChange={(e) => {
                  setFieldData((prev) => ({ ...prev, name: e.target.value }));
                  // Clear error while typing
                  if (fieldNameError) setFieldNameError(null);
                }}
                onBlur={handleFieldNameBlur}
                className={fieldNameError ? "border-red-500 focus-visible:ring-red-500" : ""}
              />
              {fieldNameValidating && (
                <p className="text-xs text-muted-foreground">Validating...</p>
              )}
              {fieldNameError && (
                <p className="text-xs text-red-500">{fieldNameError}</p>
              )}
              {!fieldNameError && generatedColumnName && (
                <p className="text-xs text-muted-foreground">
                  Column name: <code className="bg-muted px-1 py-0.5 rounded font-mono text-xs">{generatedColumnName}</code>
                </p>
              )}
            </div>

            {/* Field type selector — shown for new fields, draft edits, AND published edits */}
            {(
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>Field Type</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="Field type information"
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[28rem] max-h-[24rem] overflow-y-auto" align="start">
                      <div className="space-y-3">
                        <h4 className="font-medium">Field Types</h4>
                        <div className="space-y-2 text-sm">
                          {FIELD_TYPE_DESCRIPTIONS.map((d) => (
                            <div key={d.code}>
                              <span className="font-medium">{d.headline}</span>
                              <p className="text-muted-foreground">{d.detail}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <Select
                  value={fieldData.field_type_id.toString()}
                  // Lock the type dropdown when editing a field that is
                  // ALREADY inherited on a PUBLISHED table. Switching its type
                  // would mean disinheriting + retyping the SQL column at the
                  // same time, which the create flow does not support and the
                  // BE blocks. Gate on the field's ORIGINAL state
                  // (editingField.is_inherited), not isInheritMode (the pending
                  // selection): a non-inherited field being CONVERTED must stay
                  // free to pick the sentinel and change its mind. On draft
                  // tables the column has not been materialised yet, so the
                  // user can freely switch (including disinheriting by picking a
                  // non-inherit type, which the normal-update branch then
                  // explicitly clears IsInherited).
                  disabled={Boolean(editingField?.is_inherited) && Boolean(form?.is_published)}
                  onValueChange={async (value) => {
                    const newTypeId = value;

                    // Re-arm the type-change confirmation: picking a new type
                    // invalidates any prior Apply Change approval, so the
                    // user must reconfirm.
                    pendingTypeChangeApprovedRef.current = false;

                    // Inherit-from-Parent sentinel: route into the inherit flow.
                    // No validateName yet, the name is driven by the picked parent field.
                    if (newTypeId === INHERIT_FROM_PARENT_ID) {
                      setFieldData((prev) => ({
                        ...prev,
                        field_type_id: newTypeId,
                        linked_table_id: null,
                        linked_display_field_id: null,
                      }));
                      setDisplayFields([]);
                      setInheritError(null);
                      setFieldNameError(null);
                      setGeneratedColumnName(null);
                      return;
                    }

                    // Leaving inherit mode — clear the inherit pickers so they don't
                    // linger with stale selections.
                    setInheritSourceFieldId("");
                    setInheritError(null);

                    const selectedType = fieldTypes.find((ft) => ft.id === newTypeId);

                    // Auto-set name for Sort Order field type
                    const shouldAutoSetName = selectedType?.code === "sortorder" && !fieldData.name;
                    const autoSetName = shouldAutoSetName ? "Sort Order" : fieldData.name;

                    setFieldData((prev) => ({
                      ...prev,
                      field_type_id: newTypeId,
                      name: autoSetName,
                      // Clear linked fields when changing away from link type
                      linked_table_id: null,
                      linked_display_field_id: null,
                    }));
                    setDisplayFields([]);

                    // Trigger column name generation if name was auto-set
                    if (shouldAutoSetName) {
                      try {
                        const result = await myappApi.fields.validateName(token, formId, autoSetName, undefined);
                        setGeneratedColumnName(result.generated_column_name);
                        if (!result.is_valid) {
                          setFieldNameError(result.message);
                        }
                      } catch {
                        // Ignore validation errors here, user can fix on blur
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const showTypes = editingField && form?.is_published ? publishedEditTypes : { special: specialFieldTypes, data: dataFieldTypes };
                      // Show "Inherit from Parent" when:
                      //   - Editing an existing inherited field (so the
                      //     dropdown can display the sentinel rather than
                      //     reverting to the underlying type label), OR
                      //   - On any line table that has at least one inheritable
                      //     parent field — whether ADDING a new field or
                      //     CONVERTING an existing (non-inherited) field. The
                      //     convert case is what lets a published line field be
                      //     turned into an inherited one (WS1); the BE handles
                      //     it as a metadata-only change.
                      // The dropdown is `disabled` for an ALREADY-inherited
                      // published field by the trigger's disabled prop above,
                      // so a convertible (non-inherited) field can still pick
                      // the sentinel and change its mind.
                      const showInheritOption =
                        Boolean(editingField?.is_inherited) ||
                        (!!form?.parent_table_id &&
                          inheritableParentFields.length > 0);
                      // Bolder, spaced-out group headers pinned to the left edge so the
                      // items (which default to pl-8 for the check-mark slot) visually sit
                      // "under" the heading rather than beside it.
                      const groupLabelClass =
                        "pl-1 text-xs font-bold uppercase tracking-wider text-muted-foreground";
                      // Keep shadcn's default item padding (pl-8) — no override needed;
                      // sentinel constant left here for future per-item tweaks.
                      const itemClass = "";
                      return (
                        <>
                          {(showTypes.special.length > 0 || showInheritOption) && (
                            <SelectGroup>
                              <SelectLabel className={groupLabelClass}>Special Fields</SelectLabel>
                              {showInheritOption && (
                                <SelectItem
                                  key={INHERIT_FROM_PARENT_ID}
                                  value={INHERIT_FROM_PARENT_ID}
                                  className={itemClass}
                                >
                                  Inherit from Parent
                                </SelectItem>
                              )}
                              {showTypes.special.map((ft) => (
                                <SelectItem
                                  key={ft.id}
                                  value={ft.id.toString()}
                                  className={itemClass}
                                >
                                  {ft.name}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          {(showTypes.special.length > 0 || showInheritOption) && showTypes.data.length > 0 && (
                            <SelectSeparator />
                          )}
                          {showTypes.data.length > 0 && (
                            <SelectGroup>
                              <SelectLabel className={groupLabelClass}>Data Fields</SelectLabel>
                              {showTypes.data.map((ft) => (
                                <SelectItem
                                  key={ft.id}
                                  value={ft.id.toString()}
                                  className={itemClass}
                                >
                                  {ft.name}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                        </>
                      );
                    })()}
                  </SelectContent>
                </Select>
                {/* Per-type description box. Mirrors the matching entry in the
                    Field Type info popover. Hidden in inherit mode because the
                    inherit picker block below has its own description. */}
                {!isInheritMode && (
                  <FieldTypeInfoBox code={selectedFieldType?.code} />
                )}
              </div>
            )}

            {/* Inherit-from-Parent mode: parent-field picker replaces the generic per-type config */}
            {isInheritMode && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="inherit-source-field">Source field on parent</Label>
                  {(() => {
                    // Wave 1+2: three-state picker.
                    //  - LOCKED   : healthy FK, default. Read-only chip with
                    //               a Lock icon and a labelled "Change source"
                    //               button. The button re-uses the existing
                    //               editingSource flag (unlock-on-demand).
                    //  - AUTO-UNLOCKED: FK is null OR points at a hidden /
                    //               missing parent field. Picker is editable
                    //               immediately, with an amber repair warning
                    //               above it. NO confirm dialog on Update -
                    //               this is a repair, not a behaviour change.
                    //  - UNLOCKED ON DEMAND: user clicked Change source on a
                    //               locked chip. Picker becomes editable; on
                    //               Update for a published table the snapshot
                    //               warning confirm fires (see save handler).
                    //
                    // We key "has source" off the field's own source_field_id
                    // (instant), NOT the reconstructed inheritSourceFieldId
                    // (which can lag while parent fields load).
                    const hasExistingSource = Boolean(editingField?.source_field_id);
                    const sourceHealth = resolveSourceFieldHealth(
                      editingField?.source_field_id ?? null,
                    );
                    const isDangling =
                      sourceHealth === "hidden" || sourceHealth === "missing";
                    const sourceIsEditable =
                      !editingField ||
                      !hasExistingSource ||
                      editingSource ||
                      isDangling;

                    if (!sourceIsEditable) {
                      const sourceName =
                        editingField?.source_field_name ||
                        inheritableParentFields.find(
                          (f) => f.id === editingField?.source_field_id,
                        )?.name ||
                        "the parent field";
                      return (
                        <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                          <span className="flex items-center gap-2">
                            <Lock
                              className="h-3.5 w-3.5 text-muted-foreground"
                              aria-hidden="true"
                            />
                            <span>
                              Inherits from{" "}
                              <span className="font-medium">{sourceName}</span>
                            </span>
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            onClick={() => setEditingSource(true)}
                            aria-label="Change source field"
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1" />
                            Change source
                          </Button>
                        </div>
                      );
                    }

                    return (
                      <>
                        {isDangling && (
                          <div
                            role="alert"
                            className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-md mb-2"
                          >
                            <AlertTriangle
                              className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0"
                              aria-hidden="true"
                            />
                            <div className="text-xs text-amber-700 dark:text-amber-300">
                              <p className="font-medium">
                                {sourceHealth === "hidden"
                                  ? "Source field is hidden on the parent"
                                  : "Source field is missing on the parent"}
                              </p>
                              <p className="mt-1">
                                {sourceHealth === "hidden"
                                  ? "The parent field this row inherits from has been hidden. Pick a visible source below to re-link, then click Update."
                                  : "The parent field this row inherits from is no longer available. Pick a new source below, then click Update."}
                              </p>
                            </div>
                          </div>
                        )}
                        <Select
                          value={inheritSourceFieldId}
                          // Enabled on draft AND published. On published we
                          // suppress the column-rename side effect below, so
                          // picking only updates source_field_id (a pure
                          // metadata pointer the BE accepts; the physical
                          // column is untouched).
                          onValueChange={(value) => {
                            setInheritSourceFieldId(value);
                            const src = inheritableParentFields.find(
                              (f) => f.id === value,
                            );
                            if (!src) return;
                            if (form?.is_published) {
                              return;
                            }
                            // Draft: mirror the source's name / linked refs so
                            // the cascade "just works" out of the box. User can
                            // override the name before saving.
                            setFieldData((prev) => ({
                              ...prev,
                              name: src.name,
                              linked_table_id: src.linked_table_id ?? null,
                              linked_display_field_id:
                                src.linked_display_field_id ?? null,
                            }));
                          }}
                        >
                          <SelectTrigger id="inherit-source-field">
                            <SelectValue placeholder="Pick a field on the parent to inherit" />
                          </SelectTrigger>
                          <SelectContent>
                            {inheritableParentFields.map((f) => (
                              <SelectItem key={f.id} value={f.id}>
                                {f.name}
                                {f.field_type?.name ? (
                                  <span className="text-muted-foreground ml-2">
                                    ({f.field_type.name})
                                  </span>
                                ) : null}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    );
                  })()}
                  {editingField !== null && !editingField.source_field_id && form?.is_published && (
                    <p className="text-xs text-muted-foreground italic">
                      Couldn't auto-detect the original source field. Pick
                      one above to repair the inheritance link, then click
                      Update. Only the source pointer changes; the column
                      itself stays exactly as it is.
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    The value is copied from the parent row when this line is
                    saved, and re-copied if the line's parent link later
                    changes. Edits to the parent row itself after the line is
                    saved do NOT propagate back to this line.
                  </p>
                  {/* Concatenation-specific tip: parent's concat re-derives on
                      every parent UPDATE, but this inherited copy is a
                      snapshot, so the two can drift over time. Surfaces the
                      reporting workaround inline so users don't have to dig
                      through docs. */}
                  {(() => {
                    const src = inheritableParentFields.find(
                      (f) => f.id === inheritSourceFieldId,
                    );
                    if (src?.field_type?.code !== "concatenation") return null;
                    return (
                      <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-md">
                        <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-700 dark:text-amber-300">
                          <p className="font-medium">Snapshot of a concatenated value</p>
                          <p className="mt-1">
                            The parent's concatenation is recomputed whenever
                            its inputs change, but this line keeps the value
                            captured at save time. If reports need the live
                            header value, link to the parent table from the
                            report instead of relying on this inherited copy.
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                {inheritError && (
                  <div className="text-sm text-destructive">{inheritError}</div>
                )}
              </>
            )}

            {/* Generic per-type config — hidden in inherit mode so the user sees only
                the parent-field picker. All downstream config belongs to real field types. */}
            {!isInheritMode && (
            <>

            {/* Warning when changing field type on a published table */}
            {editingField && form?.is_published && fieldData.field_type_id !== editingField.field_type?.id && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded-md border border-amber-200 dark:border-amber-800">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Changing the field type on a published table will alter the database column.
                  You will be asked to confirm before the change is applied.
                </p>
              </div>
            )}

            {/* Link to Table selection (new fields, draft editing, converting a
                published field into a link, OR re-pointing an isolated published
                link to a different table — pick the target) */}
            {isLinkType &&
              (!editingField || !form.is_published || isConverting || editingLinkIsIsolated) && (
              <>
                {/* Re-pointing a published link: warn that existing rows keep
                    their stale display value (not re-linked) and the old FK
                    column is left dormant. */}
                {editingField && form.is_published && editingLinkIsIsolated && (
                  <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-950/30 rounded-md border border-amber-200 dark:border-amber-800">
                    <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      Changing the linked table won&apos;t re-link existing records —
                      they&apos;ll keep showing their previous value, but it&apos;s no
                      longer linked to a record. The old link column is left in place
                      but no longer used.
                    </p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Link to Table</Label>
                  <Select
                    value={fieldData.linked_table_id?.toString() || ""}
                    onValueChange={(value) =>
                      handleLinkedFormChange(value || null)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a table to link" />
                    </SelectTrigger>
                    <SelectContent>
                      {linkableTables.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground">
                          No published tables available
                        </div>
                      ) : (
                        linkableTables.map((f) => (
                          <SelectItem key={f.id} value={f.id.toString()}>
                            {f.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    This field will store an ID that references a record in the selected table
                  </p>
                </div>

                {fieldData.linked_table_id && (
                  <div className="space-y-2">
                    <Label>Display Field</Label>
                    <Combobox
                      options={displayFields.map((f) => ({
                        value: f.id.toString(),
                        label: f.name,
                      }))}
                      value={fieldData.linked_display_field_id?.toString() || ""}
                      onValueChange={(value) =>
                        setFieldData((prev) => ({
                          ...prev,
                          linked_display_field_id: value || null,
                        }))
                      }
                      placeholder={
                        loadingDisplayFields
                          ? "Loading fields..."
                          : "Select field to display"
                      }
                      searchPlaceholder="Search fields..."
                      emptyMessage="No display-eligible fields available"
                      disabled={loadingDisplayFields}
                    />
                    <p className="text-xs text-muted-foreground">
                      This field will be shown in dropdowns instead of the ID
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Read-only linked-table info — for an ALREADY-link field that is
                NOT isolated (something references it) and is not being converted.
                An isolated link renders the editable picker above instead. */}
            {isLinkType && editingField && form.is_published && !isConverting && !editingLinkIsIsolated && (
              <div className="space-y-3">
                {/* Linked Table - read-only while locked */}
                <div className="p-3 rounded-lg bg-muted space-y-1">
                  <Label>Linked Table</Label>
                  <p className="text-sm">
                    {linkableTables.find((f) => f.id === fieldData.linked_table_id)?.name ||
                      "Unknown table"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {editingLinkDependentNames
                      ? `Linked table is locked because other fields depend on it: ${editingLinkDependentNames}. Remove them first to change or detach it.`
                      : "Linked table cannot be changed after field creation"}
                  </p>
                </div>

                {/* Display Field - always editable (display-only metadata) */}
                <div className="space-y-2">
                  <Label>Display Field</Label>
                  <Combobox
                    options={displayFields.map((f) => ({
                      value: f.id.toString(),
                      label: f.name,
                    }))}
                    value={fieldData.linked_display_field_id?.toString() || ""}
                    onValueChange={(value) =>
                      setFieldData((prev) => ({
                        ...prev,
                        linked_display_field_id: value || null,
                      }))
                    }
                    placeholder={
                      loadingDisplayFields
                        ? "Loading fields..."
                        : "Select field to display"
                    }
                    searchPlaceholder="Search fields..."
                    emptyMessage="No display-eligible fields available"
                    disabled={loadingDisplayFields}
                  />
                  <p className="text-xs text-muted-foreground">
                    This field will be shown in dropdowns instead of the ID
                  </p>
                </div>
              </div>
            )}

            {/* Lookup field configuration (new fields, draft editing, OR
                converting a published field into a lookup — pick source+target) */}
            {isLookupType && (!editingField || !form.is_published || isConverting) && (
              <>
                <div className="space-y-2">
                  <Label>Source Link Field</Label>
                  <Select
                    value={fieldData.source_link_field_id?.toString() || ""}
                    onValueChange={(value) =>
                      handleSourceLinkFieldChange(value || null)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a link field" />
                    </SelectTrigger>
                    <SelectContent>
                      {linkFields.length === 0 ? (
                        <div className="p-2 text-sm text-muted-foreground">
                          No link fields available. Add a &quot;Link to Table&quot; field first.
                        </div>
                      ) : (
                        linkFields.map((f) => (
                          <SelectItem key={f.id} value={f.id.toString()}>
                            {f.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    This field will get its relationship from an existing link field
                  </p>
                </div>

                {fieldData.source_link_field_id && (
                  <div className="space-y-2">
                    <Label>Field to Fetch</Label>
                    <Select
                      value={fieldData.lookup_field_id?.toString() || ""}
                      onValueChange={(value) =>
                        setFieldData((prev) => ({
                          ...prev,
                          lookup_field_id: value || null,
                        }))
                      }
                      disabled={loadingLookupFields}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            loadingLookupFields
                              ? "Loading fields..."
                              : "Select field to fetch"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {lookupTargetFields.length === 0 ? (
                          <div className="p-2 text-sm text-muted-foreground">
                            No fields available
                          </div>
                        ) : (
                          lookupTargetFields.map((f) => (
                            <SelectItem key={f.id} value={f.id.toString()}>
                              {f.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      This field&apos;s value will be fetched from the linked table (read-only)
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Read-only lookup info — only for an ALREADY-lookup field (not
                while converting a non-lookup field into a lookup) */}
            {isLookupType && editingField && form.is_published && !isConverting && (
              <div className="p-3 rounded-lg bg-muted space-y-1">
                <Label>Source Link Field</Label>
                <p className="text-sm">
                  {linkFields.find((f) => f.id === fieldData.source_link_field_id)?.name ||
                    "Unknown field"}
                </p>
                <Label className="mt-2">Field to Fetch</Label>
                <p className="text-sm">
                  {lookupTargetFields.find((f) => f.id === fieldData.lookup_field_id)?.name ||
                    "Unknown field"}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Lookup configuration cannot be changed after creation
                </p>
              </div>
            )}

            {/* Calculation field configuration - always editable since it's computed at query time */}
            {isCalculationType && (
              <div className="space-y-3">
                {/* Amber warning: the calc info is covered by the FieldTypeInfoBox
                    above; this block is only the conditional "existing rows
                    keep their old values" hint that applies when editing a
                    field on a published table. */}
                {form?.is_published && editingField && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-md">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-700 dark:text-amber-300">
                      Changing the formula affects new submissions only. Existing records keep their current values.
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="field-formula">Formula</Label>
                  <Input
                    id="field-formula"
                    placeholder="e.g., [Quantity] * [Unit Price]"
                    value={fieldData.calculation_formula || ""}
                    onChange={(e) => {
                      // Re-arm the formula-change confirmation on every edit
                      // so the user reconfirms any new formula.
                      pendingFormulaChangeApprovedRef.current = false;
                      setFieldData((prev) => ({
                        ...prev,
                        calculation_formula: e.target.value || null,
                      }));
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Use [Field Name] notation with +, -, *, / operators. Click fields below to insert.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="field-calc-result-type">
                    Result type <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Select
                    value={fieldData.calculation_result_field_type_id ?? "__legacy__"}
                    onValueChange={(value) => {
                      pendingFormulaChangeApprovedRef.current = false;
                      setFieldData((prev) => ({
                        ...prev,
                        calculation_result_field_type_id: value === "__legacy__" ? null : value,
                      }));
                    }}
                  >
                    <SelectTrigger id="field-calc-result-type" className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__legacy__">
                        Basic arithmetic (+ − × ÷ only)
                      </SelectItem>
                      <SelectSeparator />
                      {calcResultTypeOptions.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Declaring a result type unlocks full T-SQL — date functions
                    (DATEPART, YEAR, DATEDIFF), CASE, string functions — validated
                    and typed by SQL Server. Leave as basic arithmetic for the
                    simple +, -, *, / language.
                  </p>
                  {calcResultTypeId && (
                    <div className="flex items-center gap-1.5 text-xs">
                      {calcCompileState.status === "checking" && (
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Validating…
                        </span>
                      )}
                      {calcCompileState.status === "ok" && (
                        <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                          Valid — returns {calcCompileState.resultSqlType}.{" "}
                          {calcCompileState.previewAvailable
                            ? "Live preview available."
                            : "Computed on save (no live preview for this expression)."}
                        </span>
                      )}
                      {calcCompileState.status === "error" && (
                        <span className="flex items-start gap-1.5 text-destructive">
                          <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                          <span>{calcCompileState.error}</span>
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {(calcAvailableFields.length > 0 || form?.parent_table_id) && (
                  <div className="space-y-2">
                    <Label className="text-xs">Available Fields (click to insert)</Label>
                    <div className="flex flex-wrap gap-1">
                      {calcAvailableFields.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          className="px-2 py-1 text-xs bg-muted hover:bg-muted/80 rounded border cursor-pointer"
                          onClick={() => {
                            const formula = fieldData.calculation_formula || "";
                            const needsSpace = formula.length > 0 && !formula.endsWith(" ");
                            // Escape embedded "]" the same way the backend's
                            // _quote_ident does, so a field name containing
                            // one still inserts a parseable bracket reference.
                            const quoted = `[${f.name.replace(/]/g, "]]")}]`;
                            setFieldData((prev) => ({
                              ...prev,
                              calculation_formula: formula + (needsSpace ? " " : "") + quoted,
                            }));
                          }}
                        >
                          {f.name}
                        </button>
                      ))}
                      {form?.parent_table_id &&
                        (
                          [
                            ["Parent Id", "[ParentId]"],
                            ["Line Number", "[SysRecordLineNumber]"],
                          ] as const
                        ).map(([label, token]) => (
                          <button
                            key={token}
                            type="button"
                            title="System integer — immutable, like a PK"
                            className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded border border-indigo-200 cursor-pointer"
                            onClick={() => {
                              const formula = fieldData.calculation_formula || "";
                              const needsSpace =
                                formula.length > 0 && !formula.endsWith(" ");
                              setFieldData((prev) => ({
                                ...prev,
                                calculation_formula:
                                  formula + (needsSpace ? " " : "") + token,
                              }));
                            }}
                          >
                            {label} <span className="opacity-60">system</span>
                          </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {["+", "-", "*", "/", "(", ")"].map((op) => (
                        <button
                          key={op}
                          type="button"
                          className="px-3 py-1 text-xs bg-secondary hover:bg-secondary/80 rounded border cursor-pointer font-mono"
                          onClick={() => {
                            const formula = fieldData.calculation_formula || "";
                            const needsSpace = formula.length > 0 && !formula.endsWith(" ") && !["(", ")"].includes(op);
                            setFieldData((prev) => ({
                              ...prev,
                              calculation_formula: formula + (needsSpace ? " " : "") + op + (["(", ")"].includes(op) ? "" : " "),
                            }));
                          }}
                        >
                          {op}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {calcAvailableFields.length === 0 && !form?.parent_table_id && (
                  <p className="text-xs text-amber-600">
                    {calcResultTypeId
                      ? "No other fields available on this table yet."
                      : "No numeric or lookup fields available. Add numeric or lookup fields first."}
                  </p>
                )}
                {/* Formula Test Preview — v1 (legacy evaluator) only. A v2
                    formula has no execute endpoint to test against before
                    save (by design — the compile probe substitutes typed
                    NULLs and runs nothing); the validity/type feedback above
                    is the whole story until the field is saved. */}
                {!calcResultTypeId && fieldData.calculation_formula && numericFields.length > 0 && (
                  <div className="border rounded-lg p-3 space-y-2 bg-muted/50">
                    <Label className="text-xs font-medium">Test Calculation</Label>
                    <div className="grid gap-2">
                      {numericFields
                        .filter((f) => {
                          const formula = fieldData.calculation_formula || "";
                          const name = f.name.toLowerCase();
                          const col = f.column_name.toLowerCase();
                          const formulaLower = formula.toLowerCase();
                          return (
                            formulaLower.includes(`[${name}]`) ||
                            formulaLower.includes(name) ||
                            formulaLower.includes(col)
                          );
                        })
                        .map((f) => (
                          <div key={f.id} className="flex items-center gap-2">
                            <Label className="text-xs w-32 truncate" title={f.name}>
                              {f.name}
                            </Label>
                            <Input
                              type="number"
                              className="h-7 text-xs w-28"
                              placeholder="0"
                              value={
                                testValues[f.column_name] !== undefined
                                  ? String(testValues[f.column_name])
                                  : ""
                              }
                              onChange={(e) =>
                                setTestValues((prev) => ({
                                  ...prev,
                                  [f.column_name]: e.target.value,
                                }))
                              }
                            />
                          </div>
                        ))}
                      <div className="flex items-center gap-2 pt-1 border-t">
                        <Label className="text-xs w-32 font-medium">Result</Label>
                        <span className="text-sm font-mono">
                          {(() => {
                            const result = evaluateFormulaFn(
                              fieldData.calculation_formula,
                              numericFields,
                              testValues
                            );
                            return result !== null ? result : "\u2014";
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Concatenation field configuration. The "what concatenation is"
                description lives in the shared FieldTypeInfoBox above. */}
            {isConcatenationType && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="field-concat-template">Template</Label>
                  <Input
                    id="field-concat-template"
                    placeholder="e.g., [Partner Name] - [Code]"
                    value={fieldData.concatenation_template || ""}
                    onChange={(e) =>
                      setFieldData((prev) => ({
                        ...prev,
                        concatenation_template: e.target.value || null,
                      }))
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Use [Field Name] notation. Click fields below to insert.
                  </p>
                </div>
                {/* Available fields to insert */}
                {form && form.fields.filter((f) => !f.is_hidden && f.id !== editingField?.id).length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs">Available Fields (click to insert)</Label>
                    <div className="flex flex-wrap gap-1">
                      {form.fields
                        .filter((f) => !f.is_hidden && f.id !== editingField?.id)
                        .map((f) => (
                          <button
                            key={f.id}
                            type="button"
                            className="px-2 py-1 text-xs bg-muted hover:bg-muted/80 rounded border cursor-pointer"
                            onClick={() => {
                              const tmpl = fieldData.concatenation_template || "";
                              setFieldData((prev) => ({
                                ...prev,
                                concatenation_template: tmpl + `[${f.name}]`,
                              }));
                            }}
                          >
                            {f.name}
                          </button>
                        ))}
                      {form.parent_table_id && (
                        <>
                          <button
                            type="button"
                            title="The parent record's id — immutable, like a PK. Pairs with Line Number for a fully-gold identifier."
                            className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded border border-indigo-200 cursor-pointer"
                            onClick={() => {
                              const tmpl = fieldData.concatenation_template || "";
                              setFieldData((prev) => ({
                                ...prev,
                                concatenation_template: tmpl + `[ParentId]`,
                              }));
                            }}
                          >
                            Parent Id <span className="opacity-60">system</span>
                          </button>
                          <button
                            type="button"
                            title="Gold per-parent line index (1, 2, 3…) — immutable, like the PK"
                            className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded border border-indigo-200 cursor-pointer"
                            onClick={() => {
                              const tmpl = fieldData.concatenation_template || "";
                              setFieldData((prev) => ({
                                ...prev,
                                concatenation_template:
                                  tmpl + `[SysRecordLineNumber]`,
                              }));
                            }}
                          >
                            Line Number{" "}
                            <span className="opacity-60">system</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Rollup field configuration. The "what rollup is" description
                lives in the shared FieldTypeInfoBox above; this block is just
                the source-picker UI (child table, field, function). */}
            {isRollupType && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Child Table</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={selectedRollupChildTableId || ""}
                    onChange={(e) => {
                      setSelectedRollupChildTableId(e.target.value || null);
                      setFieldData((prev) => ({
                        ...prev,
                        rollup_source_field_id: null,
                      }));
                    }}
                  >
                    <option value="">Select child table...</option>
                    {rollupChildTables.map((ct) => (
                      <option key={ct.id} value={ct.id}>
                        {ct.name}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedRollupChildTableId && (
                  <div className="space-y-2">
                    <Label>Source Field</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={fieldData.rollup_source_field_id || ""}
                      onChange={(e) =>
                        setFieldData((prev) => ({
                          ...prev,
                          rollup_source_field_id: e.target.value || null,
                        }))
                      }
                    >
                      <option value="">Select field to aggregate...</option>
                      {rollupChildTables
                        .find((ct) => ct.id === selectedRollupChildTableId)
                        ?.fields.filter((f) => {
                          const func = fieldData.rollup_function?.toUpperCase();
                          if (func === "SUM" || func === "AVG") {
                            return ["int", "bigint", "decimal", "money", "calculation"].includes(f.code);
                          }
                          return true;
                        })
                        .map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name} ({f.code})
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Aggregation Function</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={fieldData.rollup_function || ""}
                    onChange={(e) =>
                      setFieldData((prev) => ({
                        ...prev,
                        rollup_function: e.target.value || null,
                      }))
                    }
                  >
                    <option value="">Select function...</option>
                    <option value="SUM">SUM — Total of all values</option>
                    <option value="COUNT">COUNT — Number of non-empty values</option>
                    <option value="AVG">AVG — Average of all values</option>
                    <option value="MIN">MIN — Smallest value</option>
                    <option value="MAX">MAX — Largest value</option>
                  </select>
                </div>
              </div>
            )}

            {selectedFieldType?.code === "options" && (
              <OptionsConfigEditor
                value={fieldData.options_config}
                onChange={(json) =>
                  setFieldData((prev) => ({ ...prev, options_config: json }))
                }
                modeLocked={Boolean(editingField) && Boolean(form?.is_published)}
              />
            )}

            {supportsMaxLength && (
              <div className="space-y-2">
                <Label htmlFor="field-max-length">Maximum Characters</Label>
                <Input
                  id="field-max-length"
                  type="number"
                  placeholder="Leave empty for default"
                  min={form.is_published && editingField ? (editingField.max_length || 1) : 1}
                  max={8000}
                  value={fieldData.max_length || ""}
                  onChange={(e) =>
                    setFieldData((prev) => ({
                      ...prev,
                      max_length: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {form.is_published && editingField
                    ? `Can only increase (currently ${editingField.max_length || 250})`
                    : selectedFieldType?.code === "longtext"
                    ? "Default: 2000 characters. Above 4000: no limit"
                    : "Default: 250 characters. Above 4000: no limit"}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="field-description">Description (help text)</Label>
              <Input
                id="field-description"
                placeholder="Optional help text"
                value={fieldData.description || ""}
                onChange={(e) =>
                  setFieldData((prev) => ({
                    ...prev,
                    description: e.target.value || null,
                  }))
                }
              />
            </div>

            {/* Required toggle - with warning for published tables */}
            {/* Calculation, Lookup, Concatenation, Rollup and Connector Files fields don't need Required/Unique (computed/derived values) */}
            {isCalculationType || isLookupType || isConcatenationType || isRollupType || isConnectorFilesType ? (
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="field-required" className="text-muted-foreground">Required</Label>
                  <p className="text-xs text-muted-foreground">
                    {isConnectorFilesType
                      ? "Connector Files counts are kept up to date by Refresh Data"
                      : isLookupType
                      ? "Lookup fields derive their value automatically"
                      : isRollupType
                        ? "Rollup fields are computed from child rows"
                        : isConcatenationType
                          ? "Concatenation fields are always computed"
                          : "Calculation fields are always computed"}
                  </p>
                </div>
                <Switch
                  id="field-required"
                  checked={false}
                  disabled={true}
                />
              </div>
            ) : form.is_published && editingField && editingField.is_required ? (
              <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="field-required" className="text-amber-800">
                      Required
                    </Label>
                    <p className="text-xs text-amber-700">
                      This is a one-way change and cannot be undone
                    </p>
                  </div>
                  <Switch
                    id="field-required"
                    checked={fieldData.is_required}
                    onCheckedChange={(checked) =>
                      setFieldData((prev) => ({
                        ...prev,
                        is_required: checked,
                        // IsRequired and RequiredFromStateId are mutually
                        // exclusive — IsRequired enforces at insert (NOT NULL),
                        // RequiredFromStateId at state transition. Clearing
                        // the state-conditional value when hard-required is
                        // turned on prevents a stale value sitting behind a
                        // disabled picker.
                        required_from_state_id: checked
                          ? null
                          : prev.required_from_state_id,
                      }))
                    }
                  />
                </div>
                {!fieldData.is_required && (
                  <div className="text-xs text-amber-800 bg-amber-100 p-2 rounded">
                    <strong>Warning:</strong> Turning off Required will make the database
                    column nullable. This change is permanent and cannot be reversed
                    through the form builder.
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="field-required">Required</Label>
                  {form.is_published && editingField && !editingField.is_required && (
                    <p className="text-xs text-muted-foreground">
                      All existing records must have values for this field
                    </p>
                  )}
                </div>
                <Switch
                  id="field-required"
                  checked={fieldData.is_required}
                  onCheckedChange={(checked) =>
                    setFieldData((prev) => ({
                      ...prev,
                      is_required: checked,
                      required_from_state_id: checked
                        ? null
                        : prev.required_from_state_id,
                    }))
                  }
                />
              </div>
            )}

            {/* Unique toggle - disabled for calculation, lookup, concatenation, rollup, connector files fields */}
            {isCalculationType || isLookupType || isConcatenationType || isRollupType || isConnectorFilesType ? (
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="field-unique" className="text-muted-foreground">
                    Unique values only
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Not applicable for {isConnectorFilesType ? "Connector Files" : isLookupType ? "lookup" : isRollupType ? "rollup" : isConcatenationType ? "concatenation" : "calculation"} fields
                  </p>
                </div>
                <Switch
                  id="field-unique"
                  checked={false}
                  disabled={true}
                />
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="field-unique">Unique values only</Label>
                  {isPrefixlessAutoNumberTarget && (
                    <p className="text-xs text-destructive">
                      {AUTO_NUMBER_UNIQUE_CONFLICT_MESSAGE}
                    </p>
                  )}
                  {form.is_published && !editingField && (
                    <p className="text-xs text-muted-foreground">
                      Tip: set a default value first so existing records get populated, then backfill unique values before enabling uniqueness.
                    </p>
                  )}
                  {form.is_published && editingField && !editingField.is_unique && (
                    <p className="text-xs text-muted-foreground">
                      All existing records must already have unique values for this field.
                    </p>
                  )}
                  {form.is_published && editingField && editingField.is_unique && (
                    <p className="text-xs text-muted-foreground">
                      Changing this will alter the table structure
                    </p>
                  )}
                </div>
                <Switch
                  id="field-unique"
                  checked={fieldData.is_unique}
                  // Enabling Unique on the per-parent, prefix-less
                  // auto-number target is blocked (backend parity) — but
                  // switching OFF must stay possible so the user can
                  // resolve the conflict by removing the constraint.
                  disabled={isPrefixlessAutoNumberTarget && !fieldData.is_unique}
                  onCheckedChange={(checked) => {
                    setFieldData((prev) => ({
                      ...prev,
                      is_unique: checked,
                      // Clear default value when enabling unique — all records would get the same default
                      ...(checked ? { default_value: null } : {}),
                    }));
                  }}
                />
              </div>
            )}

            {selectedFieldType?.supports_range && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="field-min">
                    {isDateType ? "Minimum Date" : "Minimum Value"}
                  </Label>
                  {isDateType ? (
                    <DateBoundInput
                      id="field-min"
                      value={fieldData.min_value}
                      onChange={(v) =>
                        setFieldData((prev) => ({ ...prev, min_value: v }))
                      }
                    />
                  ) : (
                    <Input
                      id="field-min"
                      type="text"
                      placeholder="Optional"
                      value={fieldData.min_value || ""}
                      onChange={(e) =>
                        setFieldData((prev) => ({
                          ...prev,
                          min_value: e.target.value || null,
                        }))
                      }
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="field-max">
                    {isDateType ? "Maximum Date" : "Maximum Value"}
                  </Label>
                  {isDateType ? (
                    <DateBoundInput
                      id="field-max"
                      value={fieldData.max_value}
                      onChange={(v) =>
                        setFieldData((prev) => ({ ...prev, max_value: v }))
                      }
                    />
                  ) : (
                    <Input
                      id="field-max"
                      type="text"
                      placeholder="Optional"
                      value={fieldData.max_value || ""}
                      onChange={(e) =>
                        setFieldData((prev) => ({
                          ...prev,
                          max_value: e.target.value || null,
                        }))
                      }
                    />
                  )}
                </div>
              </>
            )}

            {/* Default value - not applicable for calculation, lookup, concatenation, rollup, connector files, or unique fields */}
            {!isCalculationType && !isLookupType && !isConcatenationType && !isRollupType && !isConnectorFilesType && (
              <div className="space-y-2">
                <Label htmlFor="field-default" className={fieldData.is_unique ? "text-muted-foreground" : undefined}>
                  Default Value
                </Label>
                {fieldData.is_unique && (
                  <p className="text-xs text-muted-foreground">
                    Default value not available for unique fields
                  </p>
                )}
                {isDateType ? (
                  <div className="space-y-2">
                    <Select
                      disabled={fieldData.is_unique}
                      value={
                        fieldData.default_value === "GETDATE()"
                          ? "today"
                          : fieldData.default_value?.startsWith("DATEADD(")
                            ? "days_from_today"
                            : fieldData.default_value
                              ? "custom"
                              : "none"
                      }
                      onValueChange={(value) => {
                        if (value === "today") {
                          setFieldData((prev) => ({
                            ...prev,
                            default_value: "GETDATE()",
                          }));
                        } else if (value === "none") {
                          setFieldData((prev) => ({
                            ...prev,
                            default_value: null,
                          }));
                        } else if (value === "days_from_today") {
                          // Default to 0 days (today)
                          setFieldData((prev) => ({
                            ...prev,
                            default_value: "DATEADD(DAY,0,GETDATE())",
                          }));
                        } else {
                          // Custom - keep current value or clear
                          setFieldData((prev) => ({
                            ...prev,
                            default_value: prev.default_value === "GETDATE()" || prev.default_value?.startsWith("DATEADD(") ? null : prev.default_value,
                          }));
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No default</SelectItem>
                        <SelectItem value="today">Today (current date)</SelectItem>
                        <SelectItem value="days_from_today">Days from today...</SelectItem>
                        <SelectItem value="custom">Specific date...</SelectItem>
                      </SelectContent>
                    </Select>
                    {fieldData.default_value?.startsWith("DATEADD(") && (
                      <div className="space-y-1">
                        <Input
                          type="number"
                          placeholder="Number of days (e.g., 5 or -7)"
                          value={
                            // Extract the number from DATEADD(DAY,X,GETDATE())
                            fieldData.default_value.match(/DATEADD\(DAY,(-?\d+),GETDATE\(\)\)/)?.[1] || "0"
                          }
                          onChange={(e) => {
                            const days = parseInt(e.target.value) || 0;
                            setFieldData((prev) => ({
                              ...prev,
                              default_value: `DATEADD(DAY,${days},GETDATE())`,
                            }));
                          }}
                        />
                        <p className="text-xs text-muted-foreground">
                          Use positive numbers for future dates, negative for past dates
                        </p>
                      </div>
                    )}
                    {fieldData.default_value && fieldData.default_value !== "GETDATE()" && !fieldData.default_value.startsWith("DATEADD(") && (
                      <Input
                        type="date"
                        value={fieldData.default_value}
                        onChange={(e) =>
                          setFieldData((prev) => ({
                            ...prev,
                            default_value: e.target.value || null,
                          }))
                        }
                      />
                    )}
                  </div>
                ) : (
                  <Input
                    id="field-default"
                    placeholder="Optional"
                    disabled={fieldData.is_unique}
                    value={fieldData.default_value || ""}
                    onChange={(e) =>
                      setFieldData((prev) => ({
                        ...prev,
                        default_value: e.target.value || null,
                      }))
                    }
                  />
                )}
              </div>
            )}

            {/* Decimal places - for decimal, money, and calculation types (not int/bigint — whole numbers) */}
            {(selectedFieldType?.code === "decimal" ||
              selectedFieldType?.code === "money" ||
              selectedFieldType?.code === "calculation") && (
              <div className="space-y-2">
                <Label htmlFor="field-decimal-places">Decimal Places</Label>
                {selectedFieldType?.code === "decimal" ? (
                  <p className="text-xs text-muted-foreground">
                    Number of decimal places. Values are stored with at least 4 places; setting
                    more than 4 stores that many, and lowering it never reduces stored precision
                    (default display: 2). This widening applies to Decimal fields only — money
                    and calculation fields are unaffected.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Number of decimal places for display formatting (default: 2). Stored
                    precision widening does not apply to this field type — only Decimal fields
                    widen.
                  </p>
                )}
                <Input
                  id="field-decimal-places"
                  type="number"
                  min={0}
                  max={10}
                  placeholder="2"
                  value={fieldData.decimal_places !== null && fieldData.decimal_places !== undefined ? String(fieldData.decimal_places) : ""}
                  onChange={(e) =>
                    setFieldData((prev) => ({
                      ...prev,
                      decimal_places: e.target.value ? Number(e.target.value) : null,
                    }))
                  }
                  className="w-24"
                />
              </div>
            )}

            {/* Show column total toggle - only for numeric and calculation types */}
            {(selectedFieldType?.code === "int" ||
              selectedFieldType?.code === "bigint" ||
              selectedFieldType?.code === "decimal" ||
              selectedFieldType?.code === "money" ||
              selectedFieldType?.code === "calculation") && (
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="field-show-total">Show column total</Label>
                  <p className="text-xs text-muted-foreground">
                    Display a sum total at the bottom of grid and line item views
                  </p>
                </div>
                <Switch
                  id="field-show-total"
                  checked={fieldData.show_total || false}
                  onCheckedChange={(checked) =>
                    setFieldData((prev) => ({
                      ...prev,
                      show_total: checked,
                    }))
                  }
                />
              </div>
            )}

            {/* Required from state — soft, application-level required marker
                that gates state transitions. Distinct from "Required" (hard
                SQL NOT NULL): use this for fields only knowable mid-workflow
                (e.g. an approval reference filled in at the Approved state).
                Mutually exclusive with `IsRequired`: if a field is already
                NOT NULL at insert, state-conditional required adds no value
                and the two settings can confuse the user. Picker is disabled
                when "Required" is on. Hidden for inherently read-only /
                system-computed field types.
                Rendered BEFORE "Locks at state" so the designer thinks
                "when does it become required" before "when does it lock". */}
            {canHaveStateRules && (
                <div className="space-y-2">
                  <Label
                    className={
                      fieldData.is_required ? "text-muted-foreground" : undefined
                    }
                  >
                    Required from state
                  </Label>
                  <Select
                    value={fieldData.required_from_state_id ?? "__never__"}
                    onValueChange={(v) =>
                      setFieldData((prev) => ({
                        ...prev,
                        required_from_state_id: v === "__never__" ? null : v,
                      }))
                    }
                    disabled={fieldData.is_required === true}
                  >
                    <SelectTrigger
                      title={
                        fieldData.is_required
                          ? '"Required" already enforces this at insert. Toggle Required off to use state-conditional required instead.'
                          : undefined
                      }
                    >
                      {fieldData.is_required ? (
                        <span>Always required</span>
                      ) : (
                        <SelectValue />
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__never__">
                        Not required by any state
                      </SelectItem>
                      {[...tableStates]
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .map((s, idx) => (
                          <SelectItem key={s.id} value={s.id}>
                            {idx + 1}. {s.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {fieldData.is_required
                      ? '"Required" already enforces this field at insert. Toggle Required off to use state-conditional required instead.'
                      : "Record cannot advance PAST this state unless this field is populated. Reaching the state itself is allowed (the field becomes required upon arrival, gating further forward progress). Use this when a value is only knowable mid-workflow, e.g. someone else fills it after the record arrives at the state."}
                  </p>
                </div>
              )}

            {/* Read-only from state, for state-machine field lockdown.
                Parallel grammar with "Required from": both name the
                first state at which the rule kicks in.
                Hidden for inherently read-only field types. */}
            {canHaveStateRules && (
                <div className="space-y-2">
                  <Label>Read-only from state</Label>
                  <Select
                    // See note in SortableFieldItem: stored
                    // locked_at_state_id points at the LAST editable
                    // state; the label asks for the FIRST locked state,
                    // so we translate through the helpers in both
                    // directions to keep the value <-> display in sync.
                    value={
                      lockedAtToReadOnlyFromStateId(
                        fieldData.locked_at_state_id,
                        [...tableStates].sort(
                          (a, b) => a.sort_order - b.sort_order,
                        ),
                      ) ?? "__never__"
                    }
                    onValueChange={(v) =>
                      setFieldData((prev) => ({
                        ...prev,
                        locked_at_state_id:
                          v === "__never__"
                            ? null
                            : readOnlyFromToLockedAtStateId(
                                v,
                                [...tableStates].sort(
                                  (a, b) => a.sort_order - b.sort_order,
                                ),
                              ),
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__never__">
                        Never (editable until terminal)
                      </SelectItem>
                      {/* First state excluded — see fieldLockResolution
                          helpers for the rationale. */}
                      {[...tableStates]
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .slice(1)
                        .map((s, i) => (
                          <SelectItem key={s.id} value={s.id}>
                            {i + 2}. {s.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Field becomes read-only from this state onwards. The
                    previous state is the last one in which the field
                    remains editable.
                  </p>
                </div>
              )}

            {/* Kanban mode — not applicable to rollup fields (computed at runtime) */}
            {!isRollupType && (
              <div className="space-y-2">
                <Label>Kanban board</Label>
                <Select
                  value={fieldData.kanban_mode ?? "off"}
                  onValueChange={(v) =>
                    setFieldData((prev) => ({
                      ...prev,
                      kanban_mode: v === "off" ? null : (v as "view" | "drag"),
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Off</SelectItem>
                    <SelectItem value="view">View only</SelectItem>
                    <SelectItem
                      value="drag"
                      disabled={
                        !!selectedFieldType &&
                        !["text", "link", "bit", "lookup"].includes(selectedFieldType.code)
                      }
                    >
                      Interactive
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {fieldData.kanban_mode === "drag"
                    ? "Cards can be dragged between columns to update this field"
                    : fieldData.kanban_mode === "view"
                    ? "Cards are grouped visually but cannot be moved"
                    : "Field is not shown in the Kanban board picker"}
                </p>
              </div>
            )}
            </>
            )}

            {/* Unhide toggle. Sibling of the {!isInheritMode} config block so it
                renders for inherited fields too: that block is suppressed in
                inherit mode, which used to hide this toggle and left a hidden
                inherited field with no way to be restored from the editor. */}
            {form.is_published && editingField && editingField.is_hidden && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                <div>
                  <Label htmlFor="field-hidden">Field is hidden</Label>
                  <p className="text-xs text-muted-foreground">Toggle off to restore this field</p>
                </div>
                <Switch
                  id="field-hidden"
                  checked={fieldData.is_hidden}
                  onCheckedChange={(checked) =>
                    setFieldData((prev) => ({ ...prev, is_hidden: checked }))
                  }
                />
              </div>
            )}
          </div>
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setFieldDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveField}
              disabled={saving || typeChangeLoading || !fieldData.name.trim() || !!fieldNameError || fieldNameValidating}
            >
              {saving ? "Saving..." : typeChangeLoading ? "Checking..." : editingField ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Field Dialog */}
      <Dialog
        open={deleteFieldOpen}
        onOpenChange={(open) => {
          setDeleteFieldOpen(open);
          // Clear any prior blocking-dependents error when the dialog is
          // dismissed so it doesn't flash on the next open.
          if (!open) {
            setDeleteFieldError(null);
            setDeleteFieldDependents(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {form.is_published ? "Hide Field" : "Delete Field"}
            </DialogTitle>
            <DialogDescription>
              {form.is_published ? (
                <>
                  Are you sure you want to hide &quot;{fieldToDelete?.name}&quot;?
                  The field will be hidden from the form but the column will remain
                  in the database. You can restore it later by editing the field.
                </>
              ) : (
                <>
                  Are you sure you want to delete &quot;{fieldToDelete?.name}&quot;?
                  This action cannot be undone.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {/* In-use block surfaced up front (from the on-open dependents
              check) so the action is disabled before any click. */}
          {deleteFieldDependents?.has_blocking && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
              <p className="font-medium">
                This field can&apos;t be {form.is_published ? "hidden" : "deleted"} while
                other fields depend on it.
              </p>
              <p className="mt-1">Repoint or remove these first:</p>
              <ul className="mt-1 list-disc pl-5">
                {deleteFieldDependents.dependents
                  .filter((d) => d.blocking)
                  .map((d, i) => (
                    <li key={i}>
                      <span className="font-medium">{d.dependent_field_name}</span>{" "}
                      on {d.table_name}
                    </li>
                  ))}
              </ul>
            </div>
          )}
          {deleteFieldError != null && (
            <ApiErrorAlert error={deleteFieldError} className="mb-0" />
          )}
          <DialogFooter>
            {deleteFieldDependents?.has_blocking ? (
              // Blocked by dependents: the destructive action is impossible, so
              // we don't show it as a dead disabled control. Once the user has
              // read the blocking list, a single neutral Close is the only
              // sensible action (the corner X alone was too easy to miss).
              <Button variant="outline" onClick={() => setDeleteFieldOpen(false)}>
                Close
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setDeleteFieldOpen(false)}
                  disabled={deletingField}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteFieldConfirm}
                  disabled={deletingField || checkingDependents}
                >
                  {deletingField
                    ? form.is_published
                      ? "Hiding..."
                      : "Deleting..."
                    : checkingDependents
                    ? "Checking..."
                    : form.is_published
                    ? "Hide Field"
                    : "Delete"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Publish Dialog */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Publish Table</DialogTitle>
            <DialogDescription>
              This will create the following database table. Once published you
              can still add and hide table columns, but there are some
              restrictions.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <button
              type="button"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setShowTechnicalInfo((prev) => !prev)}
            >
              <Code className="h-4 w-4" />
              <span className="font-medium">Technical Information</span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${showTechnicalInfo ? "rotate-180" : ""}`}
              />
            </button>
            {showTechnicalInfo && (
              <div className="mt-2 rounded-lg overflow-hidden border">
                <SqlViewer value={ddlPreview || ""} height="300px" />
              </div>
            )}
          </div>
          <DialogFooter>
            {publishing ? (
              <div className="flex-1 space-y-2">
                {(() => {
                  const pubPhases = [
                    { label: "Creating schema", threshold: 5 },
                    { label: "Building columns", threshold: 30 },
                    { label: "Applying constraints", threshold: 60 },
                    { label: "Finalising", threshold: 80 },
                  ];
                  const activeIdx = pubPhases.reduce(
                    (acc, p, i) => (publishProgress >= p.threshold ? i : acc),
                    0,
                  );
                  return (
                    <>
                      <div className="flex items-center gap-3">
                        {pubPhases.map((p, i) => (
                          <div key={p.label} className="flex items-center gap-1.5">
                            <div
                              className={`h-2 w-2 rounded-full transition-colors duration-300 ${
                                i <= activeIdx ? "bg-primary" : "bg-muted"
                              }`}
                            />
                            <span
                              className={`text-xs transition-colors duration-300 ${
                                i <= activeIdx ? "text-foreground font-medium" : "text-muted-foreground"
                              }`}
                            >
                              {p.label}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full transition-[width] duration-100 ease-linear"
                          style={{ width: `${Math.min(publishProgress, 100)}%` }}
                        />
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setPublishDialogOpen(false)}
                >
                  Continue Editing
                </Button>
                <Button onClick={handlePublishConfirm}>
                  <Rocket className="h-4 w-4 mr-2" />
                  Publish Table
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Pairing binding builder — authored inline (pre-locked to this table) so
          the user never has to leave the editor for the Pairing tab. */}
      <Dialog
        open={bindingBuilderOpen}
        onOpenChange={(o) => {
          if (!o) {
            setBindingBuilderOpen(false);
            setPublishAfterBinding(false);
            setBindingEditInitial(null);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto p-0">
          {/* The builder renders its own visible heading; this gives the dialog a
              distinct accessible name for Radix/screen readers without colliding
              with (duplicating) that visible heading. */}
          <DialogTitle className="sr-only">Pairing setup</DialogTitle>
          {form && (
            <BindingBuilder
              // Remount when switching author ⇄ edit so pre-filled state never
              // leaks between modes (the picks are useState initialisers).
              key={bindingEditInitial ? `edit-${bindingEditInitial.id}` : "author"}
              lockedTable={form}
              initialConfig={bindingEditInitial ?? undefined}
              hasAllocations={bindingHasData}
              onSaved={handleBindingSaved}
              onCancel={() => {
                setBindingBuilderOpen(false);
                setPublishAfterBinding(false);
                setBindingEditInitial(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      {/* Re-publish Structure Preview Dialog */}
      <Dialog open={republishPreviewOpen} onOpenChange={setRepublishPreviewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Re-publish Structure</DialogTitle>
            <DialogDescription>
              Review the changes that will be made to the table structure. All existing data will be preserved.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {structureChanges.length > 0 && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-800 mb-1">Field type changes:</h4>
                <ul className="text-sm text-blue-700 list-disc list-inside">
                  {structureChanges.map((c, i) => (
                    <li key={i}>
                      <strong>{c.fieldName}</strong>: {c.fromType} → {c.toType}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {republishPreview && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium">Additional changes to apply:</h4>
                <ul className="text-sm space-y-2">
                  {republishPreview.computed_columns_found > 0 && (
                    <li className="flex items-center gap-2">
                      <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">
                        {republishPreview.computed_columns_found}
                      </span>
                      <span>Computed column(s) to convert to physical</span>
                    </li>
                  )}
                  {republishPreview.missing_fk_columns_found > 0 && (
                    <li className="flex items-center gap-2">
                      <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">
                        {republishPreview.missing_fk_columns_found}
                      </span>
                      <span>FK column(s) to add for link fields</span>
                    </li>
                  )}
                  {republishPreview.int_to_nvarchar_found > 0 && (
                    <li className="flex items-center gap-2">
                      <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-medium">
                        {republishPreview.int_to_nvarchar_found}
                      </span>
                      <span>Link column(s) with wrong data type to migrate</span>
                    </li>
                  )}
                  {republishPreview.wrong_length_found > 0 && (
                    <li className="flex items-center gap-2">
                      <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">
                        {republishPreview.wrong_length_found}
                      </span>
                      <span>Column length(s) to correct</span>
                    </li>
                  )}
                  {republishPreview.computed_columns_found === 0 &&
                    republishPreview.missing_fk_columns_found === 0 &&
                    republishPreview.int_to_nvarchar_found === 0 &&
                    republishPreview.wrong_length_found === 0 && (
                      <li className="text-green-700 font-medium">
                        ✓ {structureChanges.length > 0
                          ? "No additional changes needed - structure is in sync"
                          : "No changes needed - structure is up to date"}
                      </li>
                    )}
                </ul>
                {republishPreview.warnings.length > 0 && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                    <h5 className="text-sm font-medium text-amber-800 mb-1">Warnings:</h5>
                    <ul className="text-sm text-amber-700">
                      {republishPreview.warnings.map((w, i) => (
                        <li key={i}>• {w}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Temp column cleanup is handled automatically */}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRepublishPreviewOpen(false)}
              disabled={fullRepairing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFullRepairConfirm}
              disabled={fullRepairing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Wrench className="h-4 w-4 mr-2" />
              {fullRepairing ? "Re-publishing..." : "Confirm Re-publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Unique Constraint Removal Confirmation Dialog */}
      <Dialog open={uniqueChangeDialogOpen} onOpenChange={(open) => {
        setUniqueChangeDialogOpen(open);
        if (!open) {
          setUniqueConfirmText("");
          // Reset the toggle back to unique since user cancelled
          setFieldData((prev) => ({ ...prev, is_unique: true }));
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Unique Constraint?</DialogTitle>
            <DialogDescription className="space-y-3">
              <p>
                You are about to remove the unique values constraint from{" "}
                <strong>{editingField?.name}</strong> on a published table.
              </p>
              <p className="text-amber-600 dark:text-amber-400 font-medium">
                This will alter the table structure and allow duplicate values in this field.
              </p>
              <p className="text-muted-foreground">
                Type <strong>change</strong> below to confirm.
              </p>
              <Input
                value={uniqueConfirmText}
                onChange={(e) => setUniqueConfirmText(e.target.value)}
                placeholder="Type change to confirm"
                autoFocus
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUniqueChangeDialogOpen(false);
                setUniqueConfirmText("");
                setFieldData((prev) => ({ ...prev, is_unique: true }));
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={uniqueConfirmText !== "change"}
              onClick={() => {
                setUniqueChangeDialogOpen(false);
                setUniqueConfirmText("");
                // Proceed with save
                handleSaveField();
              }}
            >
              Remove Unique Constraint
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Link Re-point Confirmation Dialog */}
      <Dialog open={repointDialogOpen} onOpenChange={(open) => {
        if (open) {
          setRepointDialogOpen(true);
        } else {
          dismissRepointDialog();
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Linked Table?</DialogTitle>
            {/* asChild + a wrapping <div> so the block content (paragraphs and
                the confirm Input) isn't nested inside the <p> that
                DialogDescription renders by default — mirrors the field
                type-change dialog below and keeps the DOM valid. */}
            <DialogDescription className="space-y-3" asChild>
              <div>
                <p>
                  You are about to re-point <strong>{editingField?.name}</strong> to a
                  different table on a published form.
                </p>
                <p className="text-amber-600 dark:text-amber-400 font-medium">
                  Existing records won&apos;t be re-linked — they&apos;ll keep showing
                  their previous value, but it&apos;s no longer linked to a record. The
                  old link column is left in place but no longer used.
                </p>
                <p className="text-muted-foreground">
                  Type <strong>{editingField?.name}</strong> below to confirm.
                </p>
                <Input
                  value={repointConfirmText}
                  onChange={(e) => setRepointConfirmText(e.target.value)}
                  placeholder={`Type "${editingField?.name}" to confirm`}
                  autoFocus
                />
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={dismissRepointDialog}>
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={repointConfirmText !== editingField?.name}
              onClick={() => {
                // Single-shot approval so the next handleSaveField bypasses the
                // re-point gate instead of re-opening this dialog.
                pendingRepointApprovedRef.current = true;
                setRepointDialogOpen(false);
                setRepointConfirmText("");
                handleSaveField();
              }}
            >
              Change Linked Table
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Field Type Change Confirmation Dialog */}
      <Dialog open={typeChangeDialogOpen} onOpenChange={(open) => {
        setTypeChangeDialogOpen(open);
        if (!open) {
          setTypeChangePreview(null);
          setTypeChangeConfirmText("");
          // Reset field type back to original
          if (editingField) {
            setFieldData((prev) => ({ ...prev, field_type_id: editingField.field_type?.id || prev.field_type_id }));
          }
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Change Field Type?</DialogTitle>
            <DialogDescription className="space-y-3" asChild>
              <div>
                <p>
                  You are about to change <strong>{editingField?.name}</strong> from{" "}
                  <code className="bg-muted px-1 py-0.5 rounded">{typeChangePreview?.from_type_code}</code>
                  {" "}to{" "}
                  <code className="bg-muted px-1 py-0.5 rounded">{typeChangePreview?.to_type_code}</code>.
                </p>

                {typeChangePreview?.category === "safe" && (
                  <p className="text-sm text-muted-foreground">
                    This is a safe conversion — all existing data will be preserved.
                  </p>
                )}

                {typeChangePreview?.category === "structural" && (
                  <div className="text-sm">
                    {typeChangePreview.to_type_code === "lookup" ? (
                      <div className="rounded border border-amber-200 bg-amber-50 p-2 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
                        <strong>This clears existing values.</strong> The
                        column&apos;s current values are removed and refilled from
                        the linked record. This cannot be undone.
                      </div>
                    ) : (
                      <div className="rounded border border-blue-200 bg-blue-50 p-2 text-blue-800 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300">
                        Existing values are kept as the display text. Rows whose
                        value matches exactly one linked record are auto-linked;
                        the rest are left for you to set per row.
                      </div>
                    )}
                  </div>
                )}

                {typeChangePreview?.category === "structural_detach" && (
                  <div className="text-sm">
                    {/* The backend reason is target-aware: text-family detaches
                        keep the value as plain text, cross-cast/options targets
                        go blank. Render it verbatim rather than asserting a
                        blanket "won't be carried over". */}
                    <div className="rounded border border-amber-200 bg-amber-50 p-2 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
                      {typeChangePreview.reason ||
                        "Existing records aren't re-linked. The previous link column is left in place but no longer used."}
                    </div>
                  </div>
                )}

                {typeChangePreview?.category === "validated" && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-muted p-2 rounded">
                        <span className="text-muted-foreground">Total rows:</span>{" "}
                        <strong>{typeChangePreview.total_rows}</strong>
                      </div>
                      <div className="bg-muted p-2 rounded">
                        <span className="text-muted-foreground">With values:</span>{" "}
                        <strong>{typeChangePreview.non_null_rows}</strong>
                      </div>
                      <div className="bg-green-50 dark:bg-green-950/30 p-2 rounded">
                        <span className="text-green-700 dark:text-green-400">Will convert:</span>{" "}
                        <strong>{typeChangePreview.convertible_rows}</strong>
                      </div>
                      {typeChangePreview.failed_rows > 0 && (
                        <div className="bg-red-50 dark:bg-red-950/30 p-2 rounded">
                          <span className="text-red-700 dark:text-red-400">Will become NULL:</span>{" "}
                          <strong>{typeChangePreview.failed_rows}</strong>
                        </div>
                      )}
                    </div>

                    {typeChangePreview.failed_rows > 0 && (
                      <div className="space-y-1">
                        <p className="text-amber-600 dark:text-amber-400 font-medium text-sm">
                          {typeChangePreview.failed_rows} value(s) cannot be converted and will become NULL.
                        </p>
                        {typeChangePreview.sample_failures.length > 0 && (
                          <div className="bg-muted p-2 rounded text-xs">
                            <p className="text-muted-foreground mb-1">Sample values that will fail:</p>
                            <ul className="list-disc list-inside">
                              {typeChangePreview.sample_failures.map((v, i) => (
                                <li key={i}><code>{v}</code></li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {typeChangePreview.will_become_nullable && (
                      <p className="text-amber-600 dark:text-amber-400 text-sm">
                        This field will be made optional because some values cannot convert.
                      </p>
                    )}
                  </div>
                )}

                {typeChangePreview?.category === "computed" && (
                  <p className="text-muted-foreground text-sm">
                    This will convert the field to a {typeChangePreview.to_type_code} field.
                    {typeChangePreview.to_type_code === "calculation" && " You will need to set a formula."}
                    {typeChangePreview.to_type_code === "concatenation" && " You will need to set a template."}
                  </p>
                )}

                {typeChangePreview?.will_drop_default && (
                  <p className="text-sm text-muted-foreground">
                    The default value will be cleared (may be incompatible with the new type).
                  </p>
                )}
                {typeChangePreview?.will_drop_unique && typeChangePreview?.failed_rows > 0 && (
                  <p className="text-sm text-muted-foreground">
                    The unique constraint will be removed due to conversion failures.
                  </p>
                )}

                <p className="text-muted-foreground text-sm">
                  Type the field name <strong>{editingField?.name}</strong> below to confirm this change.
                </p>
                <Input
                  value={typeChangeConfirmText}
                  onChange={(e) => setTypeChangeConfirmText(e.target.value)}
                  placeholder={`Type "${editingField?.name}" to confirm`}
                  autoFocus
                />
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setTypeChangeDialogOpen(false);
                setTypeChangePreview(null);
                setTypeChangeConfirmText("");
                pendingTypeChangeApprovedRef.current = false;
                if (editingField) {
                  setFieldData((prev) => ({ ...prev, field_type_id: editingField.field_type?.id || prev.field_type_id }));
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant={typeChangePreview?.failed_rows ? "destructive" : "default"}
              disabled={typeChangeConfirmText !== editingField?.name}
              onClick={() => {
                // Single-shot approval: lets the next handleSaveField bypass
                // the type-change guard so the dialog can't re-open and trap
                // the user in a loop. Reset when the dialog closes or the
                // user re-picks the type dropdown.
                pendingTypeChangeApprovedRef.current = true;
                setTypeChangeDialogOpen(false);
                setTypeChangeConfirmText("");
                // Proceed with save
                handleSaveField();
              }}
            >
              {typeChangePreview?.failed_rows
                ? `Apply (${typeChangePreview.failed_rows} values will be lost)`
                : "Apply Change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Formula Change Confirmation Dialog */}
      <AlertDialog open={formulaChangeDialogOpen} onOpenChange={setFormulaChangeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Calculation Formula?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to change the formula for <strong>{editingField?.name}</strong> on a published table.
              </p>
              <div className="bg-muted p-3 rounded-md text-xs space-y-1">
                <p><span className="text-muted-foreground">Current formula:</span> <code>{originalFormula || "(none)"}</code></p>
                <p><span className="text-muted-foreground">New formula:</span> <code>{fieldData.calculation_formula || "(none)"}</code></p>
              </div>
              <p className="text-amber-600 dark:text-amber-400 font-medium">
                This will recalculate values for ALL existing records.
              </p>
              <p className="text-muted-foreground">
                If you need to preserve the current calculation for historical data, consider creating a new calculation field instead.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              onClick={() => {
                setFormulaChangeDialogOpen(false);
                pendingFormulaChangeApprovedRef.current = false;
              }}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => {
                // Reset formula (and its declared result type) to original and
                // open add field dialog for new calculation
                setFieldData((prev) => ({
                  ...prev,
                  calculation_formula: originalFormula,
                  calculation_result_field_type_id: originalResultTypeId,
                }));
                setFormulaChangeDialogOpen(false);
                // Close edit dialog and open new field dialog
                setFieldDialogOpen(false);
                handleAddFieldClick();
                // Pre-select calculation type
                const calcType = fieldTypes.find((ft) => ft.code === "calculation");
                if (calcType) {
                  setFieldData((prev) => ({
                    ...prev,
                    field_type_id: calcType.id,
                    name: "",
                    calculation_formula: null,
                  }));
                }
              }}
            >
              Create New Column Instead
            </Button>
            <AlertDialogAction
              onClick={() => {
                // Single-shot approval: mirrors the type-change Apply path so
                // the two confirmations can't re-open each other in a loop.
                pendingFormulaChangeApprovedRef.current = true;
                setFormulaChangeDialogOpen(false);
                // Proceed with save - the dialog is now closed so handleSaveField will continue
                handleSaveField();
              }}
            >
              Update Formula
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Inherited-Source Repoint Confirmation (Wave 2). Mirrors the
          formula-change pattern: single-shot approval ref, Cancel resets,
          Confirm re-calls handleSaveField. Only fires for deliberate
          repoints on published tables (auto-unlock REPAIRS skip this
          gate; see isInheritSourceRepoint detection in handleSaveField). */}
      <AlertDialog
        open={inheritSourceChangeDialogOpen}
        onOpenChange={setInheritSourceChangeDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Change inherited source on a published table?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to change the source field for{" "}
                <strong>{editingField?.name}</strong> on a published table.
              </p>
              <div className="bg-muted p-3 rounded-md text-xs space-y-1">
                <p>
                  <span className="text-muted-foreground">Current source:</span>{" "}
                  <code>
                    {editingField?.source_field_name ||
                      inheritableParentFields.find(
                        (f) => f.id === editingField?.source_field_id,
                      )?.name ||
                      "(unknown)"}
                  </code>
                </p>
                <p>
                  <span className="text-muted-foreground">New source:</span>{" "}
                  <code>
                    {inheritableParentFields.find(
                      (f) => f.id === inheritSourceFieldId,
                    )?.name || "(none)"}
                  </code>
                </p>
              </div>
              <p className="text-amber-600 dark:text-amber-400 font-medium">
                Existing rows keep their current inherited value.
              </p>
              <p className="text-muted-foreground">
                Inherited values are point-in-time snapshots captured when each
                row is saved. Only new rows and rows whose parent link changes
                after this update will use the new source.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              onClick={() => {
                setInheritSourceChangeDialogOpen(false);
                pendingInheritSourceChangeApprovedRef.current = false;
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                pendingInheritSourceChangeApprovedRef.current = true;
                setInheritSourceChangeDialogOpen(false);
                handleSaveField();
              }}
            >
              Change source
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Re-publish Prompt after Type Change */}
      <AlertDialog open={republishPromptOpen} onOpenChange={setRepublishPromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {/* Type changes record an entry in structureChanges; a re-point
                  (linked table changed, type unchanged) doesn't. */}
              {structureChanges.length > 0
                ? "Field type changed successfully"
                : "Linked table changed successfully"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Would you like to re-publish the table structure now, or continue making changes?</p>
                {structureChanges.length > 0 && (
                  <div className="bg-muted p-3 rounded-md text-sm space-y-1">
                    <p className="font-medium text-foreground">Changes this session:</p>
                    <ul className="list-disc list-inside text-muted-foreground">
                      {structureChanges.map((c, i) => (
                        <li key={i}>
                          <strong>{c.fieldName}</strong>: {c.fromType} → {c.toType}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRepublishPromptOpen(false)}>
              Not yet, making more changes
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setRepublishPromptOpen(false);
                handleFullRepairClick();
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Re-publish now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* WS1: back-fill prompt after converting a published field to inherited */}
      <AlertDialog
        open={inheritBackfillPromptOpen}
        onOpenChange={(open) => {
          // Block dismissal while the backfill request is in flight.
          if (!inheritBackfillRunning) setInheritBackfillPromptOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Back-fill from the parent now?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  <strong>{inheritBackfillField?.name}</strong> now inherits its
                  value from the parent table. Existing rows still hold their
                  previous values.
                </p>
                <p>
                  Back-fill copies the parent value into every existing row now.
                  Otherwise rows update the next time each one is saved.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={inheritBackfillRunning}>
              Later
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Keep the dialog open until the request resolves.
                e.preventDefault();
                void handleInheritBackfillConfirm();
              }}
              disabled={inheritBackfillRunning}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {inheritBackfillRunning ? "Back-filling…" : "Back-fill now"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Field Create Dialog */}
      <BulkFieldCreateDialog
        open={bulkFieldDialogOpen}
        onOpenChange={setBulkFieldDialogOpen}
        tableId={tableId}
        existingFields={form.fields}
        primaryKeyColumn={form.primary_key_column}
        onComplete={(newFields) => {
          setForm((prev) =>
            prev ? { ...prev, fields: [...prev.fields, ...newFields] } : null
          );
          setBulkFieldDialogOpen(false);
        }}
      />
      <BulkFieldEditDialog
        open={bulkFieldEditDialogOpen}
        onOpenChange={setBulkFieldEditDialogOpen}
        tableId={tableId}
        fields={form.fields}
        autoNumber={form}
        onComplete={async () => {
          // Reload form to get updated field data
          try {
            const updated = await myappApi.tables.get(token, formId);
            setForm(updated);
          } catch {
            // Silent — the dialog already showed success
          }
        }}
      />

    </div>
  )
}
